/**
 * Participant Dashboard — what human players see during the simulation.
 * Route: /play/:simId
 *
 * Tabs: Actions | Strictly Confidential | Country | World | Global Map
 * Persistent: Header (role + round + timer), Navigator button, Moderator broadcast
 *
 * M6 Sprint 6.1: Shell, tabs, header, data loading
 */

import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
// channelManager import removed — consolidated channel uses supabase.channel() directly
import { submitAction, getToken, type SimRun } from '@/lib/queries'
import { requestQueue } from '@/lib/requestQueue'
import { ArtefactRenderer } from '@/components/ArtefactRenderer'
import { CountryEmblem, CountryBadge, OrgEmblem } from '@/components/CountryEmblem'
import { useRealtimeRow, useRealtimeTable } from '@/hooks/useRealtimeTable'
import {
  ActionForm, TransactionReview, ProposeTransactionForm, ArrestedBanner, UnitIcon,
} from '@/components/participant/ActionForms'
import { MeetingChat } from '@/components/participant/MeetingChat'
import { NavigatorVoiceConversation } from '@/components/navigator/NavigatorVoiceConversation'
import { NavigatorTextConversation } from '@/components/navigator/NavigatorTextConversation'
import { useIsMobile } from '@/hooks/useIsMobile'
import { MobileParticipantLayout } from '@/components/mobile/MobileParticipantLayout'
import { MobileActionOverlay } from '@/components/mobile/MobileActionOverlay'
import {
  Megaphone, MessageCircle, PieChart, Scale, ShieldBan, Fuel,
  ArrowRightLeft, ScrollText, Flag, Swords, Crosshair, Anchor,
  Route, Gavel, Atom, Rocket, Network, Lock, Crown, Hand, Vote,
  Eye, Drama, Skull,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { VoiceCallInterface } from '@/components/participant/VoiceCallInterface'
import { MapLegend, HexInspector, type HexInfo } from '@/components/participant/MapControls'
import { getActiveMeetings, endMeeting, type MeetingData } from '@/lib/queries'

/* ── Hex → Country lookup (canonical from map_config.py) ──────────────── */
const HEX_OWNERS: Record<string, string> = {
  '2,10':'freeland','2,11':'sarmatia','2,12':'sarmatia','2,16':'sarmatia',
  '3,3':'columbia','3,6':'thule','3,8':'albion','3,10':'freeland',
  '3,11':'ruthenia','3,12':'sarmatia','3,13':'sarmatia','3,14':'sarmatia',
  '3,15':'sarmatia','3,16':'sarmatia','3,18':'choson',
  '4,2':'columbia','4,3':'columbia','4,7':'albion','4,9':'teutonia',
  '4,10':'teutonia','4,11':'ruthenia','4,12':'sarmatia','4,13':'sarmatia',
  '4,14':'sarmatia','4,16':'sarmatia','4,17':'hanguk','4,19':'yamato',
  '5,3':'columbia','5,4':'columbia','5,5':'columbia','5,9':'gallia',
  '5,10':'gallia','5,11':'phrygia','5,14':'sogdiana','5,15':'cathay',
  '5,16':'cathay','5,17':'cathay','5,19':'yamato',
  '6,3':'columbia','6,4':'columbia','6,8':'ponte','6,10':'levantia',
  '6,11':'phrygia','6,12':'persia','6,13':'sogdiana','6,14':'sogdiana',
  '6,15':'cathay','6,16':'cathay',
  '7,4':'columbia','7,9':'ponte','7,11':'solaria','7,13':'persia',
  '7,14':'bharata','7,15':'bharata','7,16':'cathay','7,18':'formosa',
  '8,10':'solaria','8,11':'mirage','8,13':'persia','8,14':'bharata',
  '8,15':'bharata',
  '9,5':'caribe','9,10':'horn','9,15':'bharata',
}
function hexCountryName(row: number, col: number): string {
  return (HEX_OWNERS[`${row},${col}`] || 'sea').toUpperCase()
}

/* ── Types ─────────────────────────────────────────────────────────────── */

interface RoleData {
  id: string; character_name: string; country_code: string; position_type: string; positions?: string[]; status?: string
  title: string; public_bio: string; confidential_brief: string | null
  objectives: string[]; powers: string[]
}

interface CountryData {
  id: string; sim_name: string; color_ui: string | null
  gdp: number; stability: number; inflation: number; treasury: number
  mil_ground: number; mil_naval: number; mil_tactical_air: number
  mil_strategic_missiles: number; mil_air_defense: number
  nuclear_level: number; nuclear_confirmed: boolean
  ai_level: number; debt_burden: number
}

interface Artefact {
  id: string; role_id: string; artefact_type: string; title: string
  subtitle: string | null; classification: string; from_entity: string | null
  date_label: string | null; content_html: string | null
  round_delivered: number; is_read: boolean
}

interface SimState {
  status: string; current_round: number; current_phase: string
  phase_started_at: string | null; phase_duration_seconds: number | null
}

type TabId = 'actions' | 'newsbrief' | 'confidential' | 'country' | 'world'

/* ── My Action Log ────────────────────────────────────────────────── */
// System/internal event types to hide from participant log
const LOG_HIDDEN_TYPES = new Set([
  'round_started', 'round_completed', 'round_resolving', 'phase_b_complete',
  'agent_started', 'agent_committed', 'agent_no_commit', 'conversations_complete',
  'ai_agent_log', 'respond_meeting', 'movement_applied',
])

function shortenSummary(summary: string): string {
  // Remove "by CharName" prefix patterns and clean up
  return summary
    .replace(/^(PUBLIC STATEMENT by \S+:\s*)/i, 'Statement: ')
    .replace(/^(PENDING: \S+ by .+ — )/i, '')
    .replace(/ — awaiting moderator approval$/, ' ⏳')
    .replace(/^(\S+ by \S+ \(\S+\) )(queued for Phase B processing)$/i, '$1Phase B')
    .slice(0, 80) + (summary.length > 80 ? '…' : '')
}

function getEventReaction(eventType: string, payload: Record<string, unknown>, sentInvitations: Record<string, unknown>[], proposedTxns: Record<string, unknown>[], proposedAgrs: Record<string, unknown>[]): { text: string; color: string } | null {
  const result = (payload?.result || {}) as Record<string, unknown>

  // Combat results
  if (result.outcome === 'victory' || result.result === 'victory') return { text: 'Victory', color: 'text-success' }
  if (result.outcome === 'defeat' || result.result === 'defeat') return { text: 'Defeat', color: 'text-danger' }
  if (result.hit === true || result.outcome === 'hit') return { text: 'Hit', color: 'text-success' }
  if (result.hit === false || result.outcome === 'miss') return { text: 'Missed', color: 'text-danger' }

  // Meeting invitation reactions — match by most recent invitation
  if (eventType === 'bilateral_conversation' || eventType === 'invite_to_meet' || eventType === 'call_org_meeting') {
    const invId = (payload?.action as Record<string, unknown>)?.invitation_id || result.invitation_id
    const inv = invId
      ? sentInvitations.find(i => (i as Record<string, unknown>).id === invId)
      : sentInvitations[sentInvitations.length - 1] // fallback to latest
    if (inv) {
      const s = (inv as Record<string, unknown>).status as string
      if (s === 'accepted') return { text: '✓ accepted', color: 'text-success' }
      if (s === 'rejected') return { text: '✗ declined', color: 'text-danger' }
      if (s === 'expired') return { text: 'expired', color: 'text-text-muted' }
      if (s === 'pending') return { text: '⏳', color: 'text-warning' }
    }
  }

  // Transaction reactions
  if (eventType === 'transaction_proposed') {
    const txnId = result.transaction_id
    const txn = txnId
      ? proposedTxns.find(t => (t as Record<string, unknown>).id === txnId)
      : proposedTxns[proposedTxns.length - 1]
    if (txn) {
      const s = (txn as Record<string, unknown>).status as string
      if (s === 'accepted' || s === 'executed') return { text: '✓ accepted', color: 'text-success' }
      if (s === 'rejected') return { text: '✗ rejected', color: 'text-danger' }
      if (s === 'pending') return { text: '⏳', color: 'text-warning' }
    }
  }

  // Agreement reactions
  if (eventType === 'propose_agreement') {
    const agrId = result.agreement_id
    const agr = agrId
      ? proposedAgrs.find(g => (g as Record<string, unknown>).id === agrId)
      : proposedAgrs[proposedAgrs.length - 1]
    if (agr) {
      const a = agr as Record<string, unknown>
      const sigs = (a.signatures || {}) as Record<string, { confirmed?: boolean }>
      const total = Array.isArray(a.signatories) ? (a.signatories as string[]).length : 0
      const signed = Object.values(sigs).filter(s => s?.confirmed).length
      if (a.status === 'active') return { text: '✓ active', color: 'text-success' }
      if (signed > 0) return { text: `${signed}/${total} signed`, color: signed >= total ? 'text-success' : 'text-warning' }
      return { text: '⏳', color: 'text-warning' }
    }
  }

  // Generic success from result
  if (result.success === true) return { text: '✓', color: 'text-success' }
  if (result.success === false) return { text: '✗', color: 'text-danger' }

  return null
}

// Event types that are country-level diplomatic actions (shown to diplomat/HoS positions only)
const DIPLOMATIC_EVENT_TYPES = new Set([
  'transaction_proposed', 'transaction_executed', 'transaction_declined', 'transaction_countered',
  'agreement_proposed', 'agreement_signed', 'agreement_activated', 'agreement_declined',
  'propose_transaction', 'accept_transaction', 'propose_agreement', 'sign_agreement',
])
// Positions authorized to see diplomatic events
const DIPLOMATIC_POSITIONS = new Set(['head_of_state', 'diplomat'])

function MyActionLog({ events, sentInvitations, proposedTxns, proposedAgrs, roleId, positions }: {
  events: Record<string, unknown>[]
  sentInvitations: Record<string, unknown>[]
  proposedTxns: Record<string, unknown>[]
  proposedAgrs: Record<string, unknown>[]
  roleId: string
  positions: string[]
}) {
  const sorted = useMemo(() => {
    const rId = roleId.toLowerCase()
    const hasDiplomaticAuth = positions.some(p => DIPLOMATIC_POSITIONS.has(p))
    return events
      .filter(e => !LOG_HIDDEN_TYPES.has(e.event_type as string))
      .filter(e => (e.visibility as string | null | undefined) !== 'moderator')
      .filter(e => {
        const etype = e.event_type as string
        const payload = (e.payload || {}) as Record<string, unknown>
        const action = (payload.action || {}) as Record<string, unknown>

        // Path 1: Diplomatic events (transactions, agreements) — show to diplomat/HoS
        // These are country-level, counterpart is a country not a role
        if (DIPLOMATIC_EVENT_TYPES.has(etype)) {
          return hasDiplomaticAuth
        }

        // Path 2: All other events — show if role_id matches (I did it or targets me)
        const evtRoleId = ((action.role_id || payload.role_id || '') as string).toLowerCase()
        const evtRoleName = ((e.role_name || '') as string).toLowerCase() // fallback for events without payload role_id
        const targetRoleId = ((action.target_role_id || payload.target_role_id || '') as string).toLowerCase()
        return evtRoleId === rId || evtRoleName === rId || targetRoleId === rId
      })
      .sort((a, b) => new Date(b.created_at as string).getTime() - new Date(a.created_at as string).getTime())
  }, [events, roleId, positions])

  return (
    <div className="border rounded-lg p-3 bg-card border-border flex-1 min-w-0 flex flex-col">
      <h3 className="font-heading text-[13px] text-text-secondary mb-1.5">My Action Log</h3>
      {sorted.length === 0
        ? <p className="font-body text-[11px] text-text-muted italic">No actions yet this SIM.</p>
        : <div className="flex flex-col gap-0.5 overflow-y-auto" style={{ maxHeight: '80px' }}>
            {sorted.map(e => {
              const desc = shortenSummary(e.summary as string || (e.event_type as string).replace(/_/g, ' '))
              const reaction = getEventReaction(e.event_type as string, (e.payload || {}) as Record<string, unknown>, sentInvitations, proposedTxns, proposedAgrs)
              return (
                <div key={e.id as string} className="flex items-center gap-1.5 py-0.5 border-b border-border/30 last:border-0">
                  <span className="font-mono text-[9px] text-text-muted w-5 flex-shrink-0">R{e.round_num as number}</span>
                  <span className="font-body text-[11px] text-text-primary truncate">{desc}</span>
                  {reaction && <span className={`font-body text-[10px] ${reaction.color} ml-auto flex-shrink-0`}>{reaction.text}</span>}
                </div>
              )
            })}
          </div>
      }
    </div>
  )
}

/* ── Map with theater tabs + unit toggle + legend + inspector ─────── */
function MapWithTheaterTabs({iframeId, src, defaultHideUnits=false, height='100%', isMobile=false}:{iframeId:string;src:string;defaultHideUnits?:boolean;height?:string;isMobile?:boolean}) {
  const [hideUnits, setHideUnits] = useState(defaultHideUnits)
  const [hexInfo, setHexInfo] = useState<HexInfo|null>(null)
  const [isLandscape, setIsLandscape] = useState(() => typeof window !== 'undefined' && window.innerWidth > window.innerHeight)
  const postMsg = (msg: Record<string, unknown>) => {
    const f = document.getElementById(iframeId) as HTMLIFrameElement
    f?.contentWindow?.postMessage(msg, '*')
  }
  useEffect(()=>{
    const handler = (e: MessageEvent) => {
      if (e.data?.type === 'hex-info' || e.data?.type === 'hex-click') setHexInfo(e.data as HexInfo)
    }
    window.addEventListener('message', handler)
    return ()=>window.removeEventListener('message', handler)
  },[])
  useEffect(()=>{
    if (!isMobile) return
    const mql = window.matchMedia('(orientation: landscape)')
    const handler = (e: MediaQueryListEvent) => setIsLandscape(e.matches)
    setIsLandscape(mql.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  },[isMobile])

  // Mobile landscape: full-screen map only
  if (isMobile && isLandscape) {
    return (
      <div className="fixed inset-0 z-[55] bg-base flex flex-col">
        <div className="flex items-center gap-2 px-2 py-1 bg-card border-b border-border flex-shrink-0">
          <div className="flex gap-1">
            {(['global','eastern_ereb','mashriq'] as const).map(v=>(
              <button key={v} onClick={()=>postMsg({type:'navigate-theater',theater:v==='global'?'global':v})}
                className="font-body text-caption px-2 py-1 rounded border border-border text-text-secondary hover:text-action min-h-[36px]">
                {v==='global'?'Global':v==='eastern_ereb'?'E. Ereb':'Mashriq'}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 ml-auto cursor-pointer">
            <input type="checkbox" checked={!hideUnits} onChange={e=>{setHideUnits(!e.target.checked);postMsg({type:'toggle-units',hide:!e.target.checked})}}
              className="accent-action w-4 h-4"/>
            <span className="font-body text-caption text-text-secondary">Units</span>
          </label>
        </div>
        <div className="relative flex-1" style={{touchAction:'pinch-zoom pan-x pan-y'}}>
          <iframe id={iframeId} src={src} className="absolute inset-0 w-full h-full border-0" style={{touchAction:'pinch-zoom pan-x pan-y'}} title="Map"/>
        </div>
      </div>
    )
  }

  // Mobile portrait: full-width map + legend/inspector below
  if (isMobile) {
    return (
      <div className="flex flex-col gap-2 -mx-3">
        <div className="flex items-center gap-2 px-3">
          <div className="flex gap-1">
            {(['global','eastern_ereb','mashriq'] as const).map(v=>(
              <button key={v} onClick={()=>postMsg({type:'navigate-theater',theater:v==='global'?'global':v})}
                className="font-body text-caption px-2 py-1 rounded border border-border text-text-secondary hover:text-action min-h-[44px]">
                {v==='global'?'Global':v==='eastern_ereb'?'E. Ereb':'Mashriq'}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 ml-auto cursor-pointer">
            <input type="checkbox" checked={!hideUnits} onChange={e=>{setHideUnits(!e.target.checked);postMsg({type:'toggle-units',hide:!e.target.checked})}}
              className="accent-action w-4 h-4"/>
            <span className="font-body text-caption text-text-secondary">Units</span>
          </label>
        </div>
        <div className="relative w-full" style={{height:'50vh', touchAction:'pinch-zoom pan-x pan-y'}}>
          <iframe id={iframeId} src={src} className="absolute inset-0 w-full h-full border-0" style={{touchAction:'pinch-zoom pan-x pan-y'}} title="Map"/>
        </div>
        <div className="flex gap-2 px-3">
          <div className="flex-1 min-w-0"><MapLegend/></div>
          <div className="flex-1 min-w-0"><HexInspector info={hexInfo}/></div>
        </div>
      </div>
    )
  }

  // Desktop: original layout (sidebars + center map)
  return (
    <div className="flex flex-col gap-1" style={{height}}>
      <div className="flex items-center gap-2">
        <div className="flex gap-1">
          {(['global','eastern_ereb','mashriq'] as const).map(v=>(
            <button key={v} onClick={()=>postMsg({type:'navigate-theater',theater:v==='global'?'global':v})}
              className="font-body text-caption px-2 py-0.5 rounded border border-border text-text-secondary hover:border-action/30 hover:text-action transition-colors">
              {v==='global'?'Global':v==='eastern_ereb'?'E. Ereb':'Mashriq'}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 ml-auto cursor-pointer select-none">
          <input type="checkbox" checked={!hideUnits} onChange={e=>{setHideUnits(!e.target.checked);postMsg({type:'toggle-units',hide:!e.target.checked})}}
            className="accent-action w-3.5 h-3.5"/>
          <span className="font-body text-caption text-text-secondary">Military</span>
        </label>
      </div>
      <div className="flex gap-1.5 flex-1 min-h-0">
        <MapLegend/>
        <div className="relative flex-1 rounded-lg overflow-hidden border border-border">
          <iframe id={iframeId} src={src} className="absolute inset-0 w-full h-full border-0" title="Map"/>
        </div>
        <HexInspector info={hexInfo}/>
      </div>
    </div>
  )
}

/* ── Helpers ───────────────────────────────────────────────────────────── */

const POS: Record<string, string> = {
  head_of_state: 'Head of State', military_chief: 'Military Chief',
  economy_officer: 'Economy Officer', diplomat: 'Diplomat',
  security: 'Security', opposition: 'Opposition',
  // New canonical position names (from positions[] array)
  military: 'Military', economy: 'Economy',
}
function displayPositions(role: { positions?: string[]; position_type: string }): string {
  const pos = role.positions
  // If positions array exists (even empty), it's the source of truth
  if (Array.isArray(pos)) {
    if (pos.length > 0) return pos.map(p => POS[p] ?? p).join(' + ')
    return 'Citizen'
  }
  // Fallback to legacy position_type only if positions not set
  return POS[role.position_type] ?? role.position_type ?? 'Citizen'
}

const RD: Record<number, string> = {
  0:'Pre-Sim',1:'H2 2026',2:'H1 2027',3:'H2 2027',4:'H1 2028',5:'H2 2028',6:'H1 2029',7:'H2 2029',8:'H1 2030',
}

function fmtTimer(s: number) { if (s<0) return 'OVERTIME'; const m=Math.floor(s/60),ss=Math.floor(s%60); return `${String(m).padStart(2,'0')}:${String(ss).padStart(2,'0')}` }

/* ── Action Catalog ────────────────────────────────────────────────────── */

// Phase restrictions (WORLD_MODEL Section 7):
// - move_units: only Phase B after engines complete
// - All other proactive actions: only Phase A (hidden during Phase B)
const PHASE_A_ONLY = new Set([
  'public_statement', 'invite_to_meet', 'call_org_meeting',
  'set_budget', 'set_tariffs', 'set_sanctions', 'set_opec',
  'propose_transaction', 'propose_agreement', 'basing_rights', 'declare_war',
  'attack', 'ground_attack', 'air_strike', 'naval_combat', 'naval_bombardment', 'launch_missile_conventional',
  'naval_blockade', 'martial_law', 'nuclear_test', 'nuclear_launch_initiate',
  'reassign_types', 'arrest', 'change_leader',
  'covert_operation', 'assassination', 'intelligence',
])
const PHASE_B_ONLY = new Set(['move_units'])

// Monochrome icons for action cards (Lucide)
const ACTION_ICONS: Record<string, LucideIcon> = {
  public_statement: Megaphone,
  invite_to_meet: MessageCircle,
  set_budget: PieChart,
  set_tariffs: Scale,
  set_sanctions: ShieldBan,
  set_opec: Fuel,
  propose_transaction: ArrowRightLeft,
  propose_agreement: ScrollText,
  basing_rights: Flag,
  declare_war: Swords,
  attack: Crosshair,
  naval_blockade: Anchor,
  move_units: Route,
  martial_law: Gavel,
  nuclear_test: Atom,
  nuclear_launch_initiate: Rocket,
  reassign_types: Network,
  arrest: Lock,
  change_leader: Crown,
  self_nominate: Hand,
  cast_vote: Vote,
  intelligence: Eye,
  covert_operation: Drama,
  assassination: Skull,
}

const AGREEMENT_TYPES = [
  { value: 'trade', label: 'Trade Agreement', relation: 'trade partners' },
  { value: 'military', label: 'Military Alliance', relation: 'military allies' },
  { value: 'diplomatic', label: 'Diplomatic Accord', relation: 'diplomatic partners' },
  { value: 'ceasefire', label: 'Ceasefire', relation: 'ceasefire parties' },
  { value: 'non_aggression', label: 'Non-Aggression Pact', relation: 'non-aggression partners' },
  { value: 'technology', label: 'Technology Sharing', relation: 'tech partners' },
  { value: 'defense', label: 'Defense Pact', relation: 'defense allies' },
  { value: 'free_format', label: 'Free Format', relation: '' },
]

type ActionCat = { key: string; label: string; actions: { id: string; label: string }[] }

const ACTION_CATS: Record<string, ActionCat> = {
  general: { key:'general', label:'General', actions:[
    {id:'public_statement',label:'Public Statement'},{id:'invite_to_meet',label:'Set Meetings'},
  ]},
  economic: { key:'economic', label:'Economic', actions:[
    {id:'set_budget',label:'Set Budget'},{id:'set_tariffs',label:'Set Tariffs'},
    {id:'set_sanctions',label:'Set Sanctions'},{id:'set_opec',label:'Set Cartel Production'},
  ]},
  intl: { key:'intl', label:'International Affairs & Trade', actions:[
    {id:'propose_transaction',label:'Propose Transaction'},
    {id:'propose_agreement',label:'Propose Agreement'},
    {id:'basing_rights',label:'Grant / Revoke Basing Rights'},
    {id:'declare_war',label:'Declare War'},
  ]},
  military: { key:'military', label:'Military', actions:[
    {id:'attack',label:'Attack'},
    {id:'naval_blockade',label:'Blockade'},{id:'move_units',label:'Move Units'},
    {id:'martial_law',label:'Martial Law'},{id:'nuclear_test',label:'Nuclear Test'},
    {id:'nuclear_launch_initiate',label:'Nuclear Launch'},
  ]},
  political: { key:'political', label:'Political', actions:[
    {id:'reassign_types',label:'Reassign Powers'},{id:'arrest',label:'Arrest'},{id:'change_leader',label:'Change Leader'},
  ]},
  covert: { key:'covert', label:'Secret Operations', actions:[
    {id:'intelligence',label:'Intelligence'},{id:'covert_operation',label:'Covert Operation'},{id:'assassination',label:'Assassination'},
  ]},
}

// Standard order for remaining categories (after general + primary)
const STANDARD_ORDER = ['economic', 'intl', 'military', 'political', 'covert']

// Map position types to their primary action category
const POSITION_TO_PRIMARY: Record<string, string> = {
  head_of_state: 'political',
  opposition: 'political',
  economy: 'economic',
  economy_officer: 'economic',
  diplomat: 'intl',
  military: 'military',
  military_chief: 'military',
  security: 'covert',
}

function getOrderedCats(positions: string[]): ActionCat[] {
  // Find primary categories for this role's positions
  const primaryKeys = new Set<string>()
  for (const pos of positions) {
    const pk = POSITION_TO_PRIMARY[pos]
    if (pk) primaryKeys.add(pk)
  }

  // If no positions (citizen), political is primary
  if (primaryKeys.size === 0) primaryKeys.add('political')

  // Build ordered list: general → primaries (in order) → remaining standard order
  const result: ActionCat[] = [ACTION_CATS.general]
  // Add primaries in standard order
  for (const key of STANDARD_ORDER) {
    if (primaryKeys.has(key)) result.push(ACTION_CATS[key])
  }
  // Add remaining in standard order
  for (const key of STANDARD_ORDER) {
    if (!primaryKeys.has(key)) result.push(ACTION_CATS[key])
  }
  return result
}

type NotificationData = {
  id: string; notification_type: string; title: string; body: string | null;
  payload: Record<string, unknown> | null; round_num: number; created_at: string;
  dismissed: boolean;
}

/* ── Main Component ────────────────────────────────────────────────────── */

export function ParticipantDashboard() {
  const isMobile = useIsMobile()
  const { simId } = useParams<{ simId: string }>()
  const location = useLocation()
  const { user, profile } = useAuth()

  // Clear stale request queue on mount (prevents Loading... stuck state after navigation)
  useEffect(() => { requestQueue.reset() }, [])

  // Proxy mode: ?role=sabre allows moderator to view as a specific role
  const urlParams = new URLSearchParams(window.location.search)
  const proxyRoleId = urlParams.get('role')
  const isProxyMode = !!proxyRoleId

  const [myRole, setMyRole] = useState<RoleData | null>(null)
  const [myCountry, setMyCountry] = useState<CountryData | null>(null)
  const [artefacts, setArtefacts] = useState<Artefact[]>([])
  const [tab, setTab] = useState<TabId>('actions')
  const [navNudge, setNavNudge] = useState(false)
  const [navLoading, setNavLoading] = useState(false)
  const [navModeSelector, setNavModeSelector] = useState(false)
  const [navMode, setNavMode] = useState<'voice' | 'text' | null>(null)
  const [navSession, setNavSession] = useState<{
    conversation_id: string; voice_agent_id: string; prompt: string; first_message: string; conversation_type: string
  } | null>(null)
  const [kpiBarHidden, setKpiBarHidden] = useState(false)
  const initialTabSetRef = useRef(false)
  const [wasFreshAssignment] = useState(() => (location.state as Record<string, unknown>)?.freshAssignment === true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Name change (R0 only)
  const [showNameModal, setShowNameModal] = useState(false)
  const [originalName, setOriginalName] = useState('')
  const [newName, setNewName] = useState('')
  const [nameError, setNameError] = useState<string | null>(null)
  const [nameSaving, setNameSaving] = useState(false)
  const [remaining, setRemaining] = useState<number | null>(null)
  const [broadcast, setBroadcast] = useState<string | null>(null)
  const [roleActions, setRoleActions] = useState<string[]>([])
  const [dataVersion, setDataVersion] = useState(0) // increments on every loadData — children use as refresh trigger
  const [objectives, setObjectives] = useState<string[]>([])
  const [myRelationships, setMyRelationships] = useState<{to_country_code:string;relationship:string;status:string}[]>([])
  const [myOrgMemberships, setMyOrgMemberships] = useState<{org_id:string;role_in_org:string;has_veto:boolean}[]>([])
  const [personalRels, setPersonalRels] = useState<{other_role:string;type:string;notes:string}[]>([])
  const [activeAction, setActiveAction] = useState<string|null>(null)
  const [activeChatMeetingId, setActiveChatMeetingId] = useState<string|null>(null)
  const [activeVoiceMeetingId, setActiveVoiceMeetingId] = useState<string|null>(null)
  const [pendingModeSelect, _setPendingModeSelect] = useState<{meetingId:string; counterpart:{name:string;country:string;position:string;voiceAgentId:string|null}} | null>(null)
  const pendingModeRef = useRef(false) // Sync guard for Realtime handler race condition
  // Wrapper: keeps ref in sync + pre-fetches voice context when popup shows
  const setPendingModeSelect = useCallback((val: typeof pendingModeSelect) => {
    pendingModeRef.current = val !== null
    _setPendingModeSelect(val)
    // Pre-fetch avatar context when popup shows (ready by the time user clicks Voice)
    if (val && simId) {
      prefetchedVoiceCtxRef.current = null
      const otherCountry = val.counterpart.country
      Promise.all([
        supabase.from('agent_memories').select('content')
          .eq('sim_run_id', simId).eq('country_code', otherCountry)
          .eq('memory_key', 'avatar_identity').limit(1),
        supabase.from('meetings').select('metadata,participant_a_role_id')
          .eq('id', val.meetingId).limit(1),
      ]).then(([identityRes, meetingRes]) => {
        const identity = identityRes.data?.[0]?.content as string || ''
        const meta = (meetingRes.data?.[0]?.metadata as Record<string, string>) || {}
        const aiIsA = meetingRes.data?.[0]?.participant_a_role_id !== myRole?.id
        const intentNote = (aiIsA ? meta.intent_note_a : meta.intent_note_b) || ''
        prefetchedVoiceCtxRef.current = { identity, intentNote }
      }).catch(() => {})
    }
  }, [simId, myRole?.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const [voiceContext, setVoiceContext] = useState<{identity:string;intentNote:string;voiceAgentId:string;counterpartName:string;counterpartCountry:string} | null>(null)
  const prefetchedVoiceCtxRef = useRef<{identity:string;intentNote:string} | null>(null)
  const [mySanctions, setMySanctions] = useState<{imposer:string;target:string;level:number}[]>([])
  const [myTariffs, setMyTariffs] = useState<{imposer:string;target:string;level:number}[]>([])
  const [fullCountry, setFullCountry] = useState<Record<string,unknown>|null>(null)
  const [activeMeetings, setActiveMeetings] = useState<MeetingData[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const prevRoundRef = useRef<number | null>(null)
  const [nuclearBannerDismissed, setNuclearBannerDismissed] = useState(false)
  const nuclearBannerTimerRef = useRef<ReturnType<typeof setTimeout>|null>(null)

  /* Realtime hook for sim_runs — replaces manual channel + polling -------- */
  const { data: simRun } = useRealtimeRow<SimRun>('sim_runs', simId)

  /* Realtime hook for nuclear_actions — global flight banner ------------- */
  const { data: globalNuclearActions } = useRealtimeTable<Record<string, unknown>>(
    'nuclear_actions', simId,
  )

  /* ── My Action Log data — lives in parent so it survives tab switches ── */
  const myCC = myRole?.country_code || ''
  const myRoleId = myRole?.id || ''
  const evtCols = 'id,event_type,round_num,summary,payload,created_at,category,role_name,country_code,visibility,visible_to'
  const logEventsOpts = useMemo(() => ({ eq: { country_code: myCC }, columns: evtCols, enabled: !!myCC }), [myCC])
  const logEventsVisibleToOpts = useMemo(() => ({ filter: `visible_to=cs.["${myCC}"]`, columns: evtCols, enabled: !!myCC }), [myCC])
  const logInvOpts = useMemo(() => ({ eq: { inviter_role_id: myRoleId }, columns: 'id,invitee_role_id,status,invitation_type,org_name', enabled: !!myRoleId }), [myRoleId])
  const logTxnOpts = useMemo(() => ({ eq: { proposer: myCC }, columns: 'id,counterpart,status', enabled: !!myCC }), [myCC])
  const logAgrOpts = useMemo(() => ({ eq: { proposer_country_code: myCC }, columns: 'id,agreement_name,agreement_type,signatories,signatures,status', enabled: !!myCC }), [myCC])
  const { data: myEventsOwn } = useRealtimeTable<Record<string, unknown>>('observatory_events', simId, logEventsOpts)
  const { data: myEventsVisibleTo } = useRealtimeTable<Record<string, unknown>>('observatory_events', simId, logEventsVisibleToOpts)
  // Merge own-country events + events from other countries where myCC is in visible_to, deduplicate by id
  const myEventsRaw = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>()
    for (const e of myEventsOwn) map.set(e.id as string, e)
    for (const e of myEventsVisibleTo) map.set(e.id as string, e)
    return Array.from(map.values())
  }, [myEventsOwn, myEventsVisibleTo])
  const { data: mySentInvitationsRaw } = useRealtimeTable<Record<string, unknown>>('meeting_invitations', simId, logInvOpts)
  const { data: myProposedTxnsRaw } = useRealtimeTable<Record<string, unknown>>('exchange_transactions', simId, logTxnOpts)
  const { data: myProposedAgrsRaw } = useRealtimeTable<Record<string, unknown>>('agreements', simId, logAgrOpts)

  /* Public events — for Newsbrief tab (all public events across all countries) */
  const publicEvtOpts = useMemo(() => ({ eq: { visibility: 'public' }, columns: evtCols, enabled: true }), [])
  const { data: publicEventsRaw } = useRealtimeTable<Record<string, unknown>>('observatory_events', simId, publicEvtOpts)

  /* Notification cards — lives in parent so it survives tab switches */
  const notifOpts = useMemo(() => ({ filter: `role_id=eq.${myRoleId}`, enabled: !!myRoleId }), [myRoleId])
  const { data: notificationsAllRaw } = useRealtimeTable<Record<string, unknown>>('participant_notifications', simId, notifOpts)
  const parentNotifications = useMemo(() =>
    (notificationsAllRaw as unknown as NotificationData[]).filter(n => !n.dismissed),
    [notificationsAllRaw]
  )
  const parentDismissNotification = useCallback(async (notificationId: string) => {
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token
      await fetch(`/api/sim/${simId}/notifications/${notificationId}/dismiss`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      })
    } catch { /* realtime will handle UI update */ }
  }, [simId])

  // Ticking countdown for nuclear flight banner
  const activeFlightAction = (globalNuclearActions as unknown as {
    id:string; status:string; country_code:string; payload:Record<string,unknown>;
    timer_started_at:string|null; timer_duration_sec:number|null;
  }[])?.find(a => a.status === 'awaiting_interception') ?? null
  const [nuclearCountdown, setNuclearCountdown] = useState<number|null>(null)
  useEffect(() => {
    if (!activeFlightAction) { setNuclearCountdown(null); return }
    const started = new Date(activeFlightAction.timer_started_at!).getTime()
    const dur = activeFlightAction.timer_duration_sec ?? 600
    const tick = () => setNuclearCountdown(Math.max(0, dur - (Date.now() - started) / 1000))
    tick()
    const iv = setInterval(tick, 1000)
    return () => clearInterval(iv)
  }, [activeFlightAction?.id, activeFlightAction?.status])

  /* Auto-open meeting when created — show mode popup for AI counterpart --- */
  // Role info populated by RPC (get_participant_data → all_roles)
  const [allRoleInfo, setAllRoleInfo] = useState<Record<string, {name:string;country:string;position:string;voiceAgentId:string|null;isAi:boolean}>>({})
  // KPI data from RPC (for Country tab indicators)
  const [rpcAllCountries, setRpcAllCountries] = useState<{id:string;gdp:number;stability:number;treasury:number;nuclear_level:number;ai_level:number;ai_rd_progress?:number}[]>([])
  const [rpcPrevSnapshots, setRpcPrevSnapshots] = useState<{country_code:string;gdp:number;stability:number;treasury:number;inflation:number;debt_burden:number;nuclear_level:number;ai_level:number;mil_ground:number;mil_naval:number;mil_tactical_air:number;mil_strategic_missiles:number;mil_air_defense:number}[]>([])
  const [rpcDeploymentCounts, setRpcDeploymentCounts] = useState<{country_code:string;unit_type:string;cnt:number}[]>([])

  useEffect(() => {
    if (!simId || !myRole) return
    const ch = supabase
      .channel(`meeting-autoopen:${myRole.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'meetings' },
        (payload) => {
          const row = payload.new as Record<string, unknown>
          if (row.sim_run_id !== simId) return
          // Only auto-open for meetings where I'm the INVITER (participant_a).
          // When I'm the accepter (participant_b), the accept button handler (onOpenChat) shows the popup.
          if (row.participant_a_role_id !== myRole.id) {
            // I am accepter (participant_b) — onOpenChat handles popup
            return
          }
          if (activeChatMeetingId || activeVoiceMeetingId || pendingModeRef.current) {
            // Already in a meeting or popup — skip
            return
          }
          // Auto-open popup for meeting where I'm the inviter

          const otherRoleId = row.participant_a_role_id === myRole.id
            ? row.participant_b_role_id as string
            : row.participant_a_role_id as string

          const info = allRoleInfo[otherRoleId]

          if (info?.isAi) {
            // AI counterpart — show mode selection popup
            setPendingModeSelect({
              meetingId: row.id as string,
              counterpart: {
                name: info.name,
                country: info.country,
                position: info.position,
                voiceAgentId: info.voiceAgentId,
              },
            })
          } else {
            // Human counterpart — go straight to text chat
            setActiveChatMeetingId(row.id as string)
          }
        },
      )
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [simId, myRole?.id, allRoleInfo]) // eslint-disable-line react-hooks/exhaustive-deps

  /** Handle mode selection from popup — FULLY SYNCHRONOUS (no awaits). */
  const handleModeSelect = (mode: 'text' | 'voice') => {
    if (!pendingModeSelect || !simId) return
    const { meetingId, counterpart } = pendingModeSelect

    // Write modality to DB — fire and forget (don't await, Supabase client may be busy)
    supabase.from('meetings').update({ modality: mode }).eq('id', meetingId).then(() => {}).catch(() => {})

    // Clear popup FIRST
    setPendingModeSelect(null)

    if (mode === 'text') {
      setActiveVoiceMeetingId(null)
      setVoiceContext(null)
      setActiveChatMeetingId(meetingId)
    } else {
      setActiveChatMeetingId(null)
      setActiveVoiceMeetingId(meetingId)
      setVoiceContext(null) // loading state — VoiceCallInterface won't mount yet

      // Fetch avatar context directly (not from pre-fetch ref — too unreliable)
      const otherCountry = counterpart.country
      Promise.all([
        supabase.from('agent_memories').select('content')
          .eq('sim_run_id', simId).eq('country_code', otherCountry)
          .eq('memory_key', 'avatar_identity').limit(1),
        supabase.from('meetings').select('metadata,participant_a_role_id')
          .eq('id', meetingId).limit(1),
      ]).then(([identityRes, meetingRes]) => {
        const identity = identityRes.data?.[0]?.content as string || ''
        const meta = (meetingRes.data?.[0]?.metadata as Record<string, string>) || {}
        const aiIsA = meetingRes.data?.[0]?.participant_a_role_id !== myRole?.id
        const intentNote = (aiIsA ? meta.intent_note_a : meta.intent_note_b) || ''
        setVoiceContext({
          identity: identity || `Head of state of ${counterpart.country}. Role: ${counterpart.name}.`,
          intentNote,
          voiceAgentId: counterpart.voiceAgentId || '',
          counterpartName: counterpart.name,
          counterpartCountry: counterpart.country,
        })
      }).catch(() => {
        setVoiceContext({
          identity: `Head of state of ${counterpart.country}. Role: ${counterpart.name}.`,
          intentNote: '',
          voiceAgentId: counterpart.voiceAgentId || '',
          counterpartName: counterpart.name,
          counterpartCountry: counterpart.country,
        })
      })
    }
  }

  /* Derive simState from the realtime sim_runs row ----------------------- */
  const simState: SimState | null = simRun ? {
    status: simRun.status,
    current_round: simRun.current_round,
    current_phase: simRun.current_phase,
    phase_started_at: simRun.phase_started_at || simRun.started_at,
    phase_duration_seconds: simRun.phase_duration_seconds
      ?? ((simRun.schedule as Record<string, number>)?.phase_a_minutes
        ? (simRun.schedule as Record<string, number>).phase_a_minutes * 60
        : null),
  } : null

  /* loadData — fetches role via direct query, then calls get_participant_data RPC
     for all remaining data. Called once on mount and on round changes. ------ */
  const loadData = useCallback(async () => {
    if (!simId || !user) return
    try {
      // Step 1: Load role (proxy mode uses role_id, normal mode uses user_id)
      const roleQuery = supabase.from('roles')
        .select('id,character_name,country_code,positions,title,public_bio,confidential_brief,objectives,powers,status,status_detail')
        .eq('sim_run_id',simId)
      const { data: roles } = proxyRoleId
        ? await roleQuery.eq('id', proxyRoleId).limit(1)
        : await roleQuery.eq('user_id', user.id).limit(1)
      if (roles?.[0]) {
        const role = roles[0] as RoleData; setMyRole(role)
        setObjectives(Array.isArray(role.objectives) ? role.objectives as string[] : [])

        // Step 2: Single RPC call replaces 9 parallel queries
        // Read round from DB row directly (simRun state may be stale on first load)
        const freshRun = await supabase.from('sim_runs').select('current_round').eq('id', simId).limit(1).single()
        const currentRoundNum = freshRun.data?.current_round ?? simRun?.current_round ?? 1
        const { data, error: rpcError } = await supabase.rpc('get_participant_data', {
          p_sim_id: simId, p_role_id: role.id, p_country: role.country_code, p_round_num: currentRoundNum,
        })
        if (rpcError) throw rpcError

        if (data) {
          // Country — inject military counts from deployment_counts (single source of truth)
          if (data.country) {
            const c = data.country as CountryData
            const depCounts = (data.deployment_counts || []) as {country_code:string;unit_type:string;cnt:number}[]
            c.mil_ground = 0; c.mil_naval = 0; c.mil_tactical_air = 0; c.mil_strategic_missiles = 0; c.mil_air_defense = 0
            for (const d of depCounts) {
              if (d.country_code !== c.id) continue
              if (d.unit_type === 'ground') c.mil_ground = Number(d.cnt)
              else if (d.unit_type === 'naval') c.mil_naval = Number(d.cnt)
              else if (d.unit_type === 'tactical_air') c.mil_tactical_air = Number(d.cnt)
              else if (d.unit_type === 'strategic_missile') c.mil_strategic_missiles = Number(d.cnt)
              else if (d.unit_type === 'air_defense') c.mil_air_defense = Number(d.cnt)
            }
            setMyCountry(c)
            setFullCountry(data.country as Record<string, unknown>)
          }
          // Artefacts
          setArtefacts((data.artefacts || []) as Artefact[])
          // Role actions
          setRoleActions((data.role_actions || []) as string[])
          // Relationships
          setMyRelationships((data.relationships || []) as typeof myRelationships)
          // Org memberships
          setMyOrgMemberships((data.org_memberships || []) as typeof myOrgMemberships)
          // Personal relationships
          setPersonalRels((data.personal_rels || []) as typeof personalRels)
          // Sanctions
          setMySanctions((data.sanctions || []) as typeof mySanctions)
          // Tariffs
          setMyTariffs((data.tariffs || []) as typeof myTariffs)
          // Active meetings
          setActiveMeetings((data.active_meetings || []) as MeetingData[])

          // Build allRoleInfo from all_roles
          const roleInfoMap: Record<string, {name:string;country:string;position:string;voiceAgentId:string|null;isAi:boolean}> = {}
          for (const r of (data.all_roles || []) as {id:string;character_name:string;country_code:string;positions:string[];is_ai_operated:boolean;elevenlabs_agent_id:string|null;status:string}[]) {
            const positions = r.positions || []
            const pos = positions.includes('head_of_state') ? 'Head of State'
              : positions.includes('military') ? 'Military Chief'
              : positions.includes('diplomat') ? 'Diplomat'
              : positions.includes('economy') ? 'Economy Officer'
              : positions.includes('security') ? 'Security'
              : positions.includes('opposition') ? 'Opposition'
              : positions[0] || ''
            roleInfoMap[r.id] = {
              name: r.character_name || r.id,
              country: r.country_code || '',
              position: pos,
              voiceAgentId: r.elevenlabs_agent_id || null,
              isAi: !!r.is_ai_operated,
            }
          }
          setAllRoleInfo(roleInfoMap)

          // KPI data — all countries + prev round snapshots + deployment counts
          const ac = (data.all_countries || []) as {id:string;gdp:number;stability:number;treasury:number;nuclear_level:number;ai_level:number}[]
          setRpcAllCountries(ac.map(c => ({...c, gdp:Number(c.gdp), stability:Number(c.stability), treasury:Number(c.treasury), nuclear_level:Number(c.nuclear_level), ai_level:Number(c.ai_level)})))
          const ps = (data.prev_round_snapshots || []) as Record<string, unknown>[]
          setRpcPrevSnapshots(ps.map(r => ({
            country_code: r.country_code as string,
            gdp: Number(r.gdp ?? 0), stability: Number(r.stability ?? 0), treasury: Number(r.treasury ?? 0),
            inflation: Number(r.inflation ?? 0), debt_burden: Number(r.debt_burden ?? 0),
            nuclear_level: Number(r.nuclear_level ?? 0), ai_level: Number(r.ai_level ?? 0),
            mil_ground: Number(r.mil_ground ?? 0), mil_naval: Number(r.mil_naval ?? 0),
            mil_tactical_air: Number(r.mil_tactical_air ?? 0), mil_strategic_missiles: Number(r.mil_strategic_missiles ?? 0),
            mil_air_defense: Number(r.mil_air_defense ?? 0),
          })))
          setRpcDeploymentCounts((data.deployment_counts || []) as {country_code:string;unit_type:string;cnt:number}[])
        }

        // Tab selection on first load:
        // - Fresh role assignment (from RoleAssignmentWatcher pop-up) → Confidential
        // - Normal page load / refresh → Actions (default from useState)
        if (!initialTabSetRef.current && simRun) {
          initialTabSetRef.current = true
          if (wasFreshAssignment) {
            setTab('confidential')
            // Clear the navigation state so refresh lands on Actions
            window.history.replaceState({}, '')
          }
        }

      } else {
        // No role in this simrun — show observer mode (world + map tabs only)
        // Don't redirect — user may be exploring before assignment
        setTab('world')
      }
      setError(null)
    } catch(e) { setError(e instanceof Error?e.message:'Failed') }
    finally { setLoading(false); setDataVersion(v => v + 1) }
  }, [simId, user])

  // Stable callbacks for MobileActionOverlay — prevents React.memo bypass from timer re-renders
  const mobileFormClose = useCallback(() => setActiveAction(null), [])
  const mobileFormSubmitted = useCallback(() => { setActiveAction(null); loadData() }, [loadData])

  // Initial data load — once on mount
  useEffect(() => { loadData() }, [loadData])

  // Navigator nudge — pulse the button when intro/outro is needed
  useEffect(() => {
    if (!simId) return
    const check = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session) return
        const resp = await fetch(`/api/sim/${simId}/navigator/history`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        })
        if (!resp.ok) return
        const json = await resp.json()
        const h = json.data
        const status = simRun?.status || ''
        const needsIntro = (status === 'pre_start' || status === 'setup' || status === 'active') && !h.intro_complete
        const needsOutro = status === 'completed' && !h.outro_complete
        setNavNudge(needsIntro || needsOutro)
      } catch { /* non-critical */ }
    }
    check()
  }, [simId, simRun?.status])

  // Reload data when round changes
  useEffect(() => {
    if (simRun === null) return
    const newRound = simRun.current_round
    if (prevRoundRef.current !== null && prevRoundRef.current !== newRound) {
      loadData()
    }
    prevRoundRef.current = newRound
  }, [simRun?.current_round, loadData])

  // CONSOLIDATED Realtime channel (SPEC v2.0 Pattern B)
  // ONE channel monitors ALL tables that affect participant view.
  // On any change → debounced RPC refetch (not individual state patches).
  // Replaces: role-assign, participant-data, role-status channels.
  useEffect(() => {
    if (!simId || !user?.id) return
    let debounceTimer: ReturnType<typeof setTimeout> | null = null
    const debouncedLoad = () => {
      if (debounceTimer || pendingModeRef.current) return
      debounceTimer = setTimeout(() => {
        debounceTimer = null
        if (!pendingModeRef.current) loadData()
      }, 2000)
    }
    const ch = supabase
      .channel(`participant-all-${user.id}-${simId}`)
      // Role changes: assignment, arrest/release, status
      .on('postgres_changes', { event: '*', schema: 'public', table: 'roles',
        filter: `sim_run_id=eq.${simId}` }, (payload) => {
        const row = payload.new as Record<string, unknown>
        // Role assigned to me OR my role status changed
        if (row.user_id === user.id || row.id === myRole?.id) debouncedLoad()
      })
      // Country stats
      .on('postgres_changes', { event: '*', schema: 'public', table: 'countries',
        filter: `sim_run_id=eq.${simId}` }, debouncedLoad)
      // Artefacts (intel, cables)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'artefacts',
        filter: `sim_run_id=eq.${simId}` }, debouncedLoad)
      // Diplomacy
      .on('postgres_changes', { event: '*', schema: 'public', table: 'relationships',
        filter: `sim_run_id=eq.${simId}` }, debouncedLoad)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'sanctions',
        filter: `sim_run_id=eq.${simId}` }, debouncedLoad)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tariffs',
        filter: `sim_run_id=eq.${simId}` }, debouncedLoad)
      // Meetings
      .on('postgres_changes', { event: '*', schema: 'public', table: 'meetings',
        filter: `sim_run_id=eq.${simId}` }, debouncedLoad)
      .subscribe()
    // Visibility refetch — when tab becomes visible, reload data (M2 SPEC Phase 7)
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadData()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      supabase.removeChannel(ch)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [simId, user?.id, myRole?.id, loadData]) // eslint-disable-line react-hooks/exhaustive-deps

  /* Timer countdown ------------------------------------------------------- */
  useEffect(() => {
    if(timerRef.current)clearInterval(timerRef.current)
    if(!simState?.phase_started_at||!simState.phase_duration_seconds||['paused','setup','pre_start','completed'].includes(simState.status||'')){setRemaining(null);return}
    const tick=()=>{const e=(Date.now()-new Date(simState.phase_started_at!).getTime())/1000;setRemaining(simState.phase_duration_seconds!-e)}
    tick();timerRef.current=setInterval(tick,1000)
    return()=>{if(timerRef.current)clearInterval(timerRef.current)}
  }, [simState?.phase_started_at,simState?.phase_duration_seconds,simState?.status])

  const round = simState?.current_round ?? 0
  const color = myCountry?.color_ui ?? '#4A5568'
  const hasRole = !!myRole
  const isR0 = round === 0 || simState?.status === 'pre_start'

  const handleSaveName = async () => {
    if (!myRole || !newName.trim()) { setNameError('Name cannot be empty'); return }
    setNameSaving(true); setNameError(null)
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token
      const res = await fetch(`/api/sim/${simId}/roles/${myRole.id}/name`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim() }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Failed to save' }))
        throw new Error(err.detail || 'Failed to save')
      }
      setMyRole({ ...myRole, character_name: newName.trim() })
      setShowNameModal(false)
    } catch (e) { setNameError(e instanceof Error ? e.message : 'Failed to save') }
    finally { setNameSaving(false) }
  }
  const unread = artefacts.filter(a=>!a.is_read).length
  const actionsBadge = tab !== 'actions' ? (parentNotifications.length || undefined) : undefined

  const tabs:{id:TabId;label:string;badge?:number;off?:boolean}[] = hasRole
    ? [
        {id:'actions',label:'Actions',badge:actionsBadge},
        {id:'confidential',label:'Confidential',badge:unread||undefined},
        {id:'country',label:'Country'},
        {id:'world',label:'World'},
        {id:'newsbrief',label:'Newsbrief'},
      ]
    : [
        // Unassigned: world only (observer mode — map is inside World tab)
        {id:'world',label:'World'},
      ]

  if (loading) return <div className="min-h-screen bg-base flex items-center justify-center"><div className="w-8 h-8 border-2 border-action border-t-transparent rounded-full animate-spin"/></div>
  if (error||!simRun) return <div className="min-h-screen bg-base flex items-center justify-center"><div className="bg-card border border-danger/30 rounded-lg p-8"><p className="font-body text-body text-danger">{error??'Not found'}</p></div></div>

  // Dynamic nuclear actions — computed from country state, not role_actions table.
  // HoS can test when nuclear_level >= 1. HoS can launch when confirmed + has missiles.
  const isHoS = myRole?.positions?.includes('head_of_state') ?? false
  const nucLevel = myCountry?.nuclear_level ?? 0
  const nucConfirmed = myCountry?.nuclear_confirmed ?? false
  const hasStrategicMissiles = rpcDeploymentCounts.some(d => d.country_code === myRole?.country_code && d.unit_type === 'strategic_missile' && Number(d.cnt) > 0)
  const dynamicActions: string[] = []
  if (isHoS && nucLevel >= 1) dynamicActions.push('nuclear_test')
  if (isHoS && nucConfirmed && hasStrategicMissiles) dynamicActions.push('nuclear_launch_initiate')
  const effectiveRoleActions = [...roleActions, ...dynamicActions]

  // === MOBILE LAYOUT ===
  if (isMobile && !isProxyMode) {
    // Build KPI data from available state
    const mobileKPI = myCountry ? {
      stability: myCountry.stability ?? 5,
      deltaStability: null as number | null,
      treasury: myCountry.treasury ?? 0,
      deltaTreasury: null as number | null,
      gdpRank: 0,
      deltaGdpRank: null as number | null,
      milRank: 0,
      deltaMilRank: null as number | null,
      nukeRank: null as number | null,
      deltaNukeRank: null as number | null,
      nukeLevel: myCountry.nuclear_level ?? 0,
      aiRank: null as number | null,
      deltaAiRank: null as number | null,
      aiLevel: myCountry.ai_level ?? 0,
      wars: myRelationships?.filter((r: Record<string, unknown>) => r.relationship === 'at_war').map((r: Record<string, unknown>) => String(r.to_country_code)) ?? [],
    } : null

    // Render tab content — reuse desktop components where possible, mobile-adapt layout
    const renderMobileTab = (tabId: string) => {
      // === ACTIONS TAB (Chunk 2a) ===
      if (tabId === 'actions' && myRole) {
        if (myRole.status === 'arrested' || myRole.status === 'killed') {
          return (
            <div className="bg-danger/5 border border-danger/20 rounded-lg p-4 text-center">
              <h3 className="font-heading text-body-sm text-danger font-semibold mb-1">
                {myRole.status === 'killed' ? (myRole.status_detail?.cause === 'health' ? 'Passed Away' : 'Eliminated') : 'Arrested'}
              </h3>
              <p className="font-body text-caption text-text-secondary">
                {myRole.status === 'killed' ? 'No actions available.' : 'You will be released at the end of this round.'}
              </p>
            </div>
          )
        }
        // Action list + notifications (reuse TabActions)
        // Note: active action form renders as a portal at the MobileParticipantLayout level (below)
        return (
          <TabActions
            roleActions={effectiveRoleActions}
            rolePositions={myRole.positions ?? [myRole.position_type]}
            currentPhase={simState?.current_phase ?? 'pre'}
            onSelectAction={setActiveAction}
            simId={simId!}
            countryId={myRole.country_code}
            roleId={myRole.id}
            dataVersion={dataVersion}
            nuclearActionsData={globalNuclearActions}
            parentOrgIds={new Set(myOrgMemberships.map(m => m.org_id))}
            parentSimRun={simRun}
            parentActiveMeetings={activeMeetings}
            parentSetActiveMeetings={setActiveMeetings}
            parentAllRoleInfo={allRoleInfo}
            actionLogEvents={myEventsRaw}
            actionLogInvitations={mySentInvitationsRaw}
            actionLogTxns={myProposedTxnsRaw}
            actionLogAgrs={myProposedAgrsRaw}
            parentNotifications={parentNotifications}
            parentDismissNotification={parentDismissNotification}
            navNudge={navNudge}
            onStartNavigator={async () => {/* handled by bottom nav */}}
            onOpenChat={(meetingId: string, counterpartRoleId?: string) => {
              const otherRoleId = counterpartRoleId
              if (otherRoleId) {
                const info = allRoleInfo[otherRoleId]
                if (info?.isAi) {
                  setPendingModeSelect({
                    meetingId,
                    counterpart: { name: info.name, country: info.country, position: info.position, voiceAgentId: info.voiceAgentId },
                  })
                  return
                }
              }
              setActiveChatMeetingId(meetingId)
            }}
          />
        )
      }

      // === CONFIDENTIAL TAB (Chunk 3) — reuse desktop component directly ===
      if (tabId === 'confidential' && myRole) {
        return <TabConf role={myRole} artefacts={artefacts} objectives={objectives}
          personalRels={personalRels} orgMemberships={myOrgMemberships}
          isR0={isR0}
          onNameChange={() => { setOriginalName(myRole.character_name); setNewName(myRole.character_name); setNameError(null); setShowNameModal(true) }}
          onRead={id => {
            supabase.from('artefacts').update({ is_read: true }).eq('id', id).then(() => {
              setArtefacts(p => p.map(a => a.id === id ? { ...a, is_read: true } : a))
            })
          }} />
      }

      // === COUNTRY TAB (Chunk 4) — reuse desktop component ===
      if (tabId === 'country' && myCountry) {
        return <TabCountry country={myCountry} fullCountry={fullCountry}
          sanctions={mySanctions} tariffs={myTariffs} relationships={myRelationships}
          orgMemberships={myOrgMemberships} simId={simId!} currentRound={round}
          rpcAllCountries={rpcAllCountries} rpcPrevSnapshots={rpcPrevSnapshots}
          rpcDeploymentCounts={rpcDeploymentCounts} />
      }

      // === WORLD TAB (Chunk 5) — includes Map as first sub-tab ===
      if (tabId === 'world') {
        return <TabWorld simId={simId!} round={round} isMobile />
      }

      // === NEWSBRIEF TAB (Chunk 5) ===
      if (tabId === 'newsbrief' && myRole && myCountry) {
        return <TabNewsbrief simId={simId!} countryId={myCountry.id} country={myCountry} round={round}
          publicEvents={publicEventsRaw} myEvents={myEventsRaw}
          notifications={notificationsAllRaw as unknown as NotificationData[]}
          prevSnapshots={rpcPrevSnapshots} deploymentCounts={rpcDeploymentCounts}
          relationships={myRelationships} sanctions={mySanctions} tariffs={myTariffs}
          fullCountry={fullCountry} />
      }

      return <div className="p-4 text-center text-text-secondary font-body text-caption">Coming soon</div>
    }

    return (
      <>
        <MobileParticipantLayout
          simId={simId!}
          simState={simState}
          remaining={remaining}
          myRole={myRole}
          myCountry={myCountry}
          profile={profile}
          color={color}
          round={round}
          hasRole={hasRole}
          parentNotifications={parentNotifications}
          artefacts={artefacts}
          broadcast={broadcast}
          setBroadcast={setBroadcast}
          kpiData={mobileKPI}
          renderTabContent={renderMobileTab}
          onMobileTabChange={() => setActiveAction(null)}
          initialTab={!hasRole ? 'world' : (wasFreshAssignment ? 'confidential' : undefined)}
        />
        {/* Action form overlay — rendered OUTSIDE layout to escape stacking context */}
        {activeAction && myRole && (
          <MobileActionOverlay
            actionType={activeAction}
            roleId={myRole.id}
            roleName={myRole.character_name}
            countryId={myRole.country_code}
            simId={simId!}
            onClose={mobileFormClose}
            onSubmitted={mobileFormSubmitted}
          />
        )}

        {/* Meeting mode selection popup */}
        {pendingModeSelect && (
          <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center backdrop-blur-sm">
            <div className="bg-card border border-border rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
              <div className="flex items-center gap-3 mb-4">
                <CountryEmblem countryId={pendingModeSelect.counterpart.country} color="#6B7280" size={28} />
                <span className="font-heading text-h2 text-text-primary">{pendingModeSelect.counterpart.name}</span>
              </div>
              <p className="font-body text-body-sm text-text-secondary mb-4">Choose how to communicate:</p>
              <div className="flex gap-3">
                <button onClick={() => handleModeSelect('text')}
                  className="flex-1 bg-action/10 text-action font-body text-body-sm font-medium py-3 rounded-lg hover:bg-action/20 min-h-[48px]">
                  Text Chat
                </button>
                {pendingModeSelect.counterpart.voiceAgentId && (
                  <button onClick={() => handleModeSelect('voice')}
                    className="flex-1 bg-accent/10 text-accent font-body text-body-sm font-medium py-3 rounded-lg hover:bg-accent/20 min-h-[48px]">
                    Voice Call
                  </button>
                )}
              </div>
              <button onClick={() => setPendingModeSelect(null)}
                className="w-full mt-2 font-body text-caption text-text-secondary py-2 hover:text-text-primary">
                Not now
              </button>
            </div>
          </div>
        )}

        {/* Meeting chat overlay — z-[58] to appear above mobile layout (z-40) */}
        {activeChatMeetingId && myRole && (
          <div className="fixed inset-0 z-[58]">
            <MeetingChat
              meetingId={activeChatMeetingId}
              simId={simId!}
              myRoleId={myRole.id}
              myCountryCode={myRole.country_code}
              myCharacterName={myRole.character_name}
              onClose={() => { setActiveChatMeetingId(null); loadData() }}
            />
          </div>
        )}

        {/* Voice call overlay — z-[58] */}
        {voiceContext && myRole && (
          <div className="fixed inset-0 z-[58]">
            <VoiceCallInterface
              meetingId={voiceContext.meetingId}
              simId={simId!}
              myRoleId={myRole.id}
              myCountryCode={myRole.country_code}
              myCharacterName={myRole.character_name}
              counterpartName={voiceContext.counterpartName}
              counterpartCountry={voiceContext.counterpartCountry}
              voiceAgentId={voiceContext.voiceAgentId}
              avatarIdentity={voiceContext.avatarIdentity}
              intentNote={voiceContext.intentNote}
              onEnd={() => { setVoiceContext(null); loadData() }}
            />
          </div>
        )}

        {/* Name Change Modal (R0 only) — shared with desktop */}
        {showNameModal && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-card rounded-xl shadow-2xl max-w-md w-full p-6">
              <h2 className="font-heading text-h2 text-text-primary mb-4">Change Your Character Name</h2>
              <div className="mb-3">
                <div className="font-body text-caption text-text-secondary mb-1">Original Name</div>
                <div className="px-4 py-2.5 bg-base rounded-lg font-body text-body-sm text-text-secondary">{originalName}</div>
              </div>
              <div className="mb-3">
                <div className="font-body text-caption text-text-secondary mb-1">New Name</div>
                <input type="text" value={newName} autoFocus
                  onChange={e => { setNewName(e.target.value); setNameError(null) }}
                  className="w-full px-4 py-3 border border-border rounded-lg font-body text-body-sm text-text-primary bg-base focus:outline-none focus:border-action"
                  placeholder="Enter your new character name" />
              </div>
              {nameError && (
                <div className="mb-3 p-2.5 bg-danger/10 border border-danger/20 rounded-lg">
                  <p className="font-body text-caption text-danger">{nameError}</p>
                </div>
              )}
              <div className="flex gap-3">
                <button onClick={() => setShowNameModal(false)} disabled={nameSaving}
                  className="flex-1 px-4 py-2.5 border border-border rounded-lg font-body text-body-sm text-text-secondary hover:bg-base transition-colors">
                  Cancel
                </button>
                <button onClick={handleSaveName} disabled={nameSaving}
                  className="flex-1 px-4 py-2.5 bg-action text-white rounded-lg font-body text-body-sm font-medium disabled:opacity-50 hover:bg-action/90 transition-colors">
                  {nameSaving ? 'Saving...' : 'Save Name'}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  // === DESKTOP LAYOUT ===
  return (
    <div className="min-h-screen bg-base flex flex-col">
      {/* HEADER */}
      <header className="sticky top-0 z-30 bg-card border-b-2 px-6 py-3" style={{borderBottomColor:color}}>
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            {hasRole ? (<>
              <CountryEmblem countryId={myRole!.country_code} color={color} size={36} />
              <div>
                <div className="font-heading text-h3 text-text-primary">{myRole!.character_name}</div>
                <div className="font-body text-caption text-text-secondary">{myRole!.title} · {myCountry?.sim_name??myRole!.country_code}</div>
              </div>
              <span className="font-body text-caption font-medium px-2 py-0.5 rounded" style={{backgroundColor:`${color}15`,color}}>{displayPositions(myRole!)}</span>
            </>) : (<div>
              <div className="font-heading text-h3 text-text-primary">{profile?.display_name??'Participant'}</div>
              <div className="font-body text-caption text-text-secondary">Awaiting role assignment</div>
            </div>)}
          </div>
          <div className="flex items-center gap-4">
            {simState?.current_phase==='B'&&<span className="font-body text-caption font-semibold text-action bg-action/10 px-2 py-0.5 rounded">BREAK</span>}
            {simState?.status==='processing'&&<span className="font-body text-caption font-semibold text-warning bg-warning/10 px-2 py-0.5 rounded">PROCESSING</span>}
            {simState?.status==='paused'&&<span className="font-body text-caption text-warning bg-warning/10 px-2 py-0.5 rounded">PAUSED</span>}
            <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0 items-baseline">
              <span className="font-data text-data-lg text-text-primary">R{round}</span>
              {remaining!==null?<span className={`font-data text-data-lg ${remaining<0?'text-danger animate-pulse':'text-text-primary'}`}>{fmtTimer(remaining)}</span>:<span/>}
              <span className="font-body text-caption text-text-secondary">{RD[round]??''}</span>
              <button onClick={async()=>{await supabase.auth.signOut();window.location.href='/login'}} className="font-body text-caption text-text-secondary/50 hover:text-danger text-right">sign out</button>
            </div>
          </div>
        </div>
      </header>

      {/* Proxy mode banner */}
      {isProxyMode&&<div className="bg-danger/10 border-b border-danger/30 px-6 py-2">
        <div className="max-w-7xl mx-auto flex items-center gap-3">
          <span className="font-body text-caption font-bold text-danger uppercase tracking-wider">Moderator View</span>
          <span className="font-body text-body-sm text-text-primary">
            Viewing as {myRole?.character_name ?? proxyRoleId} — actions will be attributed to this role
          </span>
          <button onClick={()=>window.close()} className="ml-auto font-body text-caption text-danger hover:underline">Close</button>
        </div>
      </div>}

      {broadcast&&<div className="bg-warning/10 border-b border-warning/30 px-6 py-2"><div className="max-w-7xl mx-auto flex items-center gap-3">
        <span className="font-body text-caption font-bold text-warning uppercase">Broadcast</span>
        <span className="font-body text-body-sm text-text-primary flex-1">{broadcast}</span>
        <button onClick={()=>setBroadcast(null)} className="text-text-secondary hover:text-text-primary">{'\u2715'}</button>
      </div></div>}

      {/* Arrested banner — blocks all actions */}
      {myRole?.status === 'arrested' && <ArrestedBanner simId={simId!} roleId={myRole.id} round={round} />}

      {/* Killed banner */}
      {myRole?.status === 'killed' && (
        <div className="bg-danger/10 border-b-2 border-danger/40 px-6 py-4">
          <div className="max-w-7xl mx-auto">
            <p className="font-heading text-h3 text-danger">{myRole.status_detail?.cause === 'health' ? 'Your Leader Has Passed Away' : 'Your Character Has Been Eliminated'}</p>
            <p className="font-body text-body-sm text-text-primary">
              {myRole.status_detail?.cause === 'health'
                ? 'Your character has passed away. You can no longer take any actions in this SIM.'
                : 'Your character has been eliminated. You can no longer take any actions in this SIM.'}
            </p>
          </div>
        </div>
      )}

      {/* Nuclear Flight Banner — shown to ALL participants during Phase 3 */}
      {(() => {
        const resolvedAction = !activeFlightAction && !nuclearBannerDismissed && (globalNuclearActions as unknown as {
          id:string; status:string; country_code:string; resolved_at:string|null;
        }[])?.find(a => {
          if (a.status !== 'resolved') return false
          if (!a.resolved_at) return false
          const resolvedAt = new Date(a.resolved_at).getTime()
          return (Date.now() - resolvedAt) < 30000 // Show for 30s after resolution
        })

        if (activeFlightAction && nuclearCountdown !== null) {
          const missiles = ((activeFlightAction.payload?.changes as Record<string,unknown>)?.missiles as unknown[]) ?? []
          const mm = String(Math.floor(nuclearCountdown / 60)).padStart(2, '0')
          const ss = String(Math.floor(nuclearCountdown % 60)).padStart(2, '0')

          return (
            <div style={{
              width:'100%',backgroundColor:'#991B1B',padding:'0.75rem 1.5rem',
              display:'flex',alignItems:'center',justifyContent:'center',gap:'1.5rem',
              animation:'nuclearBannerFlash 2s ease-in-out infinite',zIndex:40,
            }}>
              <span style={{
                fontFamily:'"JetBrains Mono",monospace',fontSize:'1.1rem',fontWeight:700,
                color:'#FFFFFF',letterSpacing:'0.1em',textTransform:'uppercase',
              }}>
                {'\u26A0'} BALLISTIC MISSILE LAUNCH DETECTED {'\u2014'} {activeFlightAction.country_code.toUpperCase()}
              </span>
              <span style={{
                fontFamily:'"JetBrains Mono",monospace',fontSize:'1.1rem',fontWeight:700,
                color:'#FFFFFF',letterSpacing:'0.1em',
              }}>
                {missiles.length} MISSILE{missiles.length > 1 ? 'S' : ''} INBOUND {'\u2014'} Impact in {mm}:{ss}
              </span>
            </div>
          )
        }

        if (resolvedAction) {
          // Auto-dismiss after 30s
          if (!nuclearBannerTimerRef.current) {
            nuclearBannerTimerRef.current = setTimeout(() => {
              setNuclearBannerDismissed(true)
              nuclearBannerTimerRef.current = null
            }, 30000)
          }
          return (
            <div style={{
              width:'100%',backgroundColor:'#1E3A5F',padding:'0.75rem 1.5rem',
              display:'flex',alignItems:'center',justifyContent:'center',gap:'1rem',
              zIndex:40,
            }}>
              <span style={{
                fontFamily:'"JetBrains Mono",monospace',fontSize:'1rem',fontWeight:700,
                color:'#FFFFFF',letterSpacing:'0.1em',textTransform:'uppercase',
              }}>
                NUCLEAR STRIKE RESOLVED
              </span>
            </div>
          )
        }
        return null
      })()}
      <style>{`
        @keyframes nuclearBannerFlash {
          0%, 100% { background-color: #991B1B; }
          50% { background-color: #DC2626; }
        }
      `}</style>

      {/* KPI BAR — persistent indicators */}
      {hasRole && myCountry && (
        <div className="bg-card border-b border-border px-6">
          <div className="max-w-7xl mx-auto">
            {kpiBarHidden ? (
              <button onClick={() => setKpiBarHidden(false)} className="w-full py-1 font-body text-[9px] text-text-secondary hover:text-action text-center">▼ Show indicators</button>
            ) : (
              <div className="py-1.5">
                {(()=>{
                  const cc = myCountry.id
                  // Build live unit counts
                  const depCnts: Record<string, Record<string, number>> = {}
                  for (const d of rpcDeploymentCounts) { if (!depCnts[d.country_code]) depCnts[d.country_code] = {}; depCnts[d.country_code][d.unit_type] = Number(d.cnt) }
                  const allLive = rpcAllCountries.map(c => ({ ...c, live_ground: depCnts[c.id]?.ground??0, live_naval: depCnts[c.id]?.naval??0, live_air: depCnts[c.id]?.tactical_air??0, live_ad: depCnts[c.id]?.air_defense??0, live_missiles: depCnts[c.id]?.strategic_missile??0 }))
                  const myL = allLive.find(c => c.id === cc)
                  const myMight = myL ? calcMilitaryMightLive(myL.live_ground, myL.live_naval, myL.live_air, myL.live_ad, myL.live_missiles, myL.nuclear_level) : calcMilitaryMightLive(myCountry.mil_ground, myCountry.mil_naval, myCountry.mil_tactical_air, myCountry.mil_air_defense, myCountry.mil_strategic_missiles, myCountry.nuclear_level)
                  const allMights = allLive.map(c => ({ id: c.id, ...calcMilitaryMightLive(c.live_ground, c.live_naval, c.live_air, c.live_ad, c.live_missiles, c.nuclear_level) }))
                  const compRank = <T,>(sorted: T[], myId: string, getId: (t:T)=>string, getScore: (t:T)=>number): number => { const me = sorted.find(s => getId(s) === myId); if (!me) return 0; const myVal = getScore(me); return sorted.filter(s => getScore(s) > myVal).length + 1 }
                  const convRanked = [...allMights].sort((a, b) => b.conventional - a.conventional)
                  const convRank = compRank(convRanked, cc, m=>m.id, m=>m.conventional)
                  const gdpRanked = [...allLive].sort((a, b) => b.gdp - a.gdp)
                  const gdpRank = compRank(gdpRanked, cc, c=>c.id, c=>c.gdp)
                  const totalCountries = allLive.length || 20
                  const prev = rpcPrevSnapshots
                  const prevMe = prev.find(p => p.country_code === cc)
                  const hasPrev = prevMe != null
                  const prevMilMights = prev.map(p => ({ id: p.country_code, ...calcMilitaryMightLive(p.mil_ground??0, p.mil_naval??0, p.mil_tactical_air??0, p.mil_air_defense??0, p.mil_strategic_missiles??0, p.nuclear_level??0) }))
                  const prevConvRank = prevMilMights.length ? compRank([...prevMilMights].sort((a,b)=>b.conventional-a.conventional), cc, m=>m.id, m=>m.conventional) : 0
                  const prevGdpRank = prev.length ? compRank([...prev].sort((a,b)=>(b.gdp??0)-(a.gdp??0)), cc, p=>p.country_code, p=>p.gdp??0) : 0
                  const delta = (curr: number, p: number | undefined) => p != null ? curr - p : null
                  const deltaStab = delta(myCountry.stability, prevMe?.stability)
                  const deltaTreasury = delta(myCountry.treasury, prevMe?.treasury)
                  const deltaGdpRank = hasPrev && prevGdpRank > 0 ? prevGdpRank - gdpRank : null
                  const deltaMilRank = hasPrev && prevConvRank > 0 ? prevConvRank - convRank : null
                  // Nuclear rank (level × missiles, among nuclear countries only)
                  const nucLevel = myCountry.nuclear_level ?? 0
                  const nucScore = nucLevel * (myL?.live_missiles ?? 0)
                  const allNucScores = allLive.filter(c => (c.nuclear_level??0) > 0).map(c => ({id:c.id, score:(c.nuclear_level??0)*(c.live_missiles??0)}))
                  const nucRank = allNucScores.length ? compRank([...allNucScores].sort((a,b)=>b.score-a.score), cc, n=>n.id, n=>n.score) : 0
                  const nucTotal = allNucScores.length
                  const prevNucScores = prev.filter(p => (p.nuclear_level??0) > 0).map(p => ({id:p.country_code, score:(p.nuclear_level??0)*(p.mil_strategic_missiles??0)}))
                  const prevNucRank = prevNucScores.length ? compRank([...prevNucScores].sort((a,b)=>b.score-a.score), cc, n=>n.id, n=>n.score) : 0
                  const deltaNucRank = hasPrev && prevNucRank > 0 && nucRank > 0 ? prevNucRank - nucRank : null

                  // AI tech rank (by level, among countries with ai > 0)
                  const aiLevel = myCountry.ai_level ?? 0
                  const aiScore = (c: typeof allLive[0]) => (c.ai_level??0) + (Number(c.ai_rd_progress??0))
                  const aiRanked = [...allLive].filter(c => (c.ai_level??0) > 0).sort((a,b) => aiScore(b) - aiScore(a))
                  const aiRank = aiRanked.length ? compRank(aiRanked, cc, c=>c.id, aiScore) : 0
                  const aiTotal = aiRanked.length
                  const prevAiRanked = [...prev].filter(p => (p.ai_level??0) > 0).sort((a,b) => (b.ai_level??0) - (a.ai_level??0))
                  const prevAiRank = prevAiRanked.length ? compRank(prevAiRanked, cc, p=>p.country_code, p=>p.ai_level??0) : 0
                  const deltaAiRank = hasPrev && prevAiRank > 0 && aiRank > 0 ? prevAiRank - aiRank : null

                  const wars = myRelationships.filter(r => r.relationship === 'at_war').map(r => r.to_country_code)
                  const stab = myCountry.stability
                  const threshold = 4.0
                  const stabLabel = stab >= 7 ? 'Stable' : stab > threshold ? 'Caution' : 'Critical'
                  const stabColor = stab >= 7 ? 'text-success' : stab > threshold ? 'text-warning' : 'text-danger'
                  const stabBg = stab >= 7 ? 'bg-success' : stab > threshold ? 'bg-warning' : 'bg-danger'
                  const Arrow = ({d}: {d: number|null}) => { if (d == null || Math.abs(d) < 0.01) return null; const good = d > 0; return <span className={`text-[9px] font-semibold ${good ? 'text-success' : 'text-danger'}`}>{d > 0 ? '▲' : '▼'}{Math.abs(d) < 10 ? Math.abs(d).toFixed(1) : Math.abs(d).toFixed(0)}</span> }
                  const RankArrow = ({d}: {d: number|null}) => { if (d == null || d === 0) return null; return <span className={`text-[9px] font-semibold ${d > 0 ? 'text-success' : 'text-danger'}`}>{d > 0 ? '▲' : '▼'}{Math.abs(d)}</span> }
                  const cell = "px-2 py-1 flex flex-col items-center gap-0 min-w-0"
                  return (
                    <div className="flex items-center">
                      <div className="flex flex-1 items-stretch">
                        <div className={cell}>
                          <span className="font-body text-[8px] font-bold uppercase tracking-wider text-text-secondary">Stab</span>
                          <span className="font-data text-[13px] font-semibold flex items-center gap-0.5">{stab.toFixed(1)} <Arrow d={deltaStab}/></span>
                          <div className="w-full h-0.5 bg-base rounded-full relative" style={{minWidth:40}}>
                            <div className={`h-full rounded-full ${stabBg}`} style={{ width: `${Math.min(100, stab * 10)}%` }} />
                            <div className="absolute top-[-1px] w-[1.5px] h-[3px] bg-danger rounded-sm" style={{ left: `${threshold * 10}%` }} />
                          </div>
                        </div>
                        <div className={cell}>
                          <span className="font-body text-[8px] font-bold uppercase tracking-wider text-text-secondary">Treasury</span>
                          <span className={`font-data text-[13px] font-semibold flex items-center gap-0.5 ${myCountry.treasury <= 3 ? 'text-danger' : ''}`}>ç{myCountry.treasury.toFixed(0)} <Arrow d={deltaTreasury}/></span>
                        </div>
                        <div className={cell}>
                          <span className="font-body text-[8px] font-bold uppercase tracking-wider text-text-secondary">GDP</span>
                          <span className="font-data text-[13px] font-semibold flex items-center gap-0.5">#{gdpRank} <RankArrow d={deltaGdpRank}/></span>
                        </div>
                        <div className={cell}>
                          <span className="font-body text-[8px] font-bold uppercase tracking-wider text-text-secondary">Military</span>
                          <span className="font-data text-[13px] font-semibold flex items-center gap-0.5">#{convRank} <RankArrow d={deltaMilRank}/></span>
                        </div>
                        {nucLevel > 0 && <div className={cell}>
                          <span className="font-body text-[8px] font-bold uppercase tracking-wider text-text-secondary">Nuclear</span>
                          <span className="font-data text-[13px] font-semibold flex items-center gap-0.5">#{nucRank} <RankArrow d={deltaNucRank}/></span>
                          <span className="font-body text-[8px] text-text-secondary">T{nucLevel}·{nucTotal}</span>
                        </div>}
                        {aiLevel > 0 && <div className={cell}>
                          <span className="font-body text-[8px] font-bold uppercase tracking-wider text-text-secondary">AI Tech</span>
                          <span className="font-data text-[13px] font-semibold flex items-center gap-0.5">#{aiRank} <RankArrow d={deltaAiRank}/></span>
                          <span className="font-body text-[8px] text-text-secondary">L{aiLevel}·{aiTotal}</span>
                        </div>}
                        {wars.length > 0 && <div className={cell}>
                          <span className="font-body text-[8px] font-bold uppercase tracking-wider text-text-secondary">War</span>
                          {wars.slice(0,2).map(w => <span key={w} className="font-body text-[8px] font-bold text-danger">⚔{w.toUpperCase()}</span>)}
                        </div>}
                      </div>
                      <button onClick={() => setKpiBarHidden(true)} className="text-text-secondary/40 hover:text-text-secondary px-1 text-[10px]">▲</button>
                    </div>
                  )
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TABS */}
      <nav className="bg-card border-b border-border px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex">
            {tabs.map(t=>(
              <button key={t.id} onClick={()=>{if(!t.off){setTab(t.id);setActiveAction(null)}}} disabled={t.off}
                className={`relative px-5 py-3 font-body text-body-sm font-medium transition-colors ${tab===t.id?'text-text-primary':t.off?'text-text-secondary/30 cursor-not-allowed':'text-text-secondary hover:text-text-primary'}`}>
                {t.label}
                {t.badge?<span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-danger text-white text-[10px] font-bold rounded-full flex items-center justify-center">{t.badge}</span>:null}
                {tab===t.id&&<div className="absolute bottom-0 left-0 right-0 h-0.5" style={{backgroundColor:color}}/>}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { if (!navLoading && !navSession) setNavModeSelector(true) }}
              disabled={navLoading || !!navSession}
              className={`flex items-center gap-1.5 bg-action/10 text-action font-body text-caption font-medium px-3 py-1.5 rounded-full hover:bg-action/20 transition-colors disabled:opacity-40 ${navNudge && !navSession ? 'animate-pulse ring-1 ring-action/40' : ''}`}>
              <span className={`w-2 h-2 rounded-full ${navNudge ? 'bg-warning' : 'bg-action'}`}/>{navLoading ? 'Connecting...' : 'Navigator'}
            </button>
          </div>
        </div>
      </nav>

      {/* Navigator Mode Selector */}
      {navModeSelector && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-card border border-border rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">🧭</span>
              <h3 className="font-heading text-h3 text-text-primary">Navigator</h3>
            </div>
            <p className="font-body text-body-sm text-text-secondary mb-4">Choose how to communicate:</p>
            <div className="flex gap-3">
              <button onClick={async () => {
                setNavModeSelector(false); setNavMode('voice'); setNavLoading(true)
                try {
                  const { data: { session } } = await supabase.auth.getSession()
                  if (!session || !myRole) return
                  const resp = await fetch(`/api/sim/${simId}/navigator/start`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
                    body: JSON.stringify({ role_id: myRole.id }),
                  })
                  if (!resp.ok) { const j = await resp.json(); alert(j.detail || 'Failed'); return }
                  setNavSession((await resp.json()).data)
                } catch (e) { alert(e instanceof Error ? e.message : 'Failed') }
                finally { setNavLoading(false) }
              }} className="flex-1 bg-action/10 text-action font-body text-body-sm font-medium py-3 rounded-lg hover:bg-action/20 min-h-[48px]">
                🎙️ Voice
              </button>
              <button onClick={async () => {
                setNavModeSelector(false); setNavMode('text'); setNavLoading(true)
                try {
                  const { data: { session } } = await supabase.auth.getSession()
                  if (!session || !myRole) return
                  const resp = await fetch(`/api/sim/${simId}/navigator/start`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
                    body: JSON.stringify({ role_id: myRole.id }),
                  })
                  if (!resp.ok) { const j = await resp.json(); alert(j.detail || 'Failed'); return }
                  setNavSession((await resp.json()).data)
                } catch (e) { alert(e instanceof Error ? e.message : 'Failed') }
                finally { setNavLoading(false) }
              }} className="flex-1 bg-accent/10 text-accent font-body text-body-sm font-medium py-3 rounded-lg hover:bg-accent/20 min-h-[48px]">
                💬 Text
              </button>
            </div>
            <button onClick={() => setNavModeSelector(false)}
              className="w-full mt-3 font-body text-caption text-text-secondary hover:text-text-primary py-2">Cancel</button>
          </div>
        </div>
      )}

      {/* Navigator Voice Conversation */}
      {navSession && navMode === 'voice' && (
        <NavigatorVoiceConversation
          voiceAgentId={navSession.voice_agent_id}
          prompt={navSession.prompt}
          firstMessage={navSession.first_message}
          conversationType={navSession.conversation_type}
          onEnd={async (transcript, transcriptJson, durationSeconds) => {
            try {
              const { data: { session } } = await supabase.auth.getSession()
              if (session) {
                await fetch(`/api/sim/${simId}/navigator/end`, {
                  method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
                  body: JSON.stringify({ conversation_id: navSession.conversation_id, transcript, transcript_json: transcriptJson, duration_seconds: durationSeconds }),
                })
              }
            } catch { /* non-critical */ }
            setNavSession(null); setNavMode(null); setNavNudge(false)
          }}
          onClose={() => { setNavSession(null); setNavMode(null) }}
        />
      )}

      {/* Navigator Text Conversation */}
      {navSession && navMode === 'text' && (
        <NavigatorTextConversation
          simId={simId!}
          conversationId={navSession.conversation_id}
          systemPrompt={navSession.prompt}
          firstMessage={navSession.first_message}
          conversationType={navSession.conversation_type}
          onEnd={async (transcript, duration) => {
            try {
              const { data: { session } } = await supabase.auth.getSession()
              if (session) {
                await fetch(`/api/sim/${simId}/navigator/end`, {
                  method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
                  body: JSON.stringify({ conversation_id: navSession.conversation_id, transcript, transcript_json: [], duration_seconds: duration }),
                })
              }
            } catch { /* non-critical */ }
            setNavSession(null); setNavMode(null); setNavNudge(false)
          }}
        />
      )}

      {/* CONTENT */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-6">
        {/* Unassigned state — role not yet assigned by moderator */}
        {!myRole && (tab === 'actions' || tab === 'confidential' || tab === 'country') && !loading && (
          <div className="bg-card border border-border rounded-lg p-8 text-center">
            <h3 className="font-heading text-h3 text-text-primary mb-3">Awaiting Role Assignment</h3>
            <p className="font-body text-body-sm text-text-secondary mb-2">
              The moderator will assign you a role before the simulation starts.
            </p>
            <p className="font-body text-caption text-text-secondary">
              Once assigned, this tab will show your {tab === 'actions' ? 'available actions' : tab === 'confidential' ? 'confidential briefing and intelligence' : 'country data and statistics'}.
            </p>
            <p className="font-body text-caption text-action mt-4">
              This page updates automatically — no need to refresh.
            </p>
          </div>
        )}
        {tab==='actions'&&myRole&&(myRole.status==='arrested'||myRole.status==='killed')&&(
          <div className="bg-danger/5 border border-danger/20 rounded-lg p-6 text-center">
            <h3 className="font-heading text-h3 text-danger mb-2">{myRole.status === 'killed' ? (myRole.status_detail?.cause === 'health' ? 'Passed Away' : 'Eliminated') : 'Arrested'}</h3>
            <p className="font-body text-body-sm text-text-secondary">
              {myRole.status === 'killed'
                ? (myRole.status_detail?.cause === 'health'
                  ? 'Your character has passed away. No actions are available.'
                  : 'Your character has been eliminated. No actions are available.')
                : 'You cannot take any actions while under arrest. You will be released at the end of this round.'}
            </p>
          </div>
        )}
        {tab==='actions'&&myRole&&myRole.status!=='arrested'&&myRole.status!=='killed'&&(
          activeAction
            ? <ActionForm
                actionType={activeAction}
                roleId={myRole.id}
                roleName={myRole.character_name}
                countryId={myRole.country_code}
                simId={simId!}
                onClose={()=>setActiveAction(null)}
                onSubmitted={()=>{setActiveAction(null); loadData()}}
              />
            : <TabActions roleActions={effectiveRoleActions} rolePositions={myRole.positions ?? [myRole.position_type]} currentPhase={simState?.current_phase??'pre'} onSelectAction={setActiveAction}
                simId={simId!} countryId={myRole.country_code} roleId={myRole.id} dataVersion={dataVersion}
                nuclearActionsData={globalNuclearActions}
                parentOrgIds={new Set(myOrgMemberships.map(m=>m.org_id))}
                parentSimRun={simRun}
                parentActiveMeetings={activeMeetings}
                parentSetActiveMeetings={setActiveMeetings}
                parentAllRoleInfo={allRoleInfo}
                actionLogEvents={myEventsRaw}
                actionLogInvitations={mySentInvitationsRaw}
                actionLogTxns={myProposedTxnsRaw}
                actionLogAgrs={myProposedAgrsRaw}
                parentNotifications={parentNotifications}
                parentDismissNotification={parentDismissNotification}
                navNudge={navNudge}
                onStartNavigator={async () => {
                  if (navLoading || navSession || !myRole || !simId) return
                  setNavLoading(true)
                  try {
                    const { data: { session } } = await supabase.auth.getSession()
                    if (!session) return
                    const resp = await fetch(`/api/sim/${simId}/navigator/start`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
                      body: JSON.stringify({ role_id: myRole.id }),
                    })
                    if (!resp.ok) return
                    const json = await resp.json()
                    setNavSession(json.data)
                  } catch { /* */ }
                  finally { setNavLoading(false) }
                }}
                onOpenChat={(meetingId: string, counterpartRoleId?: string) => {
                  // If counterpart role ID provided, use it directly (no DB query needed)
                  const otherRoleId = counterpartRoleId
                  if (otherRoleId) {
                    const info = allRoleInfo[otherRoleId]
                    if (info?.isAi) {
                      setPendingModeSelect({
                        meetingId,
                        counterpart: { name: info.name, country: info.country, position: info.position, voiceAgentId: info.voiceAgentId },
                      })
                      return
                    }
                  }
                  // Human counterpart or unknown — straight to text chat
                  setActiveChatMeetingId(meetingId)
                }}/>
        )}
        {tab==='newsbrief'&&myRole&&myCountry&&<TabNewsbrief
          simId={simId!} countryId={myCountry.id} country={myCountry} round={round}
          publicEvents={publicEventsRaw} myEvents={myEventsRaw} notifications={notificationsAllRaw as unknown as NotificationData[]}
          prevSnapshots={rpcPrevSnapshots} deploymentCounts={rpcDeploymentCounts}
          relationships={myRelationships} sanctions={mySanctions} tariffs={myTariffs}
          fullCountry={fullCountry}
        />}
        {tab==='confidential'&&myRole&&<TabConf role={myRole} artefacts={artefacts} objectives={objectives} personalRels={personalRels} orgMemberships={myOrgMemberships}
          isR0={isR0}
          onNameChange={() => { setOriginalName(myRole.character_name); setNewName(myRole.character_name); setNameError(null); setShowNameModal(true) }}
          onRead={id=>{
          supabase.from('artefacts').update({is_read:true}).eq('id',id).then(()=>{
            setArtefacts(p=>p.map(a=>a.id===id?{...a,is_read:true}:a))
          })
        }}/>}
        {tab==='country'&&myCountry&&<TabCountry country={myCountry} fullCountry={fullCountry} sanctions={mySanctions} tariffs={myTariffs} relationships={myRelationships} orgMemberships={myOrgMemberships} simId={simId!} currentRound={round} rpcAllCountries={rpcAllCountries} rpcPrevSnapshots={rpcPrevSnapshots} rpcDeploymentCounts={rpcDeploymentCounts}/>}
        {tab==='world'&&<TabWorld simId={simId!} round={round}/>}
      </main>

      {/* Mode Selection Popup — Human-AI meetings only */}
      {pendingModeSelect && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-card border border-border rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <div className="text-center mb-6">
              <div className="font-body text-caption text-text-secondary mb-2">Connecting you to</div>
              <div className="flex items-center gap-3 justify-center">
                <CountryEmblem countryId={pendingModeSelect.counterpart.country} color="#6B7280" size={28} />
                <span className="font-heading text-h2 text-text-primary">{pendingModeSelect.counterpart.name}</span>
              </div>
              <div className="font-body text-body-sm text-text-secondary mt-1">
                <span className="uppercase tracking-wider">{pendingModeSelect.counterpart.country}</span>
                {pendingModeSelect.counterpart.position && (
                  <span className="ml-2 text-accent font-medium">{pendingModeSelect.counterpart.position}</span>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={(e) => { e.stopPropagation(); handleModeSelect('text') }}
                type="button"
                className="flex-1 py-3 px-4 bg-base hover:bg-action/10 text-text-primary border border-border hover:border-action/50 rounded-lg font-body text-body font-medium transition-colors cursor-pointer"
              >
                Text Chat
              </button>
              {pendingModeSelect.counterpart.voiceAgentId && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleModeSelect('voice') }}
                  type="button"
                  className="flex-1 py-3 px-4 bg-action hover:bg-action/80 text-white rounded-lg font-body text-body font-medium transition-colors cursor-pointer"
                >
                  Voice Call
                </button>
              )}
            </div>
            <button
              onClick={() => setPendingModeSelect(null)}
              className="w-full mt-3 py-2 text-text-secondary hover:text-text-primary font-body text-caption transition-colors"
            >
              Not now
            </button>
          </div>
        </div>
      )}

      {/* Meeting Chat overlay (text mode) */}
      {activeChatMeetingId && myRole && (
        <MeetingChat
          meetingId={activeChatMeetingId}
          simId={simId!}
          myRoleId={myRole.id}
          myCountryCode={myRole.country_code}
          myCharacterName={myRole.character_name}
          onClose={() => { setActiveChatMeetingId(null); setDataVersion(v => v + 1) }}
        />
      )}

      {/* Voice Call overlay — loading state while context is fetched */}
      {activeVoiceMeetingId && myRole && !voiceContext && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/90">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-3" />
            <p className="text-gray-600">Preparing voice connection...</p>
          </div>
        </div>
      )}

      {/* Voice Call overlay (voice mode) */}
      {activeVoiceMeetingId && myRole && voiceContext && (
        <VoiceCallInterface
          meetingId={activeVoiceMeetingId}
          simId={simId!}
          voiceAgentId={voiceContext.voiceAgentId}
          avatarIdentity={voiceContext.identity}
          intentNote={voiceContext.intentNote}
          conversationHistory=""
          counterpartName={voiceContext.counterpartName}
          counterpartCountry={voiceContext.counterpartCountry}
          myRoleId={myRole.id}
          myCountryCode={myRole.country_code}
          onEnd={() => {
            setActiveVoiceMeetingId(null)
            setVoiceContext(null)
            setDataVersion(v => v + 1)
          }}
          onFallbackToText={() => {
            // Switch from voice to text chat (M5.7 SPEC 6.3 — voice fallback)
            const mid = activeVoiceMeetingId
            setActiveVoiceMeetingId(null)
            setVoiceContext(null)
            if (mid) {
              supabase.from('meetings').update({ modality: 'text' }).eq('id', mid).then(() => {}).catch(() => {})
              setActiveChatMeetingId(mid)
            }
          }}
        />
      )}

      {/* Name Change Modal (R0 only) */}
      {showNameModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-xl shadow-2xl max-w-md w-full p-6">
            <h2 className="font-heading text-h2 text-text-primary mb-4">Change Your Character Name</h2>
            <div className="mb-3">
              <div className="font-body text-caption text-text-secondary mb-1">Original Name</div>
              <div className="px-4 py-2.5 bg-base rounded-lg font-body text-body-sm text-text-secondary">{originalName}</div>
            </div>
            <div className="mb-3">
              <div className="font-body text-caption text-text-secondary mb-1">New Name</div>
              <input type="text" value={newName} autoFocus
                onChange={e => { setNewName(e.target.value); setNameError(null) }}
                className="w-full px-4 py-3 border border-border rounded-lg font-body text-body-sm text-text-primary bg-base focus:outline-none focus:border-action"
                placeholder="Enter your new character name" />
            </div>
            {nameError && (
              <div className="mb-3 p-2.5 bg-danger/10 border border-danger/20 rounded-lg">
                <p className="font-body text-caption text-danger">{nameError}</p>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => setShowNameModal(false)} disabled={nameSaving}
                className="flex-1 px-4 py-2.5 border border-border rounded-lg font-body text-body-sm text-text-secondary hover:bg-base transition-colors">
                Cancel
              </button>
              <button onClick={handleSaveName} disabled={nameSaving}
                className="flex-1 px-4 py-2.5 bg-action text-white rounded-lg font-body text-body-sm font-medium disabled:opacity-50 hover:bg-action/90 transition-colors">
                {nameSaving ? 'Saving...' : 'Save Name'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ── Tab: Actions ──────────────────────────────────────────────────────── */

function TabActions({roleActions, rolePositions, currentPhase, onSelectAction, simId, countryId, roleId, dataVersion, nuclearActionsData, parentOrgIds, parentSimRun, parentActiveMeetings, parentSetActiveMeetings, parentAllRoleInfo, onOpenChat, actionLogEvents, actionLogInvitations, actionLogTxns, actionLogAgrs, parentNotifications, parentDismissNotification, navNudge, onStartNavigator}:{
  roleActions:string[]; rolePositions:string[]; currentPhase:string; onSelectAction:(id:string)=>void
  simId:string; countryId:string; roleId:string; dataVersion?:number
  nuclearActionsData?:Record<string, unknown>[]
  parentOrgIds?:Set<string>
  parentSimRun?:SimRun|null
  parentActiveMeetings?:MeetingData[]
  parentSetActiveMeetings?:(meetings:MeetingData[])=>void
  parentAllRoleInfo?:Record<string, {name:string;country:string;position:string;voiceAgentId:string|null;isAi:boolean}>
  onOpenChat?:(meetingId:string, counterpartRoleId?:string)=>void
  actionLogEvents?:Record<string, unknown>[]
  actionLogInvitations?:Record<string, unknown>[]
  actionLogTxns?:Record<string, unknown>[]
  actionLogAgrs?:Record<string, unknown>[]
  parentNotifications:NotificationData[]
  parentDismissNotification:(id:string)=>void
  navNudge?:boolean
  onStartNavigator?:()=>void
}) {
  const avail = new Set(roleActions)
  const [reviewTxn, setReviewTxn] = useState<string|null>(null)
  const [reviewAgr, setReviewAgr] = useState<string|null>(null)
  const [signingAgr, setSigningAgr] = useState<string|null>(null)
  const [counterOfferPrefill, setCounterOfferPrefill] = useState<{counterpart:string;offer:Record<string,unknown>;request:Record<string,unknown>;comment?:string}|null>(null)
  const [budgetReviewPayload, setBudgetReviewPayload] = useState<Record<string,unknown>|null>(null)
  const [budgetReviewNotifId, setBudgetReviewNotifId] = useState<string|null>(null)
  const [navDismissed, setNavDismissed] = useState(() => localStorage.getItem(`nav_dismissed_${simId}`) === '1')
  const dismissNav = () => { setNavDismissed(true); localStorage.setItem(`nav_dismissed_${simId}`, '1') }

  // Use org IDs from parent (already loaded in loadData) — no duplicate fetch
  const myOrgIds = parentOrgIds ?? new Set<string>()

  // Role name + position lookup — derived from parent's allRoleInfo (loaded via RPC)
  const roleInfo = useMemo(() => {
    if (!parentAllRoleInfo) return {} as Record<string, {name:string;country:string;position:string}>
    const map: Record<string, {name:string;country:string;position:string}> = {}
    for (const [id, info] of Object.entries(parentAllRoleInfo)) {
      // Shorten position labels for TabActions display
      const pos = info.position === 'Head of State' ? 'HoS'
        : info.position === 'Military Chief' ? 'Military'
        : info.position === 'Economy Officer' ? 'Economy'
        : info.position
      map[id] = { name: info.name, country: info.country, position: pos }
    }
    return map
  }, [parentAllRoleInfo])

  /* Realtime: meeting invitations for this role */
  const { data: meetingInvitationsRaw } = useRealtimeTable<Record<string, unknown>>(
    'meeting_invitations', simId,
    { filter: 'status=eq.pending' },
  )
  // Filter: 1:1 where I'm invitee, or org where I'm a member
  const myMeetingInvitations = (meetingInvitationsRaw as unknown as {
    id:string; invitation_type:string; inviter_role_id:string; inviter_country_code:string;
    invitee_role_id:string|null; org_id:string|null; org_name:string|null;
    message:string; theme:string|null; expires_at:string; responses:Record<string,unknown>;
  }[]).filter(inv => {
    if (inv.inviter_role_id === roleId) return false
    if (inv.invitation_type === 'one_on_one') return inv.invitee_role_id === roleId
    // Org invitations: only if I'm a member of that org
    return inv.org_id ? myOrgIds.has(inv.org_id) : false
  }).filter(inv => {
    // Check not expired
    return new Date(inv.expires_at).getTime() > Date.now()
  }).filter(inv => {
    // Check not already responded
    return !(inv.responses && (inv.responses as Record<string,unknown>)[roleId])
  })

  /* Realtime: participant notification cards (mailbox) — passed from parent */
  const notifications = parentNotifications
  const handleDismissNotification = parentDismissNotification

  const [respondingTo, setRespondingTo] = useState<string|null>(null)
  const [meetingResponse, setMeetingResponse] = useState<string>('')
  const [meetingMessage, setMeetingMessage] = useState('')
  const [meetingSubmitting, setMeetingSubmitting] = useState(false)

  const handleMeetingResponse = async (invId: string, response: string, msg: string, inviterRoleId?: string) => {
    setMeetingSubmitting(true)
    try {
      const result = await submitAction(simId, 'respond_meeting', roleId, countryId, {
        invitation_id: invId, response, message: msg,
      })
      setRespondingTo(null)
      setMeetingMessage('')
      // Auto-open chat when accepting a meeting
      if (response === 'accept' && onOpenChat) {
        const meetingId = result?.meeting_id as string
        if (meetingId) {
          onOpenChat(meetingId, inviterRoleId)
        } else {
          const { data: inv } = await supabase.from('meeting_invitations')
            .select('meeting_id').eq('id', invId).limit(1)
          if (inv?.[0]?.meeting_id) {
            onOpenChat(inv[0].meeting_id as string, inviterRoleId)
          }
        }
        // Refresh active meetings list
        getActiveMeetings(simId, roleId).then(setActiveMeetings).catch(() => {})
      }
    } catch (err) { console.error('[meeting] respond failed:', err) }
    finally { setMeetingSubmitting(false) }
  }

  // Active meetings — from parent (loaded via RPC), with setter for local updates
  const activeMeetings = parentActiveMeetings ?? []
  const setActiveMeetings = parentSetActiveMeetings ?? (() => {})

  /* Realtime hooks replace 5s polling for pending transactions + agreements */
  // Only diplomat can see and respond to transactions (WORLD_MODEL Section 5)
  const canTrade = rolePositions.includes('diplomat')
  const txnOpts = useMemo(() => ({
    columns: 'id,proposer,counterpart,offer,request,terms,created_at',
    filter: `status=eq.pending`,
    eq: { counterpart: countryId },
    enabled: canTrade,
  }), [countryId, canTrade])
  const { data: pendingTxnsRaw } = useRealtimeTable<Record<string, unknown>>(
    'exchange_transactions', simId, txnOpts,
  )
  // Also fetch countered transactions where we are the original proposer
  const counteredOpts = useMemo(() => ({
    columns: 'id,proposer,counterpart,offer,request,terms,created_at',
    filter: `status=eq.countered`,
    eq: { proposer: countryId },
    enabled: canTrade,
  }), [countryId, canTrade])
  const { data: counteredTxnsRaw } = useRealtimeTable<Record<string, unknown>>(
    'exchange_transactions', simId, counteredOpts,
  )
  const pendingTxns = [
    ...(pendingTxnsRaw || []),
    ...(counteredTxnsRaw || []),
  ] as unknown as {id:string;proposer:string;offer:Record<string,unknown>;request:Record<string,unknown>;terms:string;created_at:string}[]

  // Only HoS + diplomat can see and sign agreements (WORLD_MODEL Section 5)
  const canSignAgreements = rolePositions.some(p => ['head_of_state', 'diplomat'].includes(p))
  const agrOpts = useMemo(() => ({
    columns: 'id,agreement_name,agreement_type,proposer_country_code,signatories,terms,signatures',
    filter: 'status=eq.proposed',
    enabled: canSignAgreements,
  }), [canSignAgreements])
  const { data: allProposedAgr } = useRealtimeTable<Record<string, unknown>>(
    'agreements', simId, agrOpts,
  )
  // Client-side filter: only agreements where this country is a signatory and hasn't signed yet
  const pendingAgreements = (allProposedAgr as unknown as {id:string;agreement_name:string;agreement_type:string;proposer_country_code:string;signatories:string[];terms:string;signatures:Record<string,unknown>}[])
    .filter(a => {
      if (!Array.isArray(a.signatories) || !a.signatories.includes(countryId)) return false
      const sigs = (a.signatures as Record<string,{confirmed?:boolean}>) || {}
      return !sigs[countryId]?.confirmed
    })

  // Leadership votes — realtime subscription for change_leader voting
  const voteOpts = useMemo(() => ({ eq: { country_code: countryId }, columns: '*' }), [countryId])
  const { data: leadershipVotesRaw } = useRealtimeTable<Record<string, unknown>>(
    'leadership_votes', simId, voteOpts,
  )
  const activeVote = leadershipVotesRaw.find(v => v.status === 'voting') as {
    id:string; phase:string; status:string; country_code:string; initiated_by:string;
    target_role:string; votes:Record<string,string>; required_majority:number;
    expires_at:string|null;
  } | undefined
  const hasVotedInActiveVote = activeVote ? !!(activeVote.votes && activeVote.votes[roleId]) : false

  // Leadership vote countdown
  const [leaderVoteCountdown, setLeaderVoteCountdown] = useState<number|null>(null)
  useEffect(() => {
    if (!activeVote?.expires_at) { setLeaderVoteCountdown(null); return }
    const tick = () => {
      const rem = Math.max(0, (new Date(activeVote.expires_at!).getTime() - Date.now()) / 1000)
      setLeaderVoteCountdown(rem)
    }
    tick()
    const iv = setInterval(tick, 1000)
    return () => clearInterval(iv)
  }, [activeVote?.id, activeVote?.expires_at])

  // Leadership vote state

  const [votingOnLeader, setVotingOnLeader] = useState<string|null>(null)
  const [leaderVoteSubmitting, setLeaderVoteSubmitting] = useState(false)
  const [selectedCandidate, setSelectedCandidate] = useState<string>('')
  const [countryRoles, setCountryRoles] = useState<{id:string;character_name:string;position_type:string}[]>([])
  useEffect(() => {
    supabase.from('roles').select('id,character_name,positions')
      .eq('sim_run_id', simId).eq('country_code', countryId).eq('status', 'active')
      .then(({ data }) => { if (data) setCountryRoles(data) })
  }, [simId, countryId, dataVersion])

  const handleLeaderVote = async (voteId: string, vote: string) => {
    setLeaderVoteSubmitting(true)
    try {
      const token = await getToken()
      const API_BASE = import.meta.env.VITE_API_URL ?? ''
      await fetch(`${API_BASE}/api/sim/${simId}/leadership-votes/${voteId}/cast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ role_id: roleId, vote }),
      })
      setVotingOnLeader(null)
    } catch (e) { console.error(e) }
    finally { setLeaderVoteSubmitting(false) }
  }

  // Nuclear authorization/interception — passed from parent (no duplicate subscription)
  const nuclearActions = (nuclearActionsData ?? []) as unknown as {
    id:string; action_type:string; country_code:string; status:string;
    authorizer_1_role:string|null; authorizer_2_role:string|null;
    authorizer_1_response:string|null; authorizer_2_response:string|null;
    payload:Record<string,unknown>; timer_started_at:string|null;
    timer_duration_sec:number|null; interception_responses:Record<string,unknown>|null;
  }[]

  // Authorization cards: pending nuclear actions where this role is an authorizer and hasn't responded
  const [authRoleForCountry, setAuthRoleForCountry] = useState<string|null>(null)
  const [authorizingId, setAuthorizingId] = useState<string|null>(null)
  const [authSubmitting, setAuthSubmitting] = useState(false)
  const [interceptingId, setInterceptingId] = useState<string|null>(null)
  const [interceptSubmitting, setInterceptSubmitting] = useState(false)

  // Determine what role name this role maps to for authorization
  useEffect(() => {
    // Load the role's position_type to check against authorizer roles
    supabase.from('roles').select('id, positions')
      .eq('sim_run_id', simId).eq('id', roleId).limit(1)
      .then(({ data }) => {
        if (data?.[0]) {
          // The authorizer roles in nuclear_actions are character role IDs like 'shield', 'rampart' etc.
          // For the human interface, we match by role ID directly
          setAuthRoleForCountry(data[0].id)
        }
      })
  }, [simId, roleId])

  const pendingAuthorizations = nuclearActions.filter(a =>
    a.status === 'awaiting_authorization' &&
    a.country_code === countryId &&
    ((a.authorizer_1_role === roleId && !a.authorizer_1_response) ||
     (a.authorizer_2_role === roleId && !a.authorizer_2_response))
  )

  // Interception cards: T3+ countries with pending interception
  const pendingInterceptions = nuclearActions.filter(a =>
    a.status === 'awaiting_interception' &&
    a.country_code !== countryId && // not the launcher
    !(a.interception_responses && (a.interception_responses as Record<string,unknown>)[countryId]) // haven't responded
  )
  // Only show if our country is T3+ — check nuclearLevel from parent state via simple query
  const [myNuclearLevel, setMyNuclearLevel] = useState(0)
  useEffect(() => {
    supabase.from('countries').select('nuclear_level, nuclear_confirmed')
      .eq('sim_run_id', simId).eq('id', countryId).limit(1)
      .then(({ data }) => {
        if (data?.[0]?.nuclear_confirmed && (data[0].nuclear_level ?? 0) >= 2) {
          setMyNuclearLevel(data[0].nuclear_level)
        }
      })
  }, [simId, countryId, dataVersion])

  const visibleInterceptions = myNuclearLevel >= 2 ? pendingInterceptions : []

  // ── Columbia Elections ──────────────────────────────────────────────
  const isColumbia = countryId === 'columbia'

  // Realtime: nominations for this sim
  const { data: electionNominationsRaw } = useRealtimeTable<Record<string, unknown>>(
    'election_nominations', simId,
    { columns: '*', enabled: isColumbia },
  )
  const electionNominations = electionNominationsRaw as unknown as {
    id:string; election_type:string; election_round:number; role_id:string; camp:string
  }[]

  // Realtime: votes for this sim (just to check if I voted)
  const { data: electionVotesRaw } = useRealtimeTable<Record<string, unknown>>(
    'election_votes', simId,
    { columns: 'id,election_type,voter_role_id', enabled: isColumbia },
  )
  const electionVotes = electionVotesRaw as unknown as {
    id:string; election_type:string; voter_role_id:string
  }[]

  // Realtime: results
  const { data: electionResultsRaw } = useRealtimeTable<Record<string, unknown>>(
    'election_results', simId,
    { columns: '*', enabled: isColumbia },
  )
  const electionResults = electionResultsRaw as unknown as {
    id:string; election_type:string; election_round:number; winner_role_id:string|null
  }[]

  // Derive key_events and current_round from parent's realtime simRun (no duplicate queries)
  const keyEventsData = useMemo(() => {
    const ke = parentSimRun?.key_events as {type:string;subtype?:string;round?:number;nominations_round?:number;name?:string}[] | undefined
    if (!ke) return []
    return ke.filter(e => e.type === 'election').map(e => ({
      type: e.type, subtype: e.subtype ?? '', round: e.round ?? 0, name: e.name ?? '',
    }))
  }, [parentSimRun?.key_events])

  const currentRound = parentSimRun?.current_round ?? 0

  // Find active election events for this round
  // nominations_round is auto-computed as round - 1 (not stored in data)
  const activeNominationEvent = isColumbia ? keyEventsData.find(e => (e.round - 1) === currentRound) : undefined
  const activeElectionEvent = isColumbia ? keyEventsData.find(e => e.round === currentRound) : undefined

  // Check if already nominated
  const myNomination = activeNominationEvent
    ? electionNominations.find(n => n.role_id === roleId && n.election_type === activeNominationEvent.subtype)
    : undefined

  // Check if already voted
  const myElectionVote = activeElectionEvent
    ? electionVotes.find(v => v.voter_role_id === roleId && v.election_type === activeElectionEvent.subtype)
    : undefined

  // Check if election already resolved
  const electionResolved = activeElectionEvent
    ? electionResults.find(r => r.election_type === activeElectionEvent.subtype && r.election_round === activeElectionEvent.round)
    : undefined

  // Candidates for the active election
  const electionCandidates = activeElectionEvent
    ? electionNominations.filter(n => n.election_type === activeElectionEvent.subtype && n.election_round === activeElectionEvent.round)
    : []

  // Election UI state
  const [showElectionPanel, setShowElectionPanel] = useState<'nominate'|'vote'|'results'|null>(null)
  const [electionSubmitting, setElectionSubmitting] = useState(false)
  const [selectedElectionCandidate, setSelectedElectionCandidate] = useState<string>('')

  // Check if nominations are open/closed (moderator controls)
  // Derive nomination state from parent's realtime simRun (proper columns, not JSONB)
  const autoApprove = !!parentSimRun?.auto_approve
  const nominationsClosed = activeNominationEvent ? parentSimRun?.nominations_closed === true : false
  const nominationsOpen = activeNominationEvent ? (!nominationsClosed && (parentSimRun?.nominations_open === true || autoApprove)) : false

  // Check if this role is a sitting parliament member (cannot nominate for midterms unless seat is for reelection)
  const [isParliamentMember, setIsParliamentMember] = useState(false)
  const [isSeatForReelection, setIsSeatForReelection] = useState(false)
  useEffect(() => {
    if (!simId || !isColumbia || !roleId) return
    supabase.from('org_memberships').select('role_id,role_in_org')
      .eq('sim_run_id', simId).eq('org_id', 'columbia_parliament').eq('country_code', countryId)
      .then(({ data }) => {
        // Filter to THIS role's membership only
        const myMemberships = (data || []).filter((m: Record<string,unknown>) => m.role_id === roleId)
        setIsParliamentMember(myMemberships.length > 0)
        setIsSeatForReelection(myMemberships.some((m: Record<string,unknown>) => m.role_in_org === 'member_reelection'))
      })
  }, [simId, isColumbia, countryId, roleId])

  const canNominateForMidterm = !isParliamentMember || isSeatForReelection

  // Local nomination state for optimistic updates (realtime may be slow)
  const [localNominated, setLocalNominated] = useState<boolean|null>(null)

  // Show election in expected actions
  // Combine DB nomination with local optimistic state
  const isNominated = localNominated === true || (localNominated === null && !!myNomination)
  const showNomination = isColumbia && activeNominationEvent && !isNominated && nominationsOpen
    && (activeNominationEvent.subtype !== 'parliamentary_midterm' || canNominateForMidterm)
  // Derive election open state from parent's realtime simRun (proper column)
  const electionOpen = activeElectionEvent ? (parentSimRun?.election_open === true || autoApprove) : false

  const showElectionVote = isColumbia && activeElectionEvent && !myElectionVote && !electionResolved && electionCandidates.length > 0 && electionOpen

  // ── Early returns for review screens (AFTER all hooks) ──────────────
  // If reviewing a transaction, show the review screen
  const txnToReview = reviewTxn ? pendingTxns.find(t=>t.id===reviewTxn) : null
  if (txnToReview) {
    return <TransactionReview txn={txnToReview} simId={simId} countryId={countryId} roleId={roleId}
      onClose={()=>setReviewTxn(null)}
      onDone={()=>{setReviewTxn(null)}}
      onCounterOffer={(prefill)=>{setReviewTxn(null); setCounterOfferPrefill(prefill)}} />
  }

  // Counter-offer: show propose transaction form with prefilled data
  if (counterOfferPrefill) {
    return <ProposeTransactionForm simId={simId} countryId={countryId} roleId={roleId}
      onClose={()=>setCounterOfferPrefill(null)}
      onSubmitted={()=>setCounterOfferPrefill(null)}
      prefill={counterOfferPrefill} />
  }

  // Agreement signing handler
  const handleSignAgreement = async (agrId:string, confirm:boolean) => {
    setSigningAgr(agrId)
    try {
      await submitAction(simId,'sign_agreement',roleId,countryId,{
        agreement_id:agrId, confirm, comments:'',
      })
    } catch(e) { alert(e instanceof Error?e.message:'Failed') }
    finally { setSigningAgr(null) }
  }

  // If reviewing an agreement, show the review screen
  const agrToReview = reviewAgr ? pendingAgreements.find(a=>a.id===reviewAgr) : null
  if (agrToReview) {
    const typeLabel = AGREEMENT_TYPES.find(t=>t.value===agrToReview.agreement_type)?.label ?? agrToReview.agreement_type
    const relationLabel = AGREEMENT_TYPES.find(t=>t.value===agrToReview.agreement_type)?.relation ?? ''
    const sigs = agrToReview.signatures as Record<string,{confirmed?:boolean;role_id?:string;signed_at?:string}>
    const signedCountries = Object.entries(sigs).filter(([,s])=>s?.confirmed).map(([cc])=>cc)

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-h2 text-text-primary">Agreement Proposal</h2>
          <button onClick={()=>setReviewAgr(null)} className="font-body text-caption text-text-secondary hover:text-text-primary px-3 py-1 rounded border border-border">← Back</button>
        </div>

        <div className="bg-card border-2 border-action/20 rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-3">
            <span className="font-heading text-h3 text-text-primary">{agrToReview.agreement_name}</span>
            <span className="font-body text-caption bg-action/10 text-action px-2 py-0.5 rounded capitalize">{typeLabel}</span>
          </div>

          <div className="font-body text-caption text-text-secondary">
            Proposed by <strong className="text-text-primary">{agrToReview.proposer_country_code}</strong> ·
            Sets relationship to <strong className="text-text-primary">{relationLabel}</strong>
          </div>

          <div className="font-body text-caption text-text-secondary">
            Signatories: {(agrToReview.signatories||[]).map(s=>
              <span key={s} className={`inline-block mr-2 ${signedCountries.includes(s)?'text-success':'text-text-primary'}`}>
                {s}{signedCountries.includes(s)?' ✓':''}
              </span>
            )}
          </div>

          {agrToReview.terms&&<div className="bg-base border border-border rounded-lg p-4">
            <h3 className="font-heading text-caption text-text-secondary uppercase tracking-wider mb-2">Terms</h3>
            <p className="font-body text-body-sm text-text-primary leading-relaxed whitespace-pre-wrap">{agrToReview.terms}</p>
          </div>}
        </div>

        <div className="flex items-center gap-3">
          <button onClick={async()=>{
            setSigningAgr(agrToReview.id)
            await handleSignAgreement(agrToReview.id,true)
            setReviewAgr(null)
          }} disabled={signingAgr===agrToReview.id}
            className="bg-success text-white font-body text-body-sm font-medium px-6 py-2.5 rounded-lg hover:bg-success/90 disabled:opacity-50 transition-colors">
            {signingAgr===agrToReview.id?'Signing...':'Sign Agreement'}</button>
          <button onClick={async()=>{
            setSigningAgr(agrToReview.id)
            await handleSignAgreement(agrToReview.id,false)
            setReviewAgr(null)
          }} disabled={signingAgr===agrToReview.id}
            className="bg-danger/10 text-danger font-body text-body-sm font-medium px-6 py-2.5 rounded-lg hover:bg-danger/20 transition-colors">
            Decline</button>
          <button onClick={()=>setReviewAgr(null)} className="font-body text-body-sm text-text-secondary hover:text-text-primary px-4 py-2.5">Cancel</button>
        </div>
      </div>
    )
  }

  const handleSelfNominate = async () => {
    if (!activeNominationEvent) return
    setElectionSubmitting(true)
    try {
      const res = await submitAction(simId, 'self_nominate', roleId, countryId, {
        election_type: activeNominationEvent.subtype,
        election_round: activeNominationEvent.round,
      })
      if (res.success) {
        setLocalNominated(true)
      } else {
        alert(res.narrative || 'Nomination failed')
      }
    } catch (e) { alert(e instanceof Error ? e.message : 'Nomination failed') }
    finally { setElectionSubmitting(false) }
  }

  const handleWithdrawNomination = async () => {
    if (!activeNominationEvent) return
    setElectionSubmitting(true)
    try {
      const res = await submitAction(simId, 'withdraw_nomination', roleId, countryId, {
        election_type: activeNominationEvent.subtype,
      })
      if (res.success) {
        setLocalNominated(false)
      } else {
        alert(res.narrative || 'Withdrawal failed')
      }
    } catch (e) { alert(e instanceof Error ? e.message : 'Withdrawal failed') }
    finally { setElectionSubmitting(false) }
  }

  const handleCastElectionVote = async () => {
    if (!activeElectionEvent || !selectedElectionCandidate) return
    setElectionSubmitting(true)
    try {
      await submitAction(simId, 'cast_election_vote', roleId, countryId, {
        candidate_role_id: selectedElectionCandidate,
        election_type: activeElectionEvent.subtype,
      })
      setShowElectionPanel(null)
      setSelectedElectionCandidate('')
    } catch (e) { alert(e instanceof Error ? e.message : 'Vote failed') }
    finally { setElectionSubmitting(false) }
  }

  const handleAuthorize = async (actionId: string, confirm: boolean, rationale: string) => {
    setAuthSubmitting(true)
    try {
      await submitAction(simId, 'nuclear_authorize', roleId, countryId, {
        nuclear_action_id: actionId, confirm, rationale,
      })
      setAuthorizingId(null)
    } catch (e) { alert(e instanceof Error ? e.message : 'Failed') }
    finally { setAuthSubmitting(false) }
  }

  const handleIntercept = async (actionId: string, intercept: boolean, rationale: string) => {
    setInterceptSubmitting(true)
    try {
      await submitAction(simId, 'nuclear_intercept', roleId, countryId, {
        nuclear_action_id: actionId, intercept, rationale,
      })
      setInterceptingId(null)
    } catch (e) { alert(e instanceof Error ? e.message : 'Failed') }
    finally { setInterceptSubmitting(false) }
  }

  // Leadership vote detail panel
  if (votingOnLeader && activeVote) {
    const hosRole = countryRoles.find(r => r.id === activeVote.target_role)
    const hosName = hosRole?.character_name ?? activeVote.target_role
    const votesObj = activeVote.votes || {}
    const votesCast = Object.keys(votesObj).length
    const mm = leaderVoteCountdown !== null ? String(Math.floor(leaderVoteCountdown / 60)).padStart(2, '0') : '--'
    const ss = leaderVoteCountdown !== null ? String(Math.floor(leaderVoteCountdown % 60)).padStart(2, '0') : '--'

    if (activeVote.phase === 'removal') {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-h2 text-text-primary">Remove Head of State</h2>
            <button onClick={() => setVotingOnLeader(null)}
              className="font-body text-caption text-text-secondary hover:text-text-primary px-3 py-1 rounded border border-border">
              ← Back
            </button>
          </div>

          <div className="bg-danger/5 border border-danger/20 rounded-lg p-4">
            <p className="font-body text-body-sm text-text-primary">
              Vote to remove <strong className="text-danger">{hosName}</strong> as Head of State.
              Need <strong>{activeVote.required_majority}</strong> YES votes from non-HoS citizens.
            </p>
            <p className="font-body text-caption text-text-secondary mt-1">
              {votesCast} vote{votesCast !== 1 ? 's' : ''} cast
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="bg-base rounded-lg p-3">
              <div className="font-data text-data-lg text-text-primary">{mm}:{ss}</div>
              <div className="font-body text-caption text-text-secondary">Time remaining</div>
            </div>
            <div className="bg-base rounded-lg p-3">
              <div className="font-data text-data-lg text-danger">{activeVote.required_majority}</div>
              <div className="font-body text-caption text-text-secondary">YES votes needed</div>
            </div>
          </div>

          <div className="flex gap-3">
            <button onClick={() => handleLeaderVote(activeVote.id, 'yes')} disabled={leaderVoteSubmitting}
              className="flex-1 bg-danger/10 text-danger font-body text-body-sm font-medium py-2.5 rounded-lg hover:bg-danger/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              Yes — Remove
            </button>
            <button onClick={() => handleLeaderVote(activeVote.id, 'no')} disabled={leaderVoteSubmitting}
              className="flex-1 bg-success/10 text-success font-body text-body-sm font-medium py-2.5 rounded-lg hover:bg-success/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              No — Keep
            </button>
          </div>
        </div>
      )
    }

    // Election phase
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-h2 text-text-primary">Elect New Head of State</h2>
          <button onClick={() => setVotingOnLeader(null)}
            className="font-body text-caption text-text-secondary hover:text-text-primary px-3 py-1 rounded border border-border">
            ← Back
          </button>
        </div>

        <div className="bg-action/5 border border-action/20 rounded-lg p-4">
          <p className="font-body text-body-sm text-text-primary">
            Select a candidate. Need <strong className="text-action">{activeVote.required_majority}</strong> votes for majority.
          </p>
          <p className="font-body text-caption text-text-secondary mt-1">
            {votesCast} vote{votesCast !== 1 ? 's' : ''} cast
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 text-center">
          <div className="bg-base rounded-lg p-3">
            <div className="font-data text-data-lg text-text-primary">{mm}:{ss}</div>
            <div className="font-body text-caption text-text-secondary">Time remaining</div>
          </div>
          <div className="bg-base rounded-lg p-3">
            <div className="font-data text-data-lg text-action">{activeVote.required_majority}</div>
            <div className="font-body text-caption text-text-secondary">Votes needed</div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg divide-y divide-border">
          {countryRoles.map(r => (
            <label key={r.id} className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
              selectedCandidate === r.id ? 'bg-action/5' : 'hover:bg-base'
            }`}>
              <input type="radio" name="candidate" value={r.id} checked={selectedCandidate === r.id}
                onChange={() => setSelectedCandidate(r.id)} className="accent-action"/>
              <span className="font-body text-body-sm text-text-primary flex-1">{r.character_name}</span>
              <span className="font-body text-caption text-text-secondary capitalize">
                {r.position_type?.replace(/_/g,' ')}
              </span>
            </label>
          ))}
        </div>

        <button onClick={() => { if (selectedCandidate) handleLeaderVote(activeVote.id, selectedCandidate) }}
          disabled={!selectedCandidate || leaderVoteSubmitting}
          className="w-full bg-action text-white font-body text-body-sm font-medium py-2.5 rounded-lg hover:bg-action/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {leaderVoteSubmitting ? 'Submitting...' : 'Cast Vote'}
        </button>
      </div>
    )
  }

  // Authorization detail panel
  const authAction = authorizingId ? nuclearActions.find(a => a.id === authorizingId) : null
  if (authAction) {
    const missiles = (authAction.payload?.changes as Record<string,unknown>)?.missiles as {missile_unit_code:string;target_global_row:number;target_global_col:number}[] ?? []
    return (
      <div style={{
        backgroundColor:'#0A0E1A',borderRadius:'0.5rem',padding:'1.5rem',
        border:'2px solid rgba(255,60,20,0.4)',
      }}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'1rem'}}>
          <h2 style={{fontFamily:'JetBrains Mono, monospace',fontSize:'1.1rem',color:'#FF3C14',
            textTransform:'uppercase',letterSpacing:'0.1em'}}>{'\u26A0'} Nuclear Launch Authorization</h2>
          <button onClick={() => setAuthorizingId(null)} style={{
            fontFamily:'DM Sans, sans-serif',fontSize:'0.75rem',color:'#9CA3AF',
            cursor:'pointer',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'0.25rem',
            padding:'0.25rem 0.5rem',backgroundColor:'transparent',
          }}>{'\u2190'} Back</button>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1rem',marginBottom:'1.5rem'}}>
          <div style={{padding:'0.75rem',borderRadius:'0.25rem',border:'1px solid rgba(255,255,255,0.1)'}}>
            <div style={{fontFamily:'JetBrains Mono, monospace',fontSize:'0.65rem',color:'#9CA3AF',textTransform:'uppercase'}}>Launcher</div>
            <div style={{fontFamily:'JetBrains Mono, monospace',fontSize:'1rem',color:'#FF3C14'}}>{authAction.country_code.toUpperCase()}</div>
          </div>
          <div style={{padding:'0.75rem',borderRadius:'0.25rem',border:'1px solid rgba(255,255,255,0.1)'}}>
            <div style={{fontFamily:'JetBrains Mono, monospace',fontSize:'0.65rem',color:'#9CA3AF',textTransform:'uppercase'}}>Missiles</div>
            <div style={{fontFamily:'JetBrains Mono, monospace',fontSize:'1rem',color:'#FF3C14'}}>{missiles.length}</div>
          </div>
        </div>

        <div style={{marginBottom:'1.5rem'}}>
          <div style={{fontFamily:'JetBrains Mono, monospace',fontSize:'0.65rem',color:'#9CA3AF',textTransform:'uppercase',marginBottom:'0.5rem'}}>Targets</div>
          {missiles.map((m, i) => (
            <div key={i} style={{display:'flex',alignItems:'center',gap:'0.4rem',fontFamily:'JetBrains Mono, monospace',fontSize:'0.8rem',color:'#D1D5DB',marginBottom:'0.35rem'}}>
              <UnitIcon type="strategic_missile" size={16}/> {'\u2192'} ({m.target_global_row},{m.target_global_col}) — <span style={{color:'#FF3C14'}}>{hexCountryName(m.target_global_row, m.target_global_col)}</span>
            </div>
          ))}
        </div>

        <div style={{display:'flex',gap:'1rem'}}>
          <button onClick={() => handleAuthorize(authAction.id, true, 'Authorization confirmed by authorized officer')}
            disabled={authSubmitting}
            style={{
              flex:1,padding:'0.75rem',borderRadius:'0.375rem',cursor:'pointer',
              backgroundColor:'rgba(255,60,20,0.2)',border:'2px solid #FF3C14',
              color:'#FF3C14',fontFamily:'JetBrains Mono, monospace',
              fontSize:'0.85rem',fontWeight:700,textTransform:'uppercase',
              letterSpacing:'0.1em',opacity:authSubmitting?0.5:1,
            }}>AUTHORIZE</button>
          <button onClick={() => handleAuthorize(authAction.id, false, 'Authorization rejected by authorized officer')}
            disabled={authSubmitting}
            style={{
              flex:1,padding:'0.75rem',borderRadius:'0.375rem',cursor:'pointer',
              backgroundColor:'rgba(0,255,65,0.1)',border:'2px solid #00FF41',
              color:'#00FF41',fontFamily:'JetBrains Mono, monospace',
              fontSize:'0.85rem',fontWeight:700,textTransform:'uppercase',
              letterSpacing:'0.1em',opacity:authSubmitting?0.5:1,
            }}>REJECT</button>
        </div>
      </div>
    )
  }

  // Interception detail panel
  const intAction = interceptingId ? nuclearActions.find(a => a.id === interceptingId) : null
  if (intAction) {
    const missiles = (intAction.payload?.changes as Record<string,unknown>)?.missiles as {missile_unit_code:string;target_global_row:number;target_global_col:number}[] ?? []
    return (
      <div style={{
        backgroundColor:'#0A0E1A',borderRadius:'0.5rem',padding:'1.5rem',
        border:'2px solid rgba(255,60,20,0.4)',
      }}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'1rem'}}>
          <h2 style={{fontFamily:'JetBrains Mono, monospace',fontSize:'1.1rem',color:'#FF3C14',
            textTransform:'uppercase',letterSpacing:'0.1em'}}>{'\u26A0'} Nuclear Interception Decision</h2>
          <button onClick={() => setInterceptingId(null)} style={{
            fontFamily:'DM Sans, sans-serif',fontSize:'0.75rem',color:'#9CA3AF',
            cursor:'pointer',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'0.25rem',
            padding:'0.25rem 0.5rem',backgroundColor:'transparent',
          }}>{'\u2190'} Back</button>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'1rem',marginBottom:'1.5rem'}}>
          <div style={{padding:'0.75rem',borderRadius:'0.25rem',border:'1px solid rgba(255,255,255,0.1)'}}>
            <div style={{fontFamily:'JetBrains Mono, monospace',fontSize:'0.65rem',color:'#9CA3AF',textTransform:'uppercase'}}>Launcher</div>
            <div style={{fontFamily:'JetBrains Mono, monospace',fontSize:'1rem',color:'#FF3C14'}}>{intAction.country_code.toUpperCase()}</div>
          </div>
          <div style={{padding:'0.75rem',borderRadius:'0.25rem',border:'1px solid rgba(255,255,255,0.1)'}}>
            <div style={{fontFamily:'JetBrains Mono, monospace',fontSize:'0.65rem',color:'#9CA3AF',textTransform:'uppercase'}}>Missiles</div>
            <div style={{fontFamily:'JetBrains Mono, monospace',fontSize:'1rem',color:'#FF3C14'}}>{missiles.length}</div>
          </div>
          <div style={{padding:'0.75rem',borderRadius:'0.25rem',border:'1px solid rgba(255,255,255,0.1)'}}>
            <div style={{fontFamily:'JetBrains Mono, monospace',fontSize:'0.65rem',color:'#9CA3AF',textTransform:'uppercase'}}>Intercept Prob</div>
            <div style={{fontFamily:'JetBrains Mono, monospace',fontSize:'1rem',color:'#F59E0B'}}>25% / AD</div>
          </div>
        </div>

        <div style={{marginBottom:'1rem'}}>
          <div style={{fontFamily:'JetBrains Mono, monospace',fontSize:'0.65rem',color:'#9CA3AF',textTransform:'uppercase',marginBottom:'0.5rem'}}>Targets</div>
          {missiles.map((m, i) => (
            <div key={i} style={{display:'flex',alignItems:'center',gap:'0.4rem',fontFamily:'JetBrains Mono, monospace',fontSize:'0.8rem',color:'#D1D5DB',marginBottom:'0.35rem'}}>
              <UnitIcon type="strategic_missile" size={16}/> {'\u2192'} ({m.target_global_row},{m.target_global_col}) — <span style={{color:'#FF3C14'}}>{hexCountryName(m.target_global_row, m.target_global_col)}</span>
            </div>
          ))}
        </div>

        <div style={{
          padding:'0.75rem',borderRadius:'0.25rem',border:'1px solid rgba(245,158,11,0.3)',
          backgroundColor:'rgba(245,158,11,0.05)',marginBottom:'1.5rem',
        }}>
          <p style={{fontFamily:'JetBrains Mono, monospace',fontSize:'0.7rem',color:'#F59E0B'}}>
            WARNING: Intercepting reveals your AD capability and sides you against {intAction.country_code.toUpperCase()}.
          </p>
        </div>

        <div style={{display:'flex',gap:'1rem'}}>
          <button onClick={() => handleIntercept(intAction.id, true, 'Interception ordered')}
            disabled={interceptSubmitting}
            style={{
              flex:1,padding:'0.75rem',borderRadius:'0.375rem',cursor:'pointer',
              backgroundColor:'rgba(255,60,20,0.2)',border:'2px solid #FF3C14',
              color:'#FF3C14',fontFamily:'JetBrains Mono, monospace',
              fontSize:'0.85rem',fontWeight:700,textTransform:'uppercase',
              letterSpacing:'0.1em',opacity:interceptSubmitting?0.5:1,
            }}>INTERCEPT</button>
          <button onClick={() => handleIntercept(intAction.id, false, 'Declined interception')}
            disabled={interceptSubmitting}
            style={{
              flex:1,padding:'0.75rem',borderRadius:'0.375rem',cursor:'pointer',
              backgroundColor:'rgba(255,255,255,0.05)',border:'2px solid rgba(255,255,255,0.2)',
              color:'#9CA3AF',fontFamily:'JetBrains Mono, monospace',
              fontSize:'0.85rem',fontWeight:700,textTransform:'uppercase',
              letterSpacing:'0.1em',opacity:interceptSubmitting?0.5:1,
            }}>DECLINE</button>
        </div>
      </div>
    )
  }

  // ── Election Detail Panels (rendered before main actions list) ────────
  if (showElectionPanel === 'nominate' && activeNominationEvent) {
    const elType = activeNominationEvent.subtype === 'presidential' ? 'Presidential Election' : 'Mid-Term Parliamentary Election'
    const currentNominees = electionNominations.filter(n =>
      n.election_type === activeNominationEvent.subtype && n.election_round === activeNominationEvent.round
    )
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-h2 text-text-primary">Columbia Elections — Nominations</h2>
          <button onClick={() => setShowElectionPanel(null)}
            className="font-body text-caption text-text-secondary hover:text-text-primary px-3 py-1 rounded border border-border">
            ← Back
          </button>
        </div>

        <div className={`${nominationsClosed ? 'bg-base' : 'bg-action/5'} border ${nominationsClosed ? 'border-border' : 'border-action/20'} rounded-lg p-4 space-y-2`}>
          <p className="font-body text-body-sm text-text-primary">
            <strong className="text-action">{elType}</strong> — Election Round {activeNominationEvent.round}
          </p>
          <p className="font-body text-caption text-text-secondary">
            {nominationsClosed
              ? `Nominations are closed. The election will take place in Round ${activeNominationEvent.round}.`
              : `Nominations are open this round. The election will take place in Round ${activeNominationEvent.round}.`
            }
          </p>
          <p className="font-body text-caption text-text-secondary">
            Opposition candidates represent 2 votes each. Under certain political and economic circumstances, their popular support may grow to 3 votes each.
          </p>
        </div>

        {currentNominees.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="font-heading text-caption text-text-secondary uppercase tracking-wider mb-3">
              {nominationsClosed ? 'Candidates' : 'Current Nominees'}
            </h3>
            <div className="space-y-2">
              {currentNominees.map(n => (
                <div key={n.id} className="flex items-center gap-3 bg-base rounded px-3 py-2">
                  <span className="font-body text-body-sm text-text-primary font-medium capitalize">{n.role_id}</span>
                  <span className={`font-body text-caption px-2 py-0.5 rounded ${
                    n.camp === 'opposition' ? 'bg-action/10 text-action' : 'bg-text-secondary/10 text-text-secondary'
                  }`}>{n.camp === 'opposition' ? 'Opposition' : 'Government'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {nominationsClosed ? null : !isNominated ? (
          <button onClick={handleSelfNominate} disabled={electionSubmitting}
            className="w-full bg-action text-white font-body text-body-sm font-medium py-2.5 rounded-lg hover:bg-action/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {electionSubmitting ? 'Submitting...' : 'Nominate Yourself'}
          </button>
        ) : (
          <div className="space-y-2">
            <div className="bg-success/10 border border-success/20 rounded-lg p-3">
              <p className="font-body text-body-sm text-success">You are nominated for this election.</p>
            </div>
            <button onClick={handleWithdrawNomination} disabled={electionSubmitting}
              className="w-full bg-danger/10 text-danger font-body text-body-sm font-medium py-2.5 rounded-lg hover:bg-danger/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {electionSubmitting ? 'Withdrawing...' : 'Withdraw Nomination'}
            </button>
          </div>
        )}
      </div>
    )
  }

  if (showElectionPanel === 'vote' && activeElectionEvent && !electionResolved) {
    const elType = activeElectionEvent.subtype === 'presidential' ? 'Presidential Election' : 'Mid-Term Parliamentary Election'
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-h2 text-text-primary">Columbia Elections — Cast Your Vote</h2>
          <button onClick={() => setShowElectionPanel(null)}
            className="font-body text-caption text-text-secondary hover:text-text-primary px-3 py-1 rounded border border-border">
            ← Back
          </button>
        </div>

        <div className="bg-action/5 border border-action/20 rounded-lg p-4 space-y-2">
          <p className="font-body text-body-sm text-text-primary">
            <strong className="text-action">{elType}</strong> — Secret Ballot
          </p>
          <p className="font-body text-caption text-text-secondary">
            Select a candidate and cast your vote. Your choice will not be revealed to other participants.
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg divide-y divide-border">
          {electionCandidates.map(c => (
            <label key={c.role_id} className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
              selectedElectionCandidate === c.role_id ? 'bg-action/5' : 'hover:bg-base'
            }`}>
              <input type="radio" name="electionCandidate" value={c.role_id}
                checked={selectedElectionCandidate === c.role_id}
                onChange={() => setSelectedElectionCandidate(c.role_id)}
                className="accent-action"/>
              <span className="font-body text-body-sm text-text-primary flex-1 capitalize">{c.role_id}</span>
            </label>
          ))}
        </div>

        <button onClick={handleCastElectionVote}
          disabled={!selectedElectionCandidate || electionSubmitting}
          className="w-full bg-action text-white font-body text-body-sm font-medium py-2.5 rounded-lg hover:bg-action/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {electionSubmitting ? 'Submitting...' : 'Cast Vote'}
        </button>
      </div>
    )
  }

  // Election results panel
  if (showElectionPanel === 'results' && electionResolved && activeElectionEvent) {
    const elType = activeElectionEvent.subtype === 'presidential' ? 'Presidential Election' : 'Mid-Term Parliamentary Election'
    const totalVotes = electionResolved.total_votes as Record<string, number> || {}
    const totalSum = Object.values(totalVotes).reduce((s, v) => s + v, 0)
    const sortedCandidates = Object.entries(totalVotes).sort((a, b) => b[1] - a[1])
    const winner = electionResolved.winner_role_id

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-h2 text-text-primary">Election Results</h2>
          <button onClick={() => setShowElectionPanel(null)}
            className="font-body text-caption text-text-secondary hover:text-text-primary px-3 py-1 rounded border border-border">
            ← Back
          </button>
        </div>

        <div className="bg-success/5 border border-success/20 rounded-lg p-4">
          <p className="font-body text-body-sm text-text-primary">
            <strong className="text-success">{elType}</strong> — Results
          </p>
          {winner && (
            <p className="font-body text-body-sm text-success font-medium mt-1 capitalize">
              Winner: {winner}
            </p>
          )}
          {!winner && (
            <p className="font-body text-body-sm text-warning font-medium mt-1">
              No candidate achieved majority
            </p>
          )}
        </div>

        <div className="bg-card border border-border rounded-lg divide-y divide-border">
          {sortedCandidates.map(([candidate, votes]) => {
            const pct = totalSum > 0 ? (votes / totalSum * 100) : 0
            const isWinner = candidate === winner
            return (
              <div key={candidate} className="px-4 py-3">
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-body text-body-sm font-medium capitalize ${isWinner ? 'text-success' : 'text-text-primary'}`}>
                    {candidate} {isWinner && '— Elected'}
                  </span>
                  <span className="font-data text-caption text-text-secondary">{pct.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-border rounded-full h-2">
                  <div className={`h-2 rounded-full ${isWinner ? 'bg-success' : 'bg-action/40'}`}
                    style={{ width: `${pct}%` }} />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const showMoveUnits = currentPhase === 'B' && parentSimRun?.engines_complete && avail.has('move_units')
  const showLeaderVote = activeVote && !hasVotedInActiveVote ? 1 : 0
  const electionExpected = (showNomination ? 1 : 0) + (showElectionVote ? 1 : 0) + (isNominated ? 1 : 0) + (electionResolved ? 1 : 0)
  const showNavCard = navNudge && !navDismissed && onStartNavigator
  const expectedCount = pendingTxns.length + pendingAgreements.length + (showMoveUnits ? 1 : 0) + myMeetingInvitations.length + notifications.length
    + pendingAuthorizations.length + visibleInterceptions.length + showLeaderVote + electionExpected + (showNavCard ? 1 : 0)
  const hasExpected = expectedCount > 0

  return (
    <div className="space-y-4">
      {/* Actions Expected Now */}
      <div className={`border rounded-lg p-4 ${hasExpected?'bg-warning/10 border-warning/30':'bg-warning/5 border-warning/20'}`}>
        <h3 className="font-heading text-h3 text-warning mb-2">Actions Expected Now{hasExpected?` (${expectedCount})`:''}</h3>
        {!hasExpected
          ? <p className="font-body text-caption text-text-secondary">Urgent actions will appear here for your review and decision. No urgent actions at this time.</p>
          : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {showNavCard && (
              <div className="text-left bg-card border-2 border-warning/40 rounded-lg px-4 py-3 flex flex-col gap-2">
                <span className="font-body text-body-sm text-warning font-bold block">Prepare with Navigator</span>
                <span className="font-body text-caption text-text-secondary">Talk to your personal advisor — set goals, understand your role, form a strategy.</span>
                <div className="flex items-center gap-2 mt-1">
                  <button onClick={() => { dismissNav(); onStartNavigator!() }}
                    className="font-body text-caption font-medium bg-warning/10 text-warning px-3 py-1 rounded hover:bg-warning/20 transition-colors">
                    Talk Now
                  </button>
                  <button onClick={() => dismissNav()}
                    className="font-body text-caption text-text-secondary hover:text-text-primary transition-colors">
                    Later
                  </button>
                </div>
              </div>
            )}
            {showMoveUnits && (
              <button onClick={()=>onSelectAction('move_units')}
                className="text-left bg-card hover:bg-action/5 border-2 border-action/40 hover:border-action/60 rounded-lg px-4 py-3 transition-colors">
                <span className="font-body text-body-sm text-action font-bold block">Deploy & Move Units</span>
                <span className="font-body text-caption text-text-secondary">Position your forces for the next round</span>
              </button>
            )}
            {pendingAuthorizations.map(na => (
              <button key={na.id} onClick={() => setAuthorizingId(na.id)}
                style={{
                  textAlign:'left',backgroundColor:'rgba(255,60,20,0.08)',
                  border:'2px solid rgba(255,60,20,0.5)',borderRadius:'0.5rem',
                  padding:'0.75rem 1rem',cursor:'pointer',
                }}>
                <span style={{fontFamily:'JetBrains Mono, monospace',fontSize:'0.85rem',color:'#FF3C14',fontWeight:700,display:'block'}}>
                  {'\u26A0'} NUCLEAR LAUNCH AUTHORIZATION
                </span>
                <span style={{fontFamily:'DM Sans, sans-serif',fontSize:'0.75rem',color:'#D1D5DB',display:'block',marginTop:'0.25rem'}}>
                  {na.country_code.toUpperCase()} requests launch authorization — click to review
                </span>
              </button>
            ))}
            {visibleInterceptions.map(na => (
              <button key={na.id} onClick={() => setInterceptingId(na.id)}
                style={{
                  textAlign:'left',backgroundColor:'rgba(255,60,20,0.08)',
                  border:'2px solid rgba(255,60,20,0.5)',borderRadius:'0.5rem',
                  padding:'0.75rem 1rem',cursor:'pointer',
                }}>
                <span style={{fontFamily:'JetBrains Mono, monospace',fontSize:'0.85rem',color:'#FF3C14',fontWeight:700,display:'block'}}>
                  {'\u26A0'} NUCLEAR INTERCEPTION DECISION
                </span>
                <span style={{fontFamily:'DM Sans, sans-serif',fontSize:'0.75rem',color:'#D1D5DB',display:'block',marginTop:'0.25rem'}}>
                  {na.country_code.toUpperCase()} launched missiles — intercept?
                </span>
              </button>
            ))}
            {activeVote && !hasVotedInActiveVote && (
              <button onClick={() => setVotingOnLeader(activeVote.id)}
                className="text-left bg-card hover:bg-action/5 border border-warning/30 hover:border-action/30 rounded-lg px-4 py-3 transition-colors">
                <span className="font-body text-body-sm text-text-primary font-medium block">
                  {activeVote.phase === 'removal' ? 'Vote: Remove Head of State' : 'Vote: Elect New Leader'}
                </span>
                <span className="font-body text-caption text-text-secondary">
                  {leaderVoteCountdown !== null && `${Math.floor(leaderVoteCountdown/60)}:${String(Math.floor(leaderVoteCountdown%60)).padStart(2,'0')} remaining — `}
                  Click to vote
                </span>
              </button>
            )}
            {nominationsClosed && activeNominationEvent && (
              <button onClick={() => setShowElectionPanel('nominate')}
                className="text-left bg-card border border-border rounded-lg px-4 py-3 transition-colors hover:bg-base">
                <span className="font-body text-body-sm text-text-primary font-medium block">
                  Nominations Closed — {activeNominationEvent.subtype === 'presidential' ? 'Presidential' : 'Mid-Term'}
                </span>
                <span className="font-body text-caption text-text-secondary">
                  {electionNominations.filter(n => n.election_type === activeNominationEvent.subtype).length} candidates — Election R{activeNominationEvent.round}
                </span>
              </button>
            )}
            {!nominationsClosed && showNomination && activeNominationEvent && (
              <button onClick={() => setShowElectionPanel('nominate')}
                className="text-left bg-card hover:bg-action/5 border-2 border-action/40 hover:border-action/60 rounded-lg px-4 py-3 transition-colors">
                <span className="font-body text-body-sm text-action font-bold block">
                  Columbia Elections — Nominations Open
                </span>
                <span className="font-body text-caption text-text-secondary">
                  {activeNominationEvent.subtype === 'presidential' ? 'Presidential' : 'Mid-Term'} — Click to nominate
                </span>
              </button>
            )}
            {!nominationsClosed && isNominated && activeNominationEvent && (
              <button onClick={() => setShowElectionPanel('nominate')}
                className="text-left bg-success/5 border border-success/30 rounded-lg px-4 py-3 transition-colors hover:bg-success/10">
                <span className="font-body text-body-sm text-success font-medium block">
                  You are nominated — {activeNominationEvent.subtype === 'presidential' ? 'Presidential' : 'Mid-Term'}
                </span>
                <span className="font-body text-caption text-text-secondary">Click to view or withdraw</span>
              </button>
            )}
            {showElectionVote && activeElectionEvent && (
              <button onClick={() => setShowElectionPanel('vote')}
                className="text-left bg-card hover:bg-action/5 border-2 border-action/40 hover:border-action/60 rounded-lg px-4 py-3 transition-colors">
                <span className="font-body text-body-sm text-action font-bold block">
                  Columbia Elections — Cast Your Vote
                </span>
                <span className="font-body text-caption text-text-secondary">
                  {activeElectionEvent.subtype === 'presidential' ? 'Presidential' : 'Mid-Term'} — {electionCandidates.length} candidate{electionCandidates.length !== 1 ? 's' : ''}
                </span>
              </button>
            )}
            {electionResolved && activeElectionEvent && (
              <button onClick={() => setShowElectionPanel('results')}
                className="text-left bg-success/5 border border-success/30 rounded-lg px-4 py-3 transition-colors hover:bg-success/10">
                <span className="font-body text-body-sm text-success font-medium block">
                  Election Results — {activeElectionEvent.subtype === 'presidential' ? 'Presidential' : 'Mid-Term'}
                </span>
                <span className="font-body text-caption text-text-secondary">
                  {electionResolved.winner_role_id ? `Winner: ${electionResolved.winner_role_id}` : 'No majority'} — Click to view
                </span>
              </button>
            )}
            {pendingTxns.map(txn=>
              <button key={txn.id} onClick={()=>setReviewTxn(txn.id)}
                className="text-left bg-card hover:bg-action/5 border border-warning/30 hover:border-action/30 rounded-lg px-4 py-3 transition-colors">
                <span className="font-body text-body-sm text-text-primary font-medium block">Transaction from {txn.proposer}</span>
                <span className="font-body text-caption text-text-secondary">Click to review</span>
              </button>
            )}
            {pendingAgreements.map(agr=>
              <button key={agr.id} onClick={()=>setReviewAgr(agr.id)}
                className="text-left bg-card hover:bg-action/5 border border-action/30 hover:border-action/50 rounded-lg px-4 py-3 transition-colors">
                <span className="font-body text-body-sm text-text-primary font-medium block capitalize">{agr.agreement_type.replace('_',' ')}</span>
                <span className="font-body text-caption text-text-secondary block">"{agr.agreement_name}" — by {agr.proposer_country_code}</span>
                <span className="font-body text-caption text-action mt-1 block">Click to review</span>
              </button>
            )}
            {/* Meeting invitations */}
            {myMeetingInvitations.map(inv => (
              <div key={inv.id} className="bg-card border border-action/30 rounded-lg px-4 py-3">
                <div className="font-body text-body-sm text-text-primary font-medium">
                  {inv.invitation_type === 'one_on_one'
                    ? <>Meeting request from <strong>{roleInfo[inv.inviter_role_id]?.name || inv.inviter_role_id}</strong>
                        {roleInfo[inv.inviter_role_id]?.position && (
                          <span className="text-action/70 ml-1 font-normal">{roleInfo[inv.inviter_role_id].position}</span>
                        )}
                        <span className="text-text-secondary/50 ml-1 font-normal uppercase text-[10px] tracking-wider">{inv.inviter_country_code}</span>
                      </>
                    : `${inv.org_name || inv.org_id} meeting`}
                </div>
                {inv.message && <p className="font-body text-caption text-text-secondary mt-0.5">{inv.message}</p>}
                {inv.theme && <p className="font-body text-caption text-text-secondary mt-0.5">Theme: {inv.theme}</p>}
                <div className="flex gap-2 mt-2">
                  <button onClick={() => handleMeetingResponse(inv.id, 'accept', '', inv.inviter_role_id)} disabled={meetingSubmitting}
                    className="font-body text-caption font-medium bg-success/10 text-success px-3 py-1 rounded hover:bg-success/20 disabled:opacity-50 transition-colors">
                    Accept
                  </button>
                  <button onClick={() => handleMeetingResponse(inv.id, 'reject', '')} disabled={meetingSubmitting}
                    className="font-body text-caption font-medium bg-danger/10 text-danger px-3 py-1 rounded hover:bg-danger/20 disabled:opacity-50 transition-colors">
                    Decline
                  </button>
                  <button onClick={() => handleMeetingResponse(inv.id, 'later', '')} disabled={meetingSubmitting}
                    className="font-body text-caption font-medium bg-warning/10 text-warning px-3 py-1 rounded hover:bg-warning/20 disabled:opacity-50 transition-colors">
                    Not now
                  </button>
                </div>
              </div>
            ))}
            {/* Notification cards (universal mailbox) */}
            {notifications.map(n => {
              const isNuclearFlight = n.notification_type === 'nuclear_in_flight'
              const accent = isNuclearFlight ? 'danger'
                : n.notification_type.includes('declined') || n.notification_type.includes('rejected') || n.notification_type.includes('revoked') || n.notification_type.includes('attack')
                ? 'danger'
                : n.notification_type.includes('accepted') || n.notification_type.includes('signed') || n.notification_type.includes('granted') || n.notification_type.includes('enacted')
                ? 'success'
                : 'warning'
              return (
                <div key={n.id} className={`bg-card border-l-4 border-${accent}/40 border border-border rounded-lg px-4 py-3 ${isNuclearFlight ? 'animate-pulse' : ''}`}>
                  <div className="font-body text-body-sm text-text-primary font-medium">{n.title}</div>
                  {n.body && n.notification_type !== 'budget_approval' && <p className="font-body text-caption text-text-secondary mt-0.5">{n.body}</p>}
                  <div className="flex justify-end gap-2 mt-2">
                    {n.notification_type === 'budget_approval' && (
                      <>
                        <button
                          onClick={async () => {
                            try {
                              await submitAction(simId, 'approve_budget', roleId, countryId, { response: 'approve' })
                              handleDismissNotification(n.id)
                            } catch (e) { alert('Approve failed: ' + (e instanceof Error ? e.message : e)) }
                          }}
                          className="font-body text-caption font-medium bg-success/10 text-success px-4 py-1.5 rounded hover:bg-success/20 transition-colors"
                        >
                          Approve
                        </button>
                        <button
                          onClick={async () => {
                            if (!confirm('Reject this budget? The economy leader will need to resubmit.')) return
                            try {
                              await submitAction(simId, 'approve_budget', roleId, countryId, { response: 'reject' })
                              handleDismissNotification(n.id)
                            } catch (e) { alert('Reject failed: ' + (e instanceof Error ? e.message : e)) }
                          }}
                          className="font-body text-caption font-medium bg-danger/10 text-danger px-4 py-1.5 rounded hover:bg-danger/20 transition-colors"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => {
                            const p = n.payload as Record<string,unknown> | null
                            setBudgetReviewPayload(p)
                            setBudgetReviewNotifId(n.id)
                          }}
                          className="font-body text-caption font-medium bg-action/10 text-action px-4 py-1.5 rounded hover:bg-action/20 transition-colors"
                        >
                          View Details
                        </button>
                      </>
                    )}
                    {isNuclearFlight && n.payload?.action_id && (
                      <button
                        onClick={async () => {
                          if (!confirm('CANCEL NUCLEAR LAUNCH? Missiles will be destroyed in flight.')) return
                          try {
                            const token = (await supabase.auth.getSession()).data.session?.access_token
                            await fetch(`/api/sim/${simId}/nuclear/${n.payload!.action_id}/cancel`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ role_id: roleId }),
                            })
                            handleDismissNotification(n.id)
                          } catch (e) { alert('Cancel failed: ' + (e instanceof Error ? e.message : e)) }
                        }}
                        className="font-body text-caption font-medium bg-danger/10 text-danger px-4 py-0.5 rounded hover:bg-danger/20 transition-colors"
                      >
                        Cancel Launch
                      </button>
                    )}
                    {n.notification_type !== 'budget_approval' && (
                      <button
                        onClick={() => handleDismissNotification(n.id)}
                        className="font-body text-caption font-medium bg-action/10 text-action px-4 py-0.5 rounded hover:bg-action/20 transition-colors"
                      >
                        {isNuclearFlight ? '✕' : 'OK'}
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        }
      </div>

      {/* Budget Review Overlay (Columbia Parliament Chair) */}
      {budgetReviewPayload && (() => {
        const b = (budgetReviewPayload.budget ?? {}) as Record<string,unknown>
        const cc = (budgetReviewPayload.country ?? {}) as Record<string,unknown>
        const tu = Number(budgetReviewPayload.total_units ?? 0)
        const gdp = Number(cc.gdp ?? 0)
        const taxRate = Number(cc.tax_rate ?? 0.2)
        const revenue = Math.round(gdp * taxRate * 10) / 10
        const treasury = Number(cc.treasury ?? 0)
        const inflation = Number(cc.inflation ?? 0)
        const stability = Number(cc.stability ?? 5)
        const socialBaseline = Number(cc.social_baseline ?? 0.2)
        const socialPct = Number(b.social_pct ?? 1)
        const socialBase = Math.round(socialBaseline * revenue * 10) / 10
        const socialSpending = Math.round(socialBase * socialPct * 10) / 10
        const maintRate = Number(cc.maintenance_per_unit ?? 0.02)
        const maintenance = Math.round(tu * maintRate * 3 * 10) / 10
        const prod = (b.production ?? {}) as Record<string,number>
        const BCOST: Record<string,number> = {ground:3,naval:6,tactical_air:5,strategic_missile:8,air_defense:4}
        const PMULT: Record<number,number> = {0:0,1:1,2:2,3:4}
        const POUT: Record<number,number> = {0:0,1:1,2:2,3:3}
        let totalProd = 0
        const prodLines: {branch:string;level:number;units:number;coins:number}[] = []
        for (const br of ['ground','naval','tactical_air']) {
          const capKey = br === 'tactical_air' ? 'prod_cap_tactical' : `prod_cap_${br}`
          const cap = Number(cc[capKey] ?? 0)
          const level = prod[br] ?? 0
          const coins = Math.round(BCOST[br] * cap * (PMULT[level] ?? 0))
          const units = Math.round(cap * (POUT[level] ?? 0))
          if (cap > 0) { prodLines.push({branch: br, level, units, coins}); totalProd += coins }
        }
        const research = (b.research ?? {}) as Record<string,number>
        const nucRD = Number(research.nuclear_coins ?? 0)
        const aiRD = Number(research.ai_coins ?? 0)
        const techTotal = nucRD + aiRD
        const totalSpending = socialSpending + maintenance + totalProd + techTotal
        const balance = revenue - totalSpending
        const deficit = balance < 0 ? -balance : 0
        const surplus = balance > 0 ? balance : 0
        const coveredByTreasury = Math.min(deficit, treasury)
        const moneyPrinted = deficit > coveredByTreasury ? deficit - coveredByTreasury : 0
        const expectedTreasury = treasury + surplus - coveredByTreasury
        const Ln = ({l,v,bold,danger,sub}:{l:string;v:string;bold?:boolean;danger?:boolean;sub?:boolean}) => (
          <div className={`flex justify-between py-0.5 ${sub?'pl-4':''}`}>
            <span className={`font-body text-caption ${danger?'text-danger':'text-text-primary'} ${bold?'font-medium':''}`}>{l}</span>
            <span className={`font-data text-caption ${danger?'text-danger font-bold':'text-text-primary'} ${bold?'font-bold':''}`}>{v}</span>
          </div>
        )
        return (
          <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto space-y-3">
              <h2 className="font-heading text-h2 text-text-primary">Draft Budget — Review</h2>
              <p className="font-body text-caption text-text-secondary">Submitted by Economy leader for your approval as Parliament Chair.</p>
              <div className="grid grid-cols-2 gap-2 font-data text-caption bg-base rounded-lg p-3">
                <div><span className="text-text-secondary">GDP</span> <span className="text-text-primary font-bold">{gdp.toFixed(1)}</span></div>
                <div><span className="text-text-secondary">Treasury</span> <span className="text-text-primary font-bold">ç{treasury.toFixed(1)}</span></div>
                <div><span className="text-text-secondary">Inflation</span> <span className="text-text-primary font-bold">{inflation.toFixed(1)}%</span></div>
                <div><span className="text-text-secondary">Stability</span> <span className="text-text-primary font-bold">{stability.toFixed(1)}</span></div>
              </div>
              <div className="bg-base rounded-lg p-3">
                <Ln l={`Revenue (GDP ${gdp.toFixed(1)} × Tax ${(taxRate*100).toFixed(0)}%)`} v={`ç${revenue.toFixed(1)}`} bold />
              </div>
              <div className={`border-2 rounded-lg p-3 ${deficit>0?'border-danger/30':'border-success/20'}`}>
                <Ln l="Social Spending" v={`ç${socialSpending.toFixed(1)} (${(socialPct*100).toFixed(0)}%)`} />
                <Ln l={`Maintenance (${tu} units)`} v={`ç${maintenance.toFixed(1)}`} />
                {prodLines.map(p => (
                  <Ln key={p.branch} l={`  ${p.branch.replace('_',' ')} (${['No','Std','x2','x3'][p.level]})`} v={p.coins > 0 ? `ç${p.coins} → ${p.units} units` : '—'} sub />
                ))}
                <Ln l="Production Total" v={`ç${totalProd}`} bold />
                <Ln l={`Nuclear R&D`} v={`ç${nucRD}`} sub />
                <Ln l={`AI R&D`} v={`ç${aiRD}`} sub />
                <Ln l="R&D Total" v={`ç${techTotal}`} bold />
                <div className="border-t border-border my-1"/>
                <Ln l="Total Spending" v={`ç${totalSpending.toFixed(1)}`} bold />
                <div className="border-t border-border my-1"/>
                {surplus>0 && <Ln l="Surplus → Treasury" v={`+ç${surplus.toFixed(1)}`} />}
                {deficit>0 && <>
                  <Ln l="Deficit" v={`-ç${deficit.toFixed(1)}`} danger />
                  <Ln l="From Treasury" v={`-ç${coveredByTreasury.toFixed(1)}`} sub />
                  {moneyPrinted>0 && <Ln l="Money Printed" v={`ç${moneyPrinted.toFixed(1)}`} danger sub />}
                </>}
                <div className="border-t border-border my-1"/>
                <Ln l="Expected Treasury" v={`ç${Math.max(0,expectedTreasury).toFixed(1)}`} bold />
                {moneyPrinted>0 && <p className="font-body text-caption text-danger mt-1">Warning: money printing will increase inflation.</p>}
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={async () => {
                  try {
                    await submitAction(simId, 'approve_budget', roleId, countryId, { response: 'approve' })
                    if (budgetReviewNotifId) handleDismissNotification(budgetReviewNotifId)
                    setBudgetReviewPayload(null)
                  } catch (e) { alert('Failed: ' + (e instanceof Error ? e.message : e)) }
                }} className="flex-1 bg-success/10 text-success font-body text-body-sm font-medium py-2.5 rounded-lg hover:bg-success/20 transition-colors">
                  Approve
                </button>
                <button onClick={async () => {
                  if (!confirm('Reject? Economy leader must resubmit.')) return
                  try {
                    await submitAction(simId, 'approve_budget', roleId, countryId, { response: 'reject' })
                    if (budgetReviewNotifId) handleDismissNotification(budgetReviewNotifId)
                    setBudgetReviewPayload(null)
                  } catch (e) { alert('Failed: ' + (e instanceof Error ? e.message : e)) }
                }} className="flex-1 bg-danger/10 text-danger font-body text-body-sm font-medium py-2.5 rounded-lg hover:bg-danger/20 transition-colors">
                  Reject
                </button>
                <button onClick={() => setBudgetReviewPayload(null)}
                  className="px-4 py-2.5 font-body text-body-sm text-text-secondary hover:text-text-primary transition-colors">
                  Back
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Active Meetings — chat buttons */}
      {activeMeetings.length > 0 && (
        <div className="bg-card border border-action/20 rounded-lg p-4">
          <h3 className="font-heading text-caption text-text-secondary uppercase tracking-wider mb-3">Active Meetings</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {activeMeetings.map(m => {
              const isA = m.participant_a_role_id === roleId
              const otherRole = isA ? m.participant_b_role_id : m.participant_a_role_id
              const otherCountry = isA ? m.participant_b_country : m.participant_a_country
              return (
                <div key={m.id} className="relative bg-base hover:bg-action/5 border border-action/30 hover:border-action/50 rounded-lg px-4 py-3 transition-colors group">
                  <button onClick={() => onOpenChat?.(m.id, otherRole)} className="text-left w-full">
                    <span className="font-body text-body-sm text-text-primary group-hover:text-action block">
                      {roleInfo[otherRole]?.name || otherRole}
                      {roleInfo[otherRole]?.position && (
                        <span className="text-action/60 ml-1 text-[10px] font-normal">{roleInfo[otherRole].position}</span>
                      )}
                    </span>
                    <span className="font-body text-caption text-text-secondary block">
                      {otherCountry.toUpperCase()}
                    </span>
                    <span className="font-body text-caption text-action/60 group-hover:text-action mt-0.5 block">
                      {m.modality === 'voice' ? '\uD83C\uDF99\uFE0F Voice Call' : '\uD83D\uDCAC Open Chat'}
                    </span>
                  </button>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation()
                      try {
                        await endMeeting(simId, m.id, roleId)
                        setActiveMeetings(prev => prev.filter(x => x.id !== m.id))
                      } catch { /* ignore */ }
                    }}
                    className="absolute top-2 right-2 text-text-secondary/40 hover:text-danger p-1 transition-colors"
                    title="End meeting"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6 6 18M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {getOrderedCats(rolePositions).map((cat)=>{
        const ATTACK_ACTIONS = ['ground_attack','air_strike','naval_combat','naval_bombardment','launch_missile_conventional']
        const isPreStart = currentPhase === 'pre'
        const isPhaseB = currentPhase === 'B'
        const acts=cat.actions.filter(a=>{
          if (a.id === 'attack') {
            if (isPhaseB) return false // no attacks during Phase B
            return ATTACK_ACTIONS.some(x=>avail.has(x))
          }
          if (!avail.has(a.id)) return false
          if (isPreStart) return true // show all in R0
          // Phase B: hide all proactive actions, show only move_units
          if (isPhaseB) {
            if (PHASE_B_ONLY.has(a.id)) return true
            if (PHASE_A_ONLY.has(a.id)) return false
            return true // reactive/election actions pass through
          }
          // Phase A: hide move_units
          if (PHASE_B_ONLY.has(a.id)) return false
          return true
        })

        // Special timing actions — only shown in R0 to help participants learn
        const SPECIAL_TIMING: Record<string, {id:string;label:string;when:string}[]> = {
          military: [
            {id:'move_units', label:'Move Units', when:'Between rounds only'},
            {id:'nuclear_authorize', label:'Authorize Nuclear Launch', when:'When HoS initiates launch'},
            {id:'nuclear_intercept', label:'Intercept Nuclear Strike', when:'When incoming strike detected'},
          ],
          political: [
            {id:'self_nominate', label:'Self-Nominate', when:'One round before elections'},
            {id:'cast_vote', label:'Vote', when:'During elections'},
          ],
        }
        const specialActs = isPreStart ? (SPECIAL_TIMING[cat.key] ?? []).filter(sa => {
          // nuclear_authorize/intercept are reactive — check if role has military or security position
          if (sa.id === 'nuclear_authorize' || sa.id === 'nuclear_intercept') {
            return rolePositions.some(p => ['military','military_chief','security','head_of_state'].includes(p))
          }
          // self_nominate/cast_vote — Columbia only
          if (sa.id === 'self_nominate' || sa.id === 'cast_vote') {
            return countryId === 'columbia'
          }
          // move_units — check if role has it
          return avail.has(sa.id)
        }) : []

        if(!acts.length && !specialActs.length) return null
        const primaryKeys = new Set(rolePositions.map(p => POSITION_TO_PRIMARY[p]).filter(Boolean))
        if (primaryKeys.size === 0) primaryKeys.add('political')
        const isPrimary = primaryKeys.has(cat.key)
        return <div key={cat.key} className={`bg-card border rounded-lg p-4 ${isPrimary ? 'border-action/40 ring-1 ring-action/10' : 'border-border'}`}>
          <h3 className={`font-heading text-caption uppercase tracking-wider mb-3 ${isPrimary ? 'text-action font-medium' : 'text-text-secondary'}`}>{cat.label}{isPrimary ? ' — your domain' : ''}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {acts.map(a=>{const Icon=ACTION_ICONS[a.id];return <button key={a.id} onClick={()=>onSelectAction(a.id)} className="text-left bg-base hover:bg-action/5 border border-border hover:border-action/30 rounded-lg px-4 py-3 min-h-[48px] transition-colors group flex items-center gap-3">
              {Icon&&<Icon size={18} strokeWidth={1.75} className="text-text-secondary group-hover:text-action flex-shrink-0"/>}
              <span className="font-body text-body-sm text-text-primary group-hover:text-action">{a.label}</span>
            </button>})}
            {specialActs.map(sa=>{const Icon=ACTION_ICONS[sa.id];return(
              <div key={sa.id} className="text-left bg-warning/5 border border-warning/20 rounded-lg px-4 py-3 flex items-center gap-3">
                {Icon&&<Icon size={18} strokeWidth={1.75} className="text-warning/60 flex-shrink-0"/>}
                <div>
                  <span className="font-body text-body-sm text-warning/80 block">{sa.label}</span>
                  <span className="font-body text-caption text-warning/50">{sa.when}</span>
                </div>
              </div>
            )})}
          </div>
        </div>
      })}

      {/* My Action Log — at the bottom of Actions tab */}
      <MyActionLog events={actionLogEvents||[]} sentInvitations={actionLogInvitations||[]} proposedTxns={actionLogTxns||[]} proposedAgrs={actionLogAgrs||[]} roleId={roleId} positions={rolePositions}/>
    </div>
  )
}

/* ── Tab: Confidential ─────────────────────────────────────────────────── */

function TabConf({role,artefacts,objectives,personalRels,orgMemberships,isR0,onNameChange,onRead}:{
  role:RoleData; artefacts:Artefact[]; objectives:string[]
  personalRels:{other_role:string;type:string;notes:string}[]
  orgMemberships:{org_id:string;role_in_org:string;has_veto:boolean}[]
  isR0?:boolean; onNameChange?:()=>void
  onRead:(id:string)=>void
}) {
  const [open,setOpen]=useState<string|null>(null)
  const pRelColor=(t:string)=>({ally:'text-success',patron:'text-action',reports_to:'text-text-secondary',rival:'text-warning',enemy:'text-danger',mentor:'text-accent'}[t]??'text-text-secondary')

  return (
    <div className="space-y-6">
      {/* Role Identity + Name Change */}
      <div className="bg-card border border-border rounded-lg p-6">
        {isR0 && onNameChange && (
          <button onClick={onNameChange}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-caption bg-warning/10 text-warning border border-warning/30 hover:bg-warning/20 transition-colors mb-2">
            ✎ You can change the name of your character before we start
          </button>
        )}
        <div>
          <h3 className="font-heading text-h2 text-text-primary">{role.character_name}
            <span className="font-body text-body-sm text-text-secondary ml-2 font-normal">{role.title}</span>
          </h3>
        </div>
      </div>

      {/* Confidential Brief */}
      {role.confidential_brief&&<div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-heading text-h3 text-text-primary mb-3">Confidential Brief</h3>
        <p className="font-body text-body-sm text-text-primary leading-relaxed whitespace-pre-wrap">{role.confidential_brief}</p>
      </div>}

      {/* Objectives */}
      {objectives.length>0&&<div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-heading text-h3 text-text-primary mb-3">Your Objectives</h3>
        <ul className="space-y-2">
          {objectives.map((obj,i)=><li key={i} className="flex items-start gap-2">
            <span className="font-data text-caption text-action mt-0.5">{i+1}.</span>
            <span className="font-body text-body-sm text-text-primary">{obj}</span>
          </li>)}
        </ul>
      </div>}

      {/* Personal Relationships */}
      {personalRels.length>0&&<div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-heading text-h3 text-text-primary mb-3">Personal Relationships</h3>
        <div className="space-y-2">
          {personalRels.map((r,i)=>
            <div key={i} className="flex items-center gap-3 bg-base rounded px-3 py-2">
              <span className="font-body text-body-sm text-text-primary font-medium w-28">{r.other_role}</span>
              <span className={`font-body text-caption font-medium ${pRelColor(r.type)}`}>{r.type}</span>
              {r.notes&&<span className="font-body text-caption text-text-secondary">— {r.notes}</span>}
            </div>
          )}
        </div>
      </div>}

      {/* Organization Memberships */}
      {orgMemberships.length>0&&<div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-heading text-h3 text-text-primary mb-3">Organization Memberships</h3>
        <div className="space-y-2">
          {orgMemberships.map(m=><div key={`${m.org_id}_${m.role_in_org}`} className="flex items-center gap-3 bg-base rounded px-3 py-2">
            <span className="font-body text-body-sm text-text-primary font-medium">{m.org_id.replace(/_/g,' ')}</span>
            <span className="font-body text-caption text-text-secondary">{m.role_in_org}</span>
            {m.has_veto&&<span className="font-body text-caption text-danger font-medium">VETO</span>}
          </div>)}
        </div>
      </div>}

      {/* Intelligence & Documents */}
      {artefacts.length>0&&<div className="space-y-3">
        <h3 className="font-heading text-h3 text-text-primary">Intelligence & Documents</h3>
        {artefacts.map(art=><div key={art.id} className="bg-card border border-border rounded-lg overflow-hidden">
          <button onClick={()=>{setOpen(open===art.id?null:art.id);if(!art.is_read)onRead(art.id)}}
            className="w-full text-left px-5 py-3 flex items-center justify-between hover:bg-base/50 transition-colors">
            <div className="flex items-center gap-3">
              {!art.is_read&&<span className="w-2 h-2 bg-danger rounded-full shrink-0"/>}
              <div>
                <span className="font-body text-body-sm text-text-primary font-medium">{art.title}</span>
                {art.from_entity&&<span className="font-body text-caption text-text-secondary ml-2">— {art.from_entity}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-data text-caption bg-danger/10 text-danger px-2 py-0.5 rounded">{art.classification}</span>
              <span className="text-text-secondary">{open===art.id?'▲':'▼'}</span>
            </div>
          </button>
          {open===art.id&&art.content_html&&<div className="border-t border-border p-4">
            <ArtefactRenderer
              type={art.artefact_type}
              title={art.title}
              subtitle={art.subtitle}
              classification={art.classification}
              from_entity={art.from_entity}
              date_label={art.date_label}
              content_html={art.content_html}
            />
          </div>}
        </div>)}
      </div>}
    </div>
  )
}

/* ── Tab: Country ──────────────────────────────────────────────────────── */

// Military might calculation — from LIVE deployment counts
function calcMilitaryMightLive(ground: number, naval: number, air: number, ad: number, missiles: number, nuclearLevel: number): { conventional: number; nuclear: number; total: number } {
  const conv = ground * 1 + naval * 3 + air * 2 + ad * 3 + missiles * 2
  const nucBonus = missiles * 2 * nuclearLevel
  return { conventional: conv, nuclear: nucBonus, total: conv + nucBonus }
}

function TabCountry({country,fullCountry,sanctions,tariffs,relationships,orgMemberships,simId,currentRound,rpcAllCountries,rpcPrevSnapshots,rpcDeploymentCounts}:{
  country:CountryData; fullCountry:Record<string,unknown>|null
  sanctions:{imposer:string;target:string;level:number}[]
  tariffs:{imposer:string;target:string;level:number}[]
  relationships:{to_country_code:string;relationship:string;status:string}[]
  orgMemberships:{org_id:string;role_in_org:string;has_veto:boolean}[]
  simId:string; currentRound:number
  rpcAllCountries:{id:string;gdp:number;stability:number;treasury:number;nuclear_level:number;ai_level:number;ai_rd_progress?:number}[]
  rpcPrevSnapshots:{country_code:string;gdp:number;stability:number;treasury:number;inflation:number;debt_burden:number;nuclear_level:number;ai_level:number;mil_ground:number;mil_naval:number;mil_tactical_air:number;mil_strategic_missiles:number;mil_air_defense:number}[]
  rpcDeploymentCounts:{country_code:string;unit_type:string;cnt:number}[]
}) {
  const fc = fullCountry ?? {}
  const cc = country.id
  const [section,setSection]=useState<'description'|'overview'|'military'|'trade'|'diplomacy'>('description')

  // KPI data from RPC (passed as props — no separate queries)
  // Build live unit counts from deployment_counts
  const depCounts: Record<string, Record<string, number>> = {}
  for (const d of rpcDeploymentCounts) {
    if (!depCounts[d.country_code]) depCounts[d.country_code] = {}
    depCounts[d.country_code][d.unit_type] = Number(d.cnt)
  }
  type LiveCountry = { id:string; gdp:number; stability:number; treasury:number; nuclear_level:number; ai_level:number; live_ground:number; live_naval:number; live_air:number; live_ad:number; live_missiles:number }
  const allCountriesLive: LiveCountry[] = rpcAllCountries.map(c => ({
    ...c,
    live_ground: depCounts[c.id]?.ground ?? 0,
    live_naval: depCounts[c.id]?.naval ?? 0,
    live_air: depCounts[c.id]?.tactical_air ?? 0,
    live_ad: depCounts[c.id]?.air_defense ?? 0,
    live_missiles: depCounts[c.id]?.strategic_missile ?? 0,
  }))
  const prevRound = rpcPrevSnapshots

  const [basingWeGrant,setBasingWeGrant]=useState<string[]>([])
  const [basingWeReceive,setBasingWeReceive]=useState<string[]>([])

  useEffect(()=>{
    supabase.from('relationships').select('from_country_code,to_country_code,basing_rights_a_to_b,basing_rights_b_to_a')
      .eq('sim_run_id',simId)
      .or(`from_country_code.eq.${cc},to_country_code.eq.${cc}`)
      .then(({data})=>{
        const granted:string[]=[], received:string[]=[]
        ;(data??[]).forEach((r:Record<string,unknown>)=>{
          if(r.from_country_code===cc && r.basing_rights_a_to_b) granted.push(r.to_country_code as string)
          if(r.to_country_code===cc && r.basing_rights_b_to_a) granted.push(r.from_country_code as string)
          if(r.from_country_code===cc && r.basing_rights_b_to_a) received.push(r.to_country_code as string)
          if(r.to_country_code===cc && r.basing_rights_a_to_b) received.push(r.from_country_code as string)
        })
        setBasingWeGrant([...new Set(granted)])
        setBasingWeReceive([...new Set(received)])
      })
  },[simId,cc])
  const relColor=(r:string)=>({alliance:'text-success',economic_partnership:'text-action',neutral:'text-text-secondary',hostile:'text-warning',at_war:'text-danger'}[r]??'text-text-secondary')
  const relLabel=(r:string)=>({alliance:'Allied',economic_partnership:'Partnership',neutral:'Neutral',hostile:'Hostile',at_war:'AT WAR'}[r]??r)
  const sanctionsOn = sanctions.filter(s=>s.target===cc)
  const sanctionsBy = sanctions.filter(s=>s.imposer===cc)
  const tariffsOn = tariffs.filter(t=>t.target===cc)
  const tariffsBy = tariffs.filter(t=>t.imposer===cc)

  const [countryRolesTab, setCountryRolesTab] = useState<{id:string;character_name:string;position_type:string;positions?:string[];title:string;status:string}[]>([])
  useEffect(()=>{
    supabase.from('roles').select('id,character_name,positions,title,status')
      .eq('sim_run_id',simId).eq('country_code',cc).eq('status','active')
      .then(({data})=>{ if(data) setCountryRolesTab(data) })
  },[simId,cc])

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CountryEmblem countryId={country.id} color={country.color_ui??'#4A5568'} size={32} />
          <div className="w-5 h-5 rounded" style={{backgroundColor:country.color_ui??'#4A5568'}}/>
          <h2 className="font-heading text-h2 text-text-primary">{country.sim_name}</h2>
        </div>
        <div className="flex gap-1 overflow-x-auto no-scrollbar">
          {(['description','overview','military','trade','diplomacy'] as const).map(v=>
            <button key={v} onClick={()=>setSection(v)}
              className={`font-body text-caption px-2 sm:px-3 py-1 rounded transition-colors whitespace-nowrap flex-shrink-0 ${section===v?'bg-action/10 text-action font-medium':'text-text-secondary hover:text-text-primary'}`}>
              {v==='description'?'Info':v==='overview'?'Economy':v==='military'?'Military':v==='trade'?'Trade':'Diplomacy'}
            </button>
          )}
        </div>
      </div>

      {/* Description tab — KPI bar + country brief + leadership */}
      {section==='description'&&<>
        {/* === KPI BAR === */}
        {(()=>{
          // Current values
          const myLive = allCountriesLive.find(c => c.id === cc)
          const myMight = myLive
            ? calcMilitaryMightLive(myLive.live_ground, myLive.live_naval, myLive.live_air, myLive.live_ad, myLive.live_missiles, myLive.nuclear_level)
            : calcMilitaryMightLive(country.mil_ground, country.mil_naval, country.mil_tactical_air, country.mil_air_defense, country.mil_strategic_missiles, country.nuclear_level)
          const allMights = allCountriesLive.map(c => ({
            id: c.id,
            ...calcMilitaryMightLive(c.live_ground, c.live_naval, c.live_air, c.live_ad, c.live_missiles, c.nuclear_level),
          }))
          // Dense rank: ties get same rank (1,1,3 not 1,2,3)
          // Competition ranking: rank = number of countries with higher score + 1 (ties share rank, next skips)
          const compRank = <T,>(sorted: T[], myId: string, getId: (t:T)=>string, getScore: (t:T)=>number): number => {
            const me = sorted.find(s => getId(s) === myId)
            if (!me) return 0
            const myVal = getScore(me)
            return sorted.filter(s => getScore(s) > myVal).length + 1
          }
          const convRanked = [...allMights].sort((a, b) => b.conventional - a.conventional)
          const convRank = compRank(convRanked, cc, m=>m.id, m=>m.conventional)
          const gdpRanked = [...allCountriesLive].sort((a, b) => b.gdp - a.gdp)
          const gdpRank = compRank(gdpRanked, cc, c=>c.id, c=>c.gdp)
          const totalCountries = allCountriesLive.length || 20

          // Previous round values + ranks (for deltas)
          const prevMe = prevRound.find(p => p.country_code === cc)
          const prevMilMights = prevRound.map(p => ({
            id: p.country_code,
            ...calcMilitaryMightLive(p.mil_ground??0, p.mil_naval??0, p.mil_tactical_air??0, p.mil_air_defense??0, p.mil_strategic_missiles??0, p.nuclear_level??0),
          }))
          const prevConvRanked = [...prevMilMights].sort((a, b) => b.conventional - a.conventional)
          const prevConvRank = prevConvRanked.length ? compRank(prevConvRanked, cc, m=>m.id, m=>m.conventional) : 0
          const prevGdpRanked = [...prevRound].sort((a, b) => (b.gdp??0) - (a.gdp??0))
          const prevGdpRank = prevGdpRanked.length ? compRank(prevGdpRanked, cc, p=>p.country_code, p=>p.gdp??0) : 0
          const hasPrev = prevMe != null

          // Delta helper
          const delta = (curr: number, prev: number | undefined) => prev != null ? curr - prev : null
          const deltaStab = delta(country.stability, prevMe?.stability)
          const deltaTreasury = delta(country.treasury, prevMe?.treasury)
          const deltaGdpRank = hasPrev && prevGdpRank > 0 ? prevGdpRank - gdpRank : null // positive = improved
          const deltaMilRank = hasPrev && prevConvRank > 0 ? prevConvRank - convRank : null

          // Arrow component
          const Arrow = ({d, invert}: {d: number|null; invert?: boolean}) => {
            if (d == null || d === 0) return null
            const good = invert ? d < 0 : d > 0
            return <span className={`text-[10px] font-semibold ${good ? 'text-success' : 'text-danger'}`}>{d > 0 ? '▲' : '▼'}{Math.abs(d) < 10 ? Math.abs(d).toFixed(1) : Math.abs(d).toFixed(0)}</span>
          }
          const RankArrow = ({d}: {d: number|null}) => {
            if (d == null || d === 0) return null
            return <span className={`text-[10px] font-semibold ${d > 0 ? 'text-success' : 'text-danger'}`}>{d > 0 ? '▲' : '▼'}{Math.abs(d)}</span>
          }

          const wars = relationships.filter(r => r.relationship === 'at_war').map(r => r.to_country_code)
          const stab = country.stability
          const threshold = 4.0
          const stabLabel = stab >= 7 ? 'Stable' : stab > threshold ? 'Caution' : 'Critical'
          const stabColor = stab >= 7 ? 'text-success' : stab > threshold ? 'text-warning' : 'text-danger'
          const stabBg = stab >= 7 ? 'bg-success' : stab > threshold ? 'bg-warning' : 'bg-danger'
          const nucLevel = country.nuclear_level ?? 0
          const aiLevel = country.ai_level ?? 0

          // Nuclear rank: score = level × missiles, ranked among all countries with nuc > 0
          const nucScore = nucLevel * (myLive?.live_missiles ?? country.mil_strategic_missiles ?? 0)
          const allNucScores = allCountriesLive.filter(c => (c.nuclear_level ?? 0) > 0).map(c => ({
            id: c.id, score: (c.nuclear_level ?? 0) * (c.live_missiles ?? 0),
          }))
          const nucRanked = [...allNucScores].sort((a, b) => b.score - a.score)
          const nucRank = compRank(nucRanked, cc, n=>n.id, n=>n.score)
          const nucTotal = nucRanked.length

          // Previous nuclear rank
          const prevNucScores = prevRound.filter(p => (p.nuclear_level ?? 0) > 0).map(p => ({
            id: p.country_code, score: (p.nuclear_level ?? 0) * (p.mil_strategic_missiles ?? 0),
          }))
          const prevNucRanked = [...prevNucScores].sort((a, b) => b.score - a.score)
          const prevNucRank = prevNucRanked.length ? compRank(prevNucRanked, cc, n=>n.id, n=>n.score) : 0
          const deltaNucRank = hasPrev && prevNucRank > 0 && nucRank > 0 ? prevNucRank - nucRank : null

          // AI rank: by ai_level + rd_progress composite score
          const aiScoreFn = (c: typeof allCountriesLive[0]) => (c.ai_level ?? 0) + (Number(c.ai_rd_progress ?? 0))
          const aiRanked = [...allCountriesLive].filter(c => (c.ai_level ?? 0) > 0).sort((a, b) => aiScoreFn(b) - aiScoreFn(a))
          const aiRank = compRank(aiRanked, cc, c=>c.id, aiScoreFn)
          const aiTotal = aiRanked.length
          const prevAiRanked = [...prevRound].filter(p => (p.ai_level ?? 0) > 0).sort((a, b) => (b.ai_level ?? 0) - (a.ai_level ?? 0))
          const prevAiRank = prevAiRanked.length ? compRank(prevAiRanked, cc, p=>p.country_code, p=>p.ai_level??0) : 0
          const deltaAiRank = hasPrev && prevAiRank > 0 && aiRank > 0 ? prevAiRank - aiRank : null

          // Cell style — no internal borders, clean background
          const cell = "flex-1 px-3 py-2 flex flex-col items-center gap-0.5 min-w-0"

          return (
            <div className="flex bg-card border border-border rounded-lg overflow-hidden">
              {/* Stability */}
              <div className={cell}>
                <span className="font-body text-[9px] font-bold uppercase tracking-widest text-text-secondary">Stability</span>
                <span className="font-data text-data font-semibold flex items-center gap-1">{stab.toFixed(1)} <Arrow d={deltaStab}/></span>
                <div className="w-full h-1 bg-base rounded-full relative">
                  <div className={`h-full rounded-full ${stabBg}`} style={{ width: `${Math.min(100, stab * 10)}%` }} />
                  <div className="absolute top-[-2px] w-[2px] h-[6px] bg-danger rounded-sm" style={{ left: `${threshold * 10}%` }} title={`Threshold: ${threshold}`} />
                </div>
                <span className={`font-body text-[9px] font-semibold ${stabColor}`}>{stab <= threshold ? '⚠ ' : ''}{stabLabel}</span>
              </div>
              {/* Treasury */}
              <div className={cell}>
                <span className="font-body text-[9px] font-bold uppercase tracking-widest text-text-secondary">Treasury</span>
                <span className={`font-data text-data font-semibold flex items-center gap-1 ${country.treasury <= 3 ? 'text-danger' : ''}`}>ç{country.treasury.toFixed(0)} <Arrow d={deltaTreasury}/></span>
              </div>
              {/* GDP */}
              <div className={cell}>
                <span className="font-body text-[9px] font-bold uppercase tracking-widest text-text-secondary">GDP</span>
                <span className="font-data text-data font-semibold flex items-center gap-1">#{gdpRank} <RankArrow d={deltaGdpRank}/></span>
                <span className="font-body text-[9px] text-text-secondary">ç{country.gdp.toFixed(0)}</span>
              </div>
              {/* Military */}
              <div className={cell}>
                <span className="font-body text-[9px] font-bold uppercase tracking-widest text-text-secondary">Military</span>
                <span className="font-data text-data font-semibold flex items-center gap-1">#{convRank} <RankArrow d={deltaMilRank}/></span>
                <span className="font-body text-[9px] text-text-secondary">{myMight.conventional} pts</span>
              </div>
              {/* Nuclear — only if level > 0 */}
              {nucLevel > 0 && (
                <div className={cell} style={{flex:'0.8'}}>
                  <span className="font-body text-[9px] font-bold uppercase tracking-widest text-text-secondary">Nuclear</span>
                  <span className="font-data text-data font-semibold flex items-center gap-1">#{nucRank} <RankArrow d={deltaNucRank}/></span>
                  <span className="font-body text-[9px] text-text-secondary">T{nucLevel} · {nucTotal} powers</span>
                </div>
              )}
              {/* AI Tech — only if level > 0 */}
              {aiLevel > 0 && (
                <div className={cell} style={{flex:'0.8'}}>
                  <span className="font-body text-[9px] font-bold uppercase tracking-widest text-text-secondary">AI Tech</span>
                  <span className="font-data text-data font-semibold flex items-center gap-1">#{aiRank} <RankArrow d={deltaAiRank}/></span>
                  <span className="font-body text-[9px] text-text-secondary">L{aiLevel} · {aiTotal} nations</span>
                </div>
              )}
              {/* At War — only if wars exist */}
              {wars.length > 0 && (
                <div className={cell}>
                  <span className="font-body text-[9px] font-bold uppercase tracking-widest text-text-secondary">At War With</span>
                  <div className="flex flex-col items-center gap-[2px]">
                    {wars.slice(0, 3).map(w => (
                      <span key={w} className="font-body text-[9px] font-bold text-danger">⚔ {w.toUpperCase()}</span>
                    ))}
                    {wars.length > 3 && <span className="font-body text-[9px] text-danger">+{wars.length - 3} more</span>}
                  </div>
                </div>
              )}
            </div>
          )
        })()}

        {fc.country_brief&&<div className="bg-card border border-border rounded-lg p-4">
          <p className="font-body text-body-sm text-text-primary leading-relaxed">{String(fc.country_brief)}</p>
        </div>}

        {/* Leadership & Roles */}
        {countryRolesTab.length > 0 && (
          <div className="bg-card border border-border rounded-lg divide-y divide-border">
            <div className="px-4 py-2 bg-base/50">
              <span className="font-heading text-caption text-text-secondary uppercase tracking-wider">Leadership</span>
            </div>
            {countryRolesTab.map(r => {
              const pos = displayPositions(r as RoleData)
              const isHos = r.positions?.includes('head_of_state') || r.position_type === 'head_of_state'
              return (
                <div key={r.id} className="flex items-center gap-3 px-4 py-2.5">
                  <span className={`font-body text-body-sm text-text-primary font-medium w-20 sm:w-28 shrink-0 truncate ${isHos ? 'text-warning' : ''}`}>
                    {r.character_name}
                  </span>
                  <span className={`font-body text-caption px-1.5 py-0.5 rounded ${
                    isHos ? 'bg-warning/10 text-warning' :
                    (r.positions?.includes('military') || r.position_type === 'military_chief') ? 'bg-danger/10 text-danger' :
                    (r.positions?.includes('economy') || r.position_type === 'economy_officer') ? 'bg-accent/10 text-accent' :
                    (r.positions?.includes('diplomat') || r.position_type === 'diplomat') ? 'bg-action/10 text-action' :
                    (r.positions?.includes('security') || r.position_type === 'security') ? 'bg-text-secondary/10 text-text-secondary' :
                    'bg-text-secondary/5 text-text-secondary/60'
                  }`}>
                    {pos || 'Citizen'}
                  </span>
                  {r.title && <span className="font-body text-caption text-text-secondary ml-auto">{r.title}</span>}
                </div>
              )
            })}
          </div>
        )}

      </>}

      {section==='overview'&&<>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            {l:'GDP',v:`ç${country.gdp.toFixed(1)}`},
            {l:'Growth',v:`${Number(fc.gdp_growth_base??0).toFixed(1)}%`},
            {l:'Stability',v:`${country.stability.toFixed(1)}/10`},
            {l:'Inflation',v:`${country.inflation.toFixed(1)}%`},
            {l:'Treasury',v:`ç${country.treasury.toFixed(1)}`},
            {l:'Debt',v:`${(country.debt_burden*100).toFixed(0)}%`},
            {l:'Tax Rate',v:`${(Number(fc.tax_rate??0)*100).toFixed(0)}%`},
            {l:'Trade Balance',v:`ç${Number(fc.trade_balance??0).toFixed(1)}`},
          ].map(s=><div key={s.l} className="bg-card border border-border rounded-lg p-3">
            <div className="font-body text-caption text-text-secondary uppercase tracking-wider mb-0.5">{s.l}</div>
            <div className="font-data text-data text-text-primary">{s.v}</div>
          </div>)}
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-heading text-caption text-text-secondary uppercase tracking-wider mb-2">Economic Sectors</h3>
          <div className="flex gap-4">
            {[{l:'Resources',v:fc.sector_resources},{l:'Industry',v:fc.sector_industry},{l:'Services',v:fc.sector_services},{l:'Technology',v:fc.sector_technology}].map(s=>
              <div key={s.l}>
                <div className="font-body text-caption text-text-secondary">{s.l}</div>
                <div className="font-data text-data text-text-primary">{String(s.v??0)}%</div>
              </div>
            )}
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-heading text-caption text-text-secondary uppercase tracking-wider mb-2">Technology</h3>
          <div className="flex gap-6">
            <div>
              <span className="font-body text-caption text-text-secondary">Nuclear: </span>
              <span className={`font-data text-data ${country.nuclear_level>=3?'text-danger font-bold':country.nuclear_level>=1?'text-warning':'text-text-secondary'}`}>
                L{country.nuclear_level}{country.nuclear_confirmed?' (confirmed)':country.nuclear_level>0?' (unconfirmed)':''}
              </span>
              {Number(fc.nuclear_rd_progress??0)>0&&<span className="font-body text-caption text-text-secondary ml-1">R&D: {(Number(fc.nuclear_rd_progress)*100).toFixed(0)}%</span>}
            </div>
            <div>
              <span className="font-body text-caption text-text-secondary">AI: </span>
              <span className={`font-data text-data ${country.ai_level>=4?'text-accent font-bold':country.ai_level>=1?'text-accent':'text-text-secondary'}`}>
                L{country.ai_level}
              </span>
              {Number(fc.ai_rd_progress??0)>0&&<span className="font-body text-caption text-text-secondary ml-1">R&D: {(Number(fc.ai_rd_progress)*100).toFixed(0)}%</span>}
            </div>
          </div>
        </div>
      </>}

      {section==='military'&&<>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            {l:'Ground',v:country.mil_ground},{l:'Naval',v:country.mil_naval},
            {l:'Tactical Air',v:country.mil_tactical_air},{l:'Strategic Missiles',v:country.mil_strategic_missiles},
            {l:'Air Defense',v:country.mil_air_defense},
          ].map(s=><div key={s.l} className="bg-card border border-border rounded-lg p-3">
            <div className="font-body text-caption text-text-secondary uppercase tracking-wider mb-0.5">{s.l}</div>
            <div className="font-data text-data-lg text-text-primary">{s.v}</div>
          </div>)}
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-heading text-caption text-text-secondary uppercase tracking-wider mb-2">Production</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 font-body text-caption">
            <div><span className="text-text-secondary">Ground:</span> <span className="text-text-primary">cap {String(fc.prod_cap_ground??0)}/rd, cost ç{String(fc.prod_cost_ground??0)}</span></div>
            <div><span className="text-text-secondary">Naval:</span> <span className="text-text-primary">cap {String(fc.prod_cap_naval??0)}/rd, cost ç{String(fc.prod_cost_naval??0)}</span></div>
            <div><span className="text-text-secondary">Air:</span> <span className="text-text-primary">cap {String(fc.prod_cap_tactical??0)}/rd, cost ç{String(fc.prod_cost_tactical??0)}</span></div>
          </div>
          <div className="mt-2 font-body text-caption text-text-secondary">
            Maintenance: ç{String(fc.maintenance_per_unit??0)}/unit · Mobilization pool: {String(fc.mobilization_pool??0)}
          </div>
        </div>
        {(basingWeGrant.length > 0 || basingWeReceive.length > 0) && (
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="font-heading text-caption text-text-secondary uppercase tracking-wider mb-2">Basing Rights</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="font-body text-caption text-text-secondary block mb-1">We grant access to:</span>
                {basingWeGrant.length > 0
                  ? basingWeGrant.map(c=><div key={c} className="font-body text-body-sm text-text-primary">{c}</div>)
                  : <span className="font-body text-caption text-text-secondary/50">None</span>}
              </div>
              <div>
                <span className="font-body text-caption text-text-secondary block mb-1">We have access to:</span>
                {basingWeReceive.length > 0
                  ? basingWeReceive.map(c=><div key={c} className="font-body text-body-sm text-text-primary">{c}</div>)
                  : <span className="font-body text-caption text-text-secondary/50">None</span>}
              </div>
            </div>
          </div>
        )}
      </>}

      {section==='trade'&&<>
        {sanctionsOn.length>0&&<div className="bg-card border border-danger/20 rounded-lg p-4">
          <h3 className="font-heading text-caption text-danger uppercase tracking-wider mb-2">Sanctions Against Us</h3>
          <div className="space-y-1">
            {sanctionsOn.map((s,i)=><div key={i} className="flex items-center gap-2 font-body text-body-sm">
              <span className="text-text-primary">{s.imposer}</span>
              <span className="text-danger font-data">Level {s.level}</span>
            </div>)}
          </div>
        </div>}
        {sanctionsBy.length>0&&<div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-heading text-caption text-text-secondary uppercase tracking-wider mb-2">Sanctions We Impose</h3>
          <div className="space-y-1">
            {sanctionsBy.map((s,i)=><div key={i} className="flex items-center gap-2 font-body text-body-sm">
              <span className="text-text-primary">{s.target}</span>
              <span className="font-data text-warning">Level {s.level}</span>
            </div>)}
          </div>
        </div>}
        {tariffsOn.length>0&&<div className="bg-card border border-warning/20 rounded-lg p-4">
          <h3 className="font-heading text-caption text-warning uppercase tracking-wider mb-2">Tariffs Against Us</h3>
          <div className="space-y-1">
            {tariffsOn.map((t,i)=><div key={i} className="flex items-center gap-2 font-body text-body-sm">
              <span className="text-text-primary">{t.imposer}</span>
              <span className="font-data text-warning">Level {t.level}</span>
            </div>)}
          </div>
        </div>}
        {tariffsBy.length>0&&<div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-heading text-caption text-text-secondary uppercase tracking-wider mb-2">Tariffs We Impose</h3>
          <div className="space-y-1">
            {tariffsBy.map((t,i)=><div key={i} className="flex items-center gap-2 font-body text-body-sm">
              <span className="text-text-primary">{t.target}</span>
              <span className="font-data text-text-secondary">Level {t.level}</span>
            </div>)}
          </div>
        </div>}
        {sanctionsOn.length===0&&sanctionsBy.length===0&&tariffsOn.length===0&&tariffsBy.length===0&&
          <p className="font-body text-body-sm text-text-secondary">No active sanctions or tariffs.</p>
        }
      </>}

      {section==='diplomacy'&&<>
        {/* Country relationships */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-heading text-caption text-text-secondary uppercase tracking-wider mb-3">Relationships</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {relationships.filter(r=>r.relationship!=='neutral').map(r=>
              <div key={r.to_country_code} className="flex items-center gap-2 bg-base rounded px-3 py-2">
                <span className="font-body text-body-sm text-text-primary">{r.to_country_code}</span>
                <span className={`font-body text-caption font-medium ${relColor(r.relationship)}`}>{relLabel(r.relationship)}</span>
              </div>
            )}
          </div>
          {relationships.filter(r=>r.relationship==='neutral').length>0&&<div className="mt-2">
            <span className="font-body text-caption text-text-secondary">
              Neutral: {relationships.filter(r=>r.relationship==='neutral').map(r=>r.to_country_code).join(', ')}
            </span>
          </div>}
        </div>

        {/* Organization memberships */}
        {orgMemberships.length>0&&<div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-heading text-caption text-text-secondary uppercase tracking-wider mb-3">Organization Memberships</h3>
          <div className="space-y-2">
            {orgMemberships.map(m=><div key={`${m.org_id}_${m.role_in_org}`} className="flex items-center gap-3 bg-base rounded px-3 py-2">
              <OrgEmblem orgId={m.org_id} size={16} />
              <span className="font-body text-body-sm text-text-primary font-medium">{m.org_id.replace(/_/g,' ')}</span>
              <span className="font-body text-caption text-text-secondary">{m.role_in_org}</span>
              {m.has_veto&&<span className="font-body text-caption text-danger font-medium">VETO</span>}
            </div>)}
          </div>
        </div>}
      </>}
    </div>
  )
}

/* ── Tab: World ────────────────────────────────────────────────────────── */

interface WorldRole {
  id: string; character_name: string; country_code: string; position_type: string
  title: string; public_bio: string; is_ai_operated: boolean
}

interface CountryBrief {
  id: string; public_bio: string
}


/* ── Tab: World ────────────────────────────────────────────────────────── */

/* ═══════════════════════════════════════════════════════════════════════ */
/* TabNewsbrief — event log + round results                               */
/* ═══════════════════════════════════════════════════════════════════════ */

function TabNewsbrief({simId, countryId, country, round, publicEvents, myEvents, notifications, prevSnapshots, deploymentCounts, relationships, sanctions, tariffs, fullCountry}: {
  simId: string; countryId: string; country: CountryData; round: number
  publicEvents: Record<string, unknown>[]; myEvents: Record<string, unknown>[]; notifications: NotificationData[]
  prevSnapshots: {country_code:string;gdp:number;stability:number;treasury:number;inflation:number;debt_burden:number;nuclear_level:number;ai_level:number;mil_ground:number;mil_naval:number;mil_tactical_air:number;mil_strategic_missiles:number;mil_air_defense:number}[]
  deploymentCounts: {country_code:string;unit_type:string;cnt:number}[]
  relationships: {to_country_code:string;relationship:string;status:string}[]
  sanctions: {imposer:string;target:string;level:number}[]
  tariffs: {imposer:string;target:string;level:number}[]
  fullCountry: Record<string, unknown> | null
}) {
  const LOG_HIDDEN = new Set(['ai_agent_log','round_started','phase_b_complete'])
  const prevMe = prevSnapshots.find(p => p.country_code === countryId)
  const hasPrev = prevMe != null
  const prevRound = round > 1 ? round - 1 : 0

  // Merge public + own events, deduplicate, sort newest first
  const allEvents = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>()
    for (const e of publicEvents) if (!LOG_HIDDEN.has(e.event_type as string)) map.set(e.id as string, e)
    for (const e of myEvents) if (!LOG_HIDDEN.has(e.event_type as string)) map.set(e.id as string, e)
    return Array.from(map.values()).sort((a, b) => new Date(b.created_at as string).getTime() - new Date(a.created_at as string).getTime())
  }, [publicEvents, myEvents])

  const [showCount, setShowCount] = useState(25)

  // Oil price — load inside tab (tab-level data)
  const [oilPrice, setOilPrice] = useState<{current:number|null;prev:number|null}>({current:null,prev:null})
  useEffect(() => {
    supabase.from('world_state').select('round_num,oil_price')
      .eq('sim_run_id', simId).order('round_num', {ascending: false}).limit(2)
      .then(({data}) => {
        setOilPrice({
          current: data?.[0] ? Number(data[0].oil_price) : null,
          prev: data?.[1] ? Number(data[1].oil_price) : null,
        })
      })
  }, [simId, round])

  // Military: live counts
  const depCnts: Record<string, number> = {}
  for (const d of deploymentCounts) { if (d.country_code === countryId) depCnts[d.unit_type] = Number(d.cnt) }

  // Delta helper
  const Ar = ({curr, prev, invert}: {curr:number; prev:number|undefined; invert?:boolean}) => {
    if (prev == null) return null
    const d = curr - prev
    if (Math.abs(d) < 0.01) return null
    const good = invert ? d < 0 : d > 0
    return <span className={`text-[11px] font-semibold ml-1 ${good ? 'text-success' : 'text-danger'}`}>{d > 0 ? '▲' : '▼'}{Math.abs(d) < 10 ? Math.abs(d).toFixed(1) : Math.abs(d).toFixed(0)}</span>
  }

  const catColor: Record<string, string> = { military:'text-danger', economic:'text-action', diplomatic:'text-success', covert:'text-text-secondary', political:'text-warning', system:'text-text-secondary', communication:'text-action' }
  const catLabel: Record<string, string> = { military:'MIL', economic:'ECON', diplomatic:'DIPL', covert:'COV', political:'POL', communication:'COM' }

  return (
    <div className="flex flex-col sm:flex-row gap-4" style={{minHeight:'calc(100vh - 250px)'}}>
      {/* LEFT: Event Log */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading text-h3 text-text-primary">Event Log</h3>
          <span className="font-body text-caption text-text-secondary">{allEvents.length} events</span>
        </div>
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="max-h-[calc(100vh-320px)] overflow-y-auto divide-y divide-border/30">
            {allEvents.slice(0, showCount).map(e => {
              const cat = (e.category as string) || 'system'
              const isOwn = (e.country_code as string) === countryId
              return (
                <div key={e.id as string} className={`px-3 py-2 ${isOwn ? 'bg-action/3' : ''}`}>
                  <div className="flex items-start gap-2">
                    <span className={`font-body text-[8px] font-bold uppercase tracking-wider shrink-0 mt-1 w-6 ${catColor[cat] || 'text-text-secondary'}`}>{catLabel[cat] || '—'}</span>
                    <div className="min-w-0 flex-1">
                      <span className={`font-body text-body-sm ${catColor[cat] || 'text-text-primary'}`}>{e.summary as string}</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="font-mono text-[9px] text-text-secondary">R{e.round_num as number}</span>
                        <span className="font-body text-[9px] text-text-secondary uppercase">{cat}</span>
                        {isOwn && <span className="font-body text-[9px] text-action">YOUR COUNTRY</span>}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
            {allEvents.length === 0 && <div className="px-4 py-8 text-center font-body text-body-sm text-text-secondary">No events yet</div>}
          </div>
          {allEvents.length > showCount && (
            <button onClick={() => setShowCount(s => s + 25)} className="w-full py-2 font-body text-caption text-action hover:bg-action/5 border-t border-border">
              ▼ Load more ({allEvents.length - showCount} remaining)
            </button>
          )}
        </div>

        {/* Public Screen link */}
        <button onClick={() => window.open(`/screen/${simId}`, '_blank')} className="mt-3 font-body text-body-sm text-action hover:underline">
          Open Public Screen →
        </button>
      </div>

      {/* RIGHT: Round Results */}
      <div className="w-full sm:w-[340px] sm:shrink-0">
        <h3 className="font-heading text-h3 text-text-primary mb-2">
          {round <= 1 ? 'Round Results' : `Round ${prevRound} Results`}
        </h3>

        {!hasPrev && round <= 1 ? (
          <div className="bg-card border border-border rounded-lg p-6 text-center">
            <p className="font-body text-body-sm text-text-secondary">No round results yet — first round in progress.</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg divide-y divide-border/30 text-body-sm">
            {/* Economy */}
            <div className="px-4 py-3">
              <div className="font-body text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-2">Economy</div>
              <div className="space-y-1">
                {[
                  { label: 'Stability', curr: country.stability, prev: prevMe?.stability, fmt: (v:number) => v.toFixed(1) },
                  { label: 'Treasury', curr: country.treasury, prev: prevMe?.treasury, fmt: (v:number) => `ç${v.toFixed(0)}` },
                  { label: 'GDP', curr: country.gdp, prev: prevMe?.gdp, fmt: (v:number) => `ç${v.toFixed(0)}` },
                  { label: 'Inflation', curr: country.inflation, prev: prevMe?.inflation, fmt: (v:number) => `${v.toFixed(1)}%`, invert: true },
                  { label: 'Debt', curr: country.debt_burden * 100, prev: prevMe?.debt_burden != null ? prevMe.debt_burden * 100 : undefined, fmt: (v:number) => `${v.toFixed(0)}%`, invert: true },
                ].map(r => (
                  <div key={r.label} className="flex items-center justify-between">
                    <span className="font-body text-body-sm text-text-secondary">{r.label}</span>
                    <span className="font-data text-body-sm font-medium text-text-primary">
                      {r.fmt(r.curr)}
                      <Ar curr={r.curr} prev={r.prev} invert={r.invert} />
                    </span>
                  </div>
                ))}
                {/* Oil Price */}
                <div className="flex items-center justify-between">
                  <span className="font-body text-body-sm text-text-secondary">Oil Price</span>
                  <span className="font-data text-body-sm font-medium text-text-primary">
                    ç{oilPrice.current?.toFixed(0) ?? '—'}
                    {oilPrice.current != null && oilPrice.prev != null && Math.abs(oilPrice.current - oilPrice.prev) >= 0.5 && (
                      <span className={`text-[11px] font-semibold ml-1 ${oilPrice.current > oilPrice.prev ? 'text-danger' : 'text-success'}`}>
                        {oilPrice.current > oilPrice.prev ? '▲' : '▼'}{Math.abs(oilPrice.current - oilPrice.prev).toFixed(0)}
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Military */}
            <div className="px-4 py-3">
              <div className="font-body text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-2">Military</div>
              <div className="space-y-0.5">
                {[
                  { label: 'Ground', type: 'ground', prev: prevMe?.mil_ground },
                  { label: 'Naval', type: 'naval', prev: prevMe?.mil_naval },
                  { label: 'Air', type: 'tactical_air', prev: prevMe?.mil_tactical_air },
                  { label: 'Air Def', type: 'air_defense', prev: prevMe?.mil_air_defense },
                  { label: 'Missiles', type: 'strategic_missile', prev: prevMe?.mil_strategic_missiles },
                ].map(u => {
                  const now = depCnts[u.type] ?? 0
                  const d = u.prev != null ? now - u.prev : null
                  return (
                    <div key={u.type} className="flex items-center justify-between">
                      <span className="font-body text-body-sm text-text-secondary">{u.label}</span>
                      <span className="font-data text-body-sm font-medium text-text-primary">
                        {now}
                        {d != null && d !== 0 && <span className={`text-[11px] font-semibold ml-1 ${d > 0 ? 'text-success' : 'text-danger'}`}>{d > 0 ? '+' : ''}{d}</span>}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Technology (changes only) */}
            {prevMe && ((country.nuclear_level ?? 0) !== (prevMe.nuclear_level ?? 0) || (country.ai_level ?? 0) !== (prevMe.ai_level ?? 0)) && (
              <div className="px-4 py-3">
                <div className="font-body text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-2">Technology</div>
                {(country.nuclear_level ?? 0) > (prevMe.nuclear_level ?? 0) && (
                  <div className="font-body text-body-sm text-success">Nuclear advanced to T{country.nuclear_level}</div>
                )}
                {(country.ai_level ?? 0) > (prevMe.ai_level ?? 0) && (
                  <div className="font-body text-body-sm text-success">AI advanced to L{country.ai_level}</div>
                )}
              </div>
            )}

            {/* Diplomacy (changes only — from event log for this round) */}
            {(() => {
              const diplomaticEvents = allEvents.filter(e => {
                const et = e.event_type as string
                const rn = e.round_num as number
                return rn >= prevRound && (
                  et === 'declare_war' || et === 'set_sanctions' || et === 'set_tariffs' ||
                  et === 'agreement_activated' || et === 'agreement_denounced' ||
                  et.startsWith('sanctions_') || et.startsWith('tariff_')
                ) && ((e.country_code as string) === countryId || ((e.payload as Record<string,unknown>)?.target as string) === countryId)
              })
              return diplomaticEvents.length > 0 ? (
                <div className="px-4 py-3">
                  <div className="font-body text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-2">Diplomacy</div>
                  {diplomaticEvents.map((e, i) => <div key={i} className="font-body text-body-sm text-text-primary">{e.summary as string}</div>)}
                </div>
              ) : null
            })()}

            {/* Health (if applicable) */}
            {country.health_status && country.health_status !== 'healthy' && (
              <div className="px-4 py-3">
                <div className="font-body text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-2">Health</div>
                <div className="font-body text-body-sm text-warning">Leader is {country.health_status}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}


function TabWorld({simId,round,isMobile=false}:{simId:string;round:number;isMobile?:boolean}) {
  const [countries,setCountries]=useState<CountryData[]>([])
  const [relationships,setRelationships]=useState<{from_country_code:string;to_country_code:string;relationship:string}[]>([])
  const [worldState,setWorldState]=useState<{oil_price:number;global_trade_volume_index:number;dollar_credibility:number}|null>(null)
  const [prevOilPrice,setPrevOilPrice]=useState<number|null>(null)
  const [roles,setRoles]=useState<WorldRole[]>([])
  const [countryBriefs,setCountryBriefs]=useState<Record<string,string>>({})
  const [view,setView]=useState<'map'|'overview'|'military'|'relationships'|'countries'|'organizations'>('map')
  const [organizations,setOrganizations]=useState<{id:string;sim_name:string;parallel:string;description:string}[]>([])
  const [allOrgMembers,setAllOrgMembers]=useState<{org_id:string;country_code:string;role_in_org:string;has_veto:boolean}[]>([])
  const [expandedCountry,setExpandedCountry]=useState<string|null>(null)
  const [expandedRole,setExpandedRole]=useState<string|null>(null)

  useEffect(()=>{
    Promise.all([
      supabase.from('countries')
        .select('id,sim_name,color_ui,gdp,stability,inflation,treasury,nuclear_level,nuclear_confirmed,ai_level,debt_burden')
        .eq('sim_run_id',simId).order('gdp',{ascending:false}),
      supabase.from('deployments')
        .select('country_code,unit_type')
        .eq('sim_run_id',simId).neq('unit_status','destroyed'),
    ]).then(([cRes,dRes])=>{
      const cs = (cRes.data??[]) as CountryData[]
      const deps = dRes.data??[]
      const mc: Record<string,{mil_ground:number;mil_naval:number;mil_tactical_air:number;mil_strategic_missiles:number;mil_air_defense:number}> = {}
      for(const d of deps){
        if(!mc[d.country_code]) mc[d.country_code]={mil_ground:0,mil_naval:0,mil_tactical_air:0,mil_strategic_missiles:0,mil_air_defense:0}
        if(d.unit_type==='ground') mc[d.country_code].mil_ground++
        else if(d.unit_type==='naval') mc[d.country_code].mil_naval++
        else if(d.unit_type==='tactical_air') mc[d.country_code].mil_tactical_air++
        else if(d.unit_type==='strategic_missile') mc[d.country_code].mil_strategic_missiles++
        else if(d.unit_type==='air_defense') mc[d.country_code].mil_air_defense++
      }
      for(const c of cs){ const m=mc[c.id]; if(m) Object.assign(c,m); else { c.mil_ground=0;c.mil_naval=0;c.mil_tactical_air=0;c.mil_strategic_missiles=0;c.mil_air_defense=0 } }
      setCountries(cs)
    })
    supabase.from('relationships')
      .select('from_country_code,to_country_code,relationship')
      .eq('sim_run_id',simId)
      .then(({data})=>setRelationships((data??[]) as typeof relationships))
    supabase.from('world_state')
      .select('round_num,oil_price,global_trade_volume_index,dollar_credibility')
      .eq('sim_run_id',simId).order('round_num',{ascending:false}).limit(2)
      .then(({data})=>{
        if(data?.[0]) setWorldState(data[0])
        if(data?.[1]) setPrevOilPrice(Number(data[1].oil_price ?? 0))
        else setPrevOilPrice(null)
      })
    supabase.from('roles')
      .select('id,character_name,country_code,positions,title,public_bio,is_ai_operated')
      .eq('sim_run_id',simId).eq('status','active').order('country_code,position_type')
      .then(({data})=>setRoles((data??[]) as WorldRole[]))
    supabase.from('countries')
      .select('id,country_brief')
      .eq('sim_run_id',simId)
      .then(({data})=>{
        const briefs:Record<string,string>={}
        ;(data??[]).forEach((c:{id:string;country_brief:string})=>{if(c.country_brief)briefs[c.id]=c.country_brief})
        setCountryBriefs(briefs)
      })
    supabase.from('organizations')
      .select('id,sim_name,parallel,description')
      .eq('sim_run_id',simId).order('sim_name')
      .then(({data})=>setOrganizations((data??[]) as typeof organizations))
    supabase.from('org_memberships')
      .select('org_id,country_code,role_in_org,has_veto')
      .eq('sim_run_id',simId)
      .then(({data})=>setAllOrgMembers((data??[]) as typeof allOrgMembers))
  },[simId])

  const milTotal=(c:CountryData)=>c.mil_ground+c.mil_naval+c.mil_tactical_air+c.mil_strategic_missiles+c.mil_air_defense
  const relColor=(r:string)=>({alliance:'text-success',economic_partnership:'text-action',neutral:'text-text-secondary',hostile:'text-warning',at_war:'text-danger'}[r]??'text-text-secondary')
  const relLabel=(r:string)=>({alliance:'Allied',economic_partnership:'Partnership',neutral:'Neutral',hostile:'Hostile',at_war:'AT WAR'}[r]??r)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-h2 text-text-primary">{isMobile ? 'World' : `World — R${round}`}</h2>
        <div className="flex gap-1 overflow-x-auto no-scrollbar">
          {(['map','overview','military','relationships','organizations','countries'] as const).map(v=>
            <button key={v} onClick={()=>setView(v)}
              className={`font-body text-caption px-2 sm:px-3 py-1 rounded transition-colors whitespace-nowrap flex-shrink-0 ${view===v?'bg-action/10 text-action font-medium':'text-text-secondary hover:text-text-primary'}`}>
              {v==='map'?'Map':v==='overview'?'Economy':v==='military'?'Military':v==='relationships'?'Relations':v==='organizations'?'Orgs':'Countries'}
            </button>
          )}
        </div>
      </div>

      {/* Economy view */}
      {/* Map view — full interactive map with theater tabs */}
      {view==='map'&&<MapWithTheaterTabs iframeId="worldMapIframe" src={`/map/deployments.html?display=clean&hide_units=1&sim_run_id=${simId}`} defaultHideUnits={true} height="calc(100vh - 220px)" isMobile={isMobile}/>}

      {view==='overview'&&<><div className="bg-card border border-border rounded-lg px-4 py-2 w-fit">
        <div className="font-body text-caption text-text-secondary">Oil Price</div>
        <div className="font-data text-data-lg text-text-primary flex items-center gap-1">
          ç{worldState?.oil_price?.toFixed(0) ?? '—'}
          {(()=>{
            const curr = worldState?.oil_price; const prev = prevOilPrice
            if (curr == null || prev == null) return null
            const d = curr - prev
            if (Math.abs(d) < 0.5) return null
            return <span className={`text-[11px] font-semibold ${d > 0 ? 'text-danger' : 'text-success'}`}>{d > 0 ? '▲' : '▼'}{Math.abs(d).toFixed(0)}</span>
          })()}
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full min-w-[500px]">
          <thead><tr className="border-b border-border bg-base">
            {['Country','GDP','Stability','Inflation','Debt','Nuclear','AI'].map(h=>
              <th key={h} className={`font-body text-caption text-text-secondary uppercase px-3 py-2 ${h==='Country'?'text-left':'text-right'}`}>{h}</th>
            )}
          </tr></thead>
          <tbody>{countries.map(c=>
            <tr key={c.id} className="border-b border-border/50 hover:bg-base/50">
              <td className="px-3 py-2 flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{backgroundColor:c.color_ui??'#666'}}/>
                <span className="font-body text-body-sm text-text-primary">{c.sim_name}</span>
              </td>
              <td className="text-right font-data text-caption text-text-primary px-3 py-2">ç{c.gdp.toFixed(0)}</td>
              <td className="text-right font-data text-caption px-3 py-2">
                <span className={c.stability>=7?'text-success':c.stability>=4?'text-warning':'text-danger'}>{c.stability.toFixed(1)}</span>
              </td>
              <td className="text-right font-data text-caption text-text-primary px-3 py-2">{c.inflation.toFixed(1)}%</td>
              <td className="text-right font-data text-caption text-text-secondary px-3 py-2">{(c.debt_burden*100).toFixed(0)}%</td>
              <td className="text-right font-data text-caption px-3 py-2">
                {c.nuclear_level>0?<span className={c.nuclear_level>=3?'text-danger font-bold':'text-warning'}>L{c.nuclear_level}{c.nuclear_confirmed?'':'?'}</span>:<span className="text-text-secondary/30">—</span>}
              </td>
              <td className="text-right font-data text-caption px-3 py-2">
                {c.ai_level>0?<span className="text-accent">L{c.ai_level}</span>:<span className="text-text-secondary/30">—</span>}
              </td>
            </tr>
          )}</tbody>
        </table>
      </div></>}

      {/* Military view */}
      {view==='military'&&<div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full min-w-[500px]">
          <thead><tr className="border-b border-border bg-base">
            {['Country','Ground','Naval','Air','Missiles','AD','Total'].map(h=>
              <th key={h} className={`font-body text-caption text-text-secondary uppercase px-3 py-2 ${h==='Country'?'text-left':'text-right'}`}>{h}</th>
            )}
          </tr></thead>
          <tbody>{[...countries].sort((a,b)=>milTotal(b)-milTotal(a)).map(c=>
            <tr key={c.id} className="border-b border-border/50 hover:bg-base/50">
              <td className="px-3 py-2 flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{backgroundColor:c.color_ui??'#666'}}/>
                <span className="font-body text-body-sm text-text-primary">{c.sim_name}</span>
              </td>
              <td className="text-right font-data text-caption text-text-primary px-3 py-2">{c.mil_ground||'—'}</td>
              <td className="text-right font-data text-caption text-text-primary px-3 py-2">{c.mil_naval||'—'}</td>
              <td className="text-right font-data text-caption text-text-primary px-3 py-2">{c.mil_tactical_air||'—'}</td>
              <td className="text-right font-data text-caption text-text-primary px-3 py-2">{c.mil_strategic_missiles||'—'}</td>
              <td className="text-right font-data text-caption text-text-primary px-3 py-2">{c.mil_air_defense||'—'}</td>
              <td className="text-right font-data text-data font-bold text-text-primary px-3 py-2">{milTotal(c)}</td>
            </tr>
          )}</tbody>
        </table>
      </div>}

      {/* Relationships view */}
      {view==='relationships'&&<div className="bg-card border border-border rounded-lg p-4">
        <div className="space-y-1">
          {countries.slice(0,12).map(c=>{
            const rels=relationships.filter(r=>r.from_country_code===c.id&&r.relationship!=='neutral')
            if(!rels.length) return null
            return <div key={c.id} className="flex items-center gap-2 py-1">
              <div className="w-3 h-3 rounded shrink-0" style={{backgroundColor:c.color_ui??'#666'}}/>
              <span className="font-body text-caption text-text-primary w-16 sm:w-24 shrink-0 truncate">{c.sim_name}</span>
              <div className="flex flex-wrap gap-1">
                {rels.map((r,i)=>{
                  const target=countries.find(x=>x.id===r.to_country_code)
                  return <span key={i} className={`font-body text-caption ${relColor(r.relationship)}`}>
                    {target?.sim_name??r.to_country_code}({relLabel(r.relationship)})
                  </span>
                })}
              </div>
            </div>
          })}
        </div>
      </div>}

      {/* Organizations view */}
      {view==='organizations'&&<div className="space-y-4">
        {organizations.length === 0 ? (
          <p className="font-body text-caption text-text-secondary">No organizations in this SIM.</p>
        ) : organizations.map(org => {
          const members = allOrgMembers.filter(m => m.org_id === org.id)
          const countryNames = countries.reduce<Record<string,{name:string;color:string}>>((acc, c) => {
            acc[c.id] = { name: c.sim_name, color: c.color_ui || '#4A5568' }; return acc
          }, {})
          return (
            <div key={org.id} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <OrgEmblem orgId={org.id} size={24} />
                <h3 className="font-heading text-h3 text-text-primary">{org.sim_name || org.id.replace(/_/g, ' ')}</h3>
                <span className="font-data text-caption bg-action/10 text-action px-2 py-0.5 rounded-full ml-auto">{members.length} members</span>
              </div>
              {org.description && <p className="font-body text-caption text-text-secondary mb-3">{org.description}</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {members.map(m => (
                  <div key={`${org.id}-${m.country_code}`} className="flex items-center gap-2 bg-base rounded px-3 py-2">
                    <CountryEmblem countryId={m.country_code} color={countryNames[m.country_code]?.color || '#4A5568'} size={16} />
                    <span className="font-body text-body-sm text-text-primary">{countryNames[m.country_code]?.name || m.country_code}</span>
                    {m.role_in_org && m.role_in_org !== 'member' && (
                      <span className="font-body text-caption text-text-secondary ml-auto">{m.role_in_org}</span>
                    )}
                    {m.has_veto && <span className="font-body text-caption text-danger font-medium">VETO</span>}
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>}

      {/* Countries & People view */}
      {view==='countries'&&<div className="space-y-2">
        {countries.map(c=>{
          const countryRoles=roles.filter(r=>r.country_code===c.id)
          const isExpanded=expandedCountry===c.id
          return <div key={c.id} className="bg-card border border-border rounded-lg overflow-hidden">
            <button onClick={()=>setExpandedCountry(isExpanded?null:c.id)}
              className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-base/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 rounded" style={{backgroundColor:c.color_ui??'#666'}}/>
                <span className="font-heading text-body-sm text-text-primary font-medium">{c.sim_name}</span>
                <span className="font-body text-caption text-text-secondary">{countryRoles.length} roles</span>
              </div>
              <span className="text-text-secondary text-sm">{isExpanded?'▲':'▼'}</span>
            </button>

            {isExpanded&&<div className="border-t border-border">
              {/* Country brief */}
              {countryBriefs[c.id]&&<div className="px-4 py-3 bg-base/50 border-b border-border">
                <p className="font-body text-body-sm text-text-primary leading-relaxed">{countryBriefs[c.id]}</p>
              </div>}

              {/* Team members */}
              <div className="px-4 py-3 space-y-2">
                {countryRoles.map(role=>{
                  const roleExpanded=expandedRole===role.id
                  return <div key={role.id}>
                    <button onClick={()=>setExpandedRole(roleExpanded?null:role.id)}
                      className="w-full text-left flex items-center gap-3 py-1 hover:bg-base/30 rounded px-2 -mx-2 transition-colors">
                      <span className="font-body text-body-sm text-text-primary font-medium w-20 sm:w-28 shrink-0 truncate">{role.character_name}</span>
                      <span className={`font-body text-caption px-1.5 py-0.5 rounded ${
                        (role.positions?.includes('head_of_state') || role.position_type==='head_of_state')?'bg-warning/10 text-warning':
                        (role.positions?.includes('military') || role.position_type==='military_chief')?'bg-danger/10 text-danger':
                        (role.positions?.includes('economy') || role.position_type==='economy_officer')?'bg-accent/10 text-accent':
                        (role.positions?.includes('diplomat') || role.position_type==='diplomat')?'bg-action/10 text-action':
                        'bg-text-secondary/10 text-text-secondary'
                      }`}>
                        {displayPositions(role as RoleData)}
                      </span>
                      <span className="font-body text-caption text-text-secondary flex-1">{role.title}</span>
                      {role.is_ai_operated&&<span className="font-body text-caption text-accent/60">AI</span>}
                    </button>
                    {roleExpanded&&role.public_bio&&<div className="ml-2 pl-4 border-l-2 border-border my-2">
                      <p className="font-body text-caption text-text-secondary leading-relaxed">{role.public_bio}</p>
                    </div>}
                  </div>
                })}
              </div>
            </div>}
          </div>
        })}
      </div>}
    </div>
  )
}
